import { REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE } from '../../gpuLimits';
import {
  AVBD_COLLISION_MARGIN,
  AVBD_FRICTION_STATIC,
} from '../avbdParams';
import {
  CONTACT_INFO_LOGICAL_SLOT_MASK,
  CONTACT_INFO_LOGICAL_SLOT_SHIFT,
  CONTACT_INFO_SPECULATIVE_BIT,
  encodeContactLogicalSlot,
} from './contactRecord';
import {
  COLISEUM_510K_BODY_CAPACITY,
  COLISEUM_510K_CONTACT_ROW_CAPACITY,
} from './contactSoaLayout';
import {
  CompactContactPoolFatalBit,
  CompactContactPoolHeaderOffset,
} from './compactContactPool';
import {
  COLISEUM_510K_PERSISTENT_HASH_CAPACITY,
  COLISEUM_510K_PERSISTENT_MANIFOLD_CAPACITY,
} from './wideExactProfile';
import {
  planWideCompactContactPoolLayout,
  type WideCompactContactPoolLayout,
} from './wideCompactContactPool';

const U32_BYTES = Uint32Array.BYTES_PER_ELEMENT;
const U32_MAX = 0xffff_ffff;

export const WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE = 64;
export const WIDE_EXACT_CONTACT_GENERATION_DEFAULT_WORKGROUP_LIMIT = 65_535;
export const WIDE_EXACT_CONTACT_GENERATION_PAIR_MANIFOLD_SLOTS = 8;

/**
 * Seven raw 128-bit lanes shared by DCD and CCD.  The earlier five-vec4 draft
 * could not preserve both production DCD's current quaternion and production
 * CCD's independently sourced start quaternion.  Keeping explicit
 * current/start/end poses also prevents an integration-order assumption from
 * silently changing contact geometry.  wideExactCollisionBodyState.ts copies
 * every lane as vec4u, so packed shape metadata and every finite f32 bit are
 * preserved without arithmetic or NaN canonicalisation.
 */
export const WIDE_EXACT_COLLISION_BODY_STATE = Object.freeze({
  currentPositionAndInverseMass: 0,
  currentQuaternion: 1,
  shape: 2,
  startPositionAndInverseMass: 3,
  startQuaternion: 4,
  endPositionAndInverseMass: 5,
  endQuaternion: 6,
  /** Compatibility names for the CCD target/end pose. */
  targetPosition: 5,
  targetQuaternion: 6,
  vec4sPerBody: 7,
  wordsPerBody: 28,
  bytesPerBody: 112,
} as const);

/** Two u32 words per persistent logical slot; exactly 128 MiB at 510K. */
export const WIDE_EXACT_CONTACT_DECISION = Object.freeze({
  encodedInfo: 0,
  flags: 1,
  wordsPerLogicalSlot: 2,
  activeBit: 1 << 0,
  newlyReservedBit: 1 << 1,
  reservationFailedBit: 1 << 2,
  dcdBit: 1 << 3,
  ccdBit: 1 << 4,
  sphereSphereBit: 1 << 5,
  sphereBoxBit: 1 << 6,
  boxBoxBit: 1 << 7,
  /** Previous one-step CCD row whose normal penalty may seed a fresh DCD row. */
  priorSpeculativeBit: 1 << 8,
  /** Previous discrete row used only to replay hysteresis/fallback exactly. */
  priorDcdActiveBit: 1 << 9,
  /** Pre-lifecycle pass captured the exact fresh-row normal-penalty seed. */
  priorPenaltySeededBit: 1 << 10,
} as const);

/** Transaction-local state. Sticky pool fatal bits remain in the pool header. */
export const WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL = Object.freeze({
  failureBits: 0,
  reservations: 1,
  rollbacks: 2,
  releases: 3,
  committedDescriptors: 4,
  words: 16,
} as const);

export const WideExactContactLifecycleFailureBit = Object.freeze({
  rowPoolOverflow: 1 << 0,
  duplicateLogicalOwner: 1 << 1,
  freeListOverflow: 1 << 2,
  missingReservation: 1 << 3,
  invalidDescriptor: 1 << 4,
} as const);

/** Header preceding the descriptor-index stream consumed in canonical order. */
export const WIDE_EXACT_ACTIVE_DESCRIPTOR_STREAM = Object.freeze({
  count: 0,
  epoch: 1,
  dispatchWorkgroupsX: 2,
  flags: 3,
  descriptorStart: 4,
  headerWords: 4,
} as const);

export interface WideExactContactGenerationDeviceLimits {
  maxStorageBufferBindingSize: number;
  maxBufferSize: number;
  maxComputeWorkgroupsPerDimension?: number;
}

export interface WideExactContactGenerationPlanInput {
  maxBodies: number;
  persistentHashCapacity: number;
  maxPersistentManifolds: number;
  pairManifoldSlots: number;
  contactRowCapacity: number;
  bindingOffsetAlignmentBytes?: number;
  limits?: WideExactContactGenerationDeviceLimits;
}

export interface WideExactContactGenerationDispatch {
  itemCapacity: number;
  logicalWorkgroups: number;
  workgroups: readonly [number, number, 1];
  invocationCapacity: number;
}

export type WideExactContactGenerationPassStatus =
  | 'implemented-foundation'
  | 'implemented-box-box-dcd'
  | 'implemented-all-dcd'
  | 'formula-port-required';

export interface WideExactContactGenerationPassBinding {
  binding: number;
  resource: string;
  access: 'read' | 'read_write';
}

export interface WideExactContactGenerationPassContract {
  name: string;
  status: WideExactContactGenerationPassStatus;
  dispatchDomain: 'single' | 'active-descriptor' | 'logical-slot' | 'scan';
  bindings: readonly WideExactContactGenerationPassBinding[];
  storageBindingCount: number;
  exactRequirement: string;
}

export interface WideExactContactGenerationScratchLayout {
  logicalSlotCapacity: number;
  activeDescriptorStreamWords: number;
  activeDescriptorStreamBytes: number;
  decisionWords: number;
  decisionBytes: number;
  lifecycleControlWords: number;
  lifecycleControlBytes: number;
  stableDescriptorCountWords: number;
  stableDescriptorOffsetWords: number;
  stableCompactionBytes: number;
  collisionBodyStateBytes: number;
  allBindingsWithinLimits: boolean;
}

export interface WideExactContactGenerationPlan {
  maxBodies: number;
  maxPersistentManifolds: number;
  pairManifoldSlots: number;
  contactRowCapacity: number;
  pool: WideCompactContactPoolLayout;
  scratch: WideExactContactGenerationScratchLayout;
  descriptorDispatch: WideExactContactGenerationDispatch;
  logicalSlotDispatch: WideExactContactGenerationDispatch;
  passContracts: readonly WideExactContactGenerationPassContract[];
  maxStorageBindingsInAnyPass: number;
  withinStorageBindingBudget: boolean;
  allBindingsWithinLimits: boolean;
  productionReady: false;
}

export interface WideExactContactLifecycleReferenceInput {
  descriptorCapacity: number;
  pairManifoldSlots: number;
  contactRowCapacity: number;
  activeDescriptors: readonly number[];
  desiredMasks: Uint32Array;
  outputMasks: Uint32Array;
  slotRows: Uint32Array;
  currentFreeRows?: readonly number[];
  rowHighWater: number;
}

export interface WideExactContactLifecycleReferenceResult {
  committed: boolean;
  failureBits: number;
  outputMasks: Uint32Array;
  slotRows: Uint32Array;
  rowHighWater: number;
  consumedFreeRows: number;
  nextFreeRows: readonly number[];
  reservations: number;
  rollbacks: number;
  releases: number;
}

export interface WideExactReferenceContactRow {
  meta: readonly [number, number, number, number];
  normalPen: readonly [number, number, number, number];
  armA: readonly [number, number, number, number];
  armB: readonly [number, number, number, number];
  constraintC0: readonly [number, number, number, number];
  shadow: readonly [number, number, number, number];
  dual: readonly [number, number, number, number];
  penalty: readonly [number, number, number, number];
  cache: readonly [number, number, number, number];
}

export interface WideExactContactGenerationSelfTestResult {
  ok: boolean;
  checks: number;
  failures: readonly string[];
  coliseum510k: Readonly<{
    logicalSlots: number;
    activeDescriptorStreamBytes: number;
    decisionBytes: number;
    collisionBodyStateBytes: number;
    maxStorageBindingsInAnyPass: number;
    descriptorDispatch: readonly [number, number, 1];
    logicalSlotDispatch: readonly [number, number, 1];
  }>;
}

export interface WideExactContactFormulaPort {
  shapePair: 'box-box' | 'sphere-box/floor' | 'sphere-sphere' | 'ccd';
  currentSourceAnchor: string;
  targetPasses: readonly string[];
  status: 'ported-box-box-dcd' | 'ported-sphere-dcd' | 'not-yet-ported';
  exactRequirement: string;
}

/**
 * The box/box discrete path is now executable below.  The remaining entries
 * stay explicit so a DCD-only result can never be confused with CCD coverage.
 */
export const WIDE_EXACT_CONTACT_IMPLEMENTED_FORMULA_PORTS:
readonly WideExactContactFormulaPort[] = Object.freeze([
  Object.freeze({
    shapePair: 'sphere-box/floor' as const,
    currentSourceAnchor: 'ContactGenerationStage legacyPairShaderSource: isSphereA != isSphereB branch',
    targetPasses: Object.freeze(['classify-dcd', 'transition-persistent-state', 'materialize-geometry']),
    status: 'ported-sphere-dcd' as const,
    exactRequirement: 'Preserve outside/inside closest-point branches, face-axis tie breaks, normal orientation, mass-scaled static-target penalty and local/world arm convention.',
  }),
  Object.freeze({
    shapePair: 'sphere-sphere' as const,
    currentSourceAnchor: 'ContactGenerationStage legacyPairShaderSource: isSphereA && isSphereB branch',
    targetPasses: Object.freeze(['classify-dcd', 'transition-persistent-state', 'materialize-geometry']),
    status: 'ported-sphere-dcd' as const,
    exactRequirement: 'Preserve coincident-center fallback, separation test, endpoint arms and exact single-row feature key.',
  }),
  Object.freeze({
    shapePair: 'box-box' as const,
    currentSourceAnchor: 'ContactGenerationStage legacyPairShaderSource: 15-axis SAT, face clipping, edge closest-points, candidate reduction and exact-key merge',
    targetPasses: Object.freeze(['classify-dcd', 'transition-persistent-state', 'materialize-geometry']),
    status: 'ported-box-box-dcd' as const,
    exactRequirement: 'Preserve f32 operation order, separation/hysteresis thresholds, feature ordering, four-row spread reduction, sticky anchors and tangent rotation.',
  }),
]);

export const WIDE_EXACT_CONTACT_REMAINING_FORMULA_PORTS:
readonly WideExactContactFormulaPort[] = Object.freeze([
  Object.freeze({
    shapePair: 'ccd' as const,
    currentSourceAnchor: 'ContactGenerationStage CCD prefilter/speculative kernels and ccdSweepShapePair in ccdHelpers.ts',
    targetPasses: Object.freeze(['classify-ccd', 'transition-persistent-state', 'materialize-geometry']),
    status: 'not-yet-ported' as const,
    exactRequirement: 'Keep fail-open semantics, current-overlap rejection, analytic/CA ordering, conservative fallback, speculative bit, one-step row lifecycle and fatal safety propagation.',
  }),
]);

function nonNegativeSafeInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative safe integer (received ${String(value)}).`);
  }
  return value;
}

function positiveSafeInteger(value: number, label: string): number {
  const result = nonNegativeSafeInteger(value, label);
  if (result < 1) throw new Error(`${label} must be positive.`);
  return result;
}

function checkedProduct(label: string, ...factors: number[]): number {
  let result = 1;
  for (const factor of factors) {
    result *= factor;
    if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  }
  return result;
}

function checkedSum(label: string, ...terms: number[]): number {
  const result = terms.reduce((sum, value) => sum + value, 0);
  if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  return result;
}

function makeBindings(
  resources: readonly (readonly [string, 'read' | 'read_write'])[],
): readonly WideExactContactGenerationPassBinding[] {
  return Object.freeze(resources.map(([resource, access], binding) => Object.freeze({
    binding,
    resource,
    access,
  })));
}

const classifyBindings = makeBindings([
  ['collisionBodyState', 'read_write'],
  ['activeDescriptors', 'read_write'],
  ['descriptorEndpointsCombined', 'read'],
  ['persistent.slotRow', 'read'],
  ['persistent.outputMask', 'read'],
  ['contact.meta', 'read'],
  ['contact.normalPen', 'read'],
  ['contact.armA', 'read'],
  ['contact.armB', 'read'],
  ['scratch.decisions', 'read_write'],
] as const);

const stateTransitionBindings = makeBindings([
  ['collisionBodyState', 'read_write'],
  ['activeDescriptors', 'read_write'],
  ['descriptorEndpointsCombined', 'read'],
  ['persistent.slotRow', 'read'],
  ['scratch.decisions', 'read_write'],
  ['contact.normalPen', 'read'],
  ['contact.armA', 'read'],
  ['contact.shadow', 'read_write'],
  ['contact.dual', 'read_write'],
  ['contact.penalty', 'read_write'],
] as const);

const spherePenaltyPreseedBindings = makeBindings([
  ['collisionBodyState', 'read_write'],
  ['activeDescriptors', 'read_write'],
  ['descriptorEndpointsCombined', 'read'],
  ['persistent.slotRow', 'read'],
  ['scratch.decisions', 'read_write'],
  ['contact.penalty', 'read_write'],
] as const);

const materializeBindings = makeBindings([
  ['collisionBodyState', 'read_write'],
  ['activeDescriptors', 'read_write'],
  ['descriptorEndpointsCombined', 'read'],
  ['persistent.slotRow', 'read'],
  ['scratch.decisions', 'read_write'],
  ['contact.meta', 'read_write'],
  ['contact.normalPen', 'read_write'],
  ['contact.armA', 'read_write'],
  ['contact.armB', 'read_write'],
  ['contact.constraintC0', 'read_write'],
] as const);

const materializeCacheBindings = makeBindings([
  ['collisionBodyState', 'read_write'],
  ['activeDescriptors', 'read_write'],
  ['descriptorEndpointsCombined', 'read'],
  ['persistent.slotRow', 'read'],
  ['scratch.decisions', 'read_write'],
  ['contact.normalPen', 'read'],
  ['contact.armA', 'read'],
  ['contact.cache', 'read_write'],
] as const);

/** Every pass is <=10 storage bindings; formula stages remain unimplemented. */
export const WIDE_EXACT_CONTACT_GENERATION_PASS_CONTRACTS:
readonly WideExactContactGenerationPassContract[] = Object.freeze([
  Object.freeze({
    name: 'classify-dcd',
    status: 'implemented-all-dcd' as const,
    dispatchDomain: 'active-descriptor' as const,
    bindings: classifyBindings,
    storageBindingCount: classifyBindings.length,
    exactRequirement: 'Compose box/box and sphere-specialized classifiers before one lifecycle transaction; preserve exact feature matching and logical-slot selection.',
  }),
  Object.freeze({
    name: 'classify-ccd',
    status: 'formula-port-required' as const,
    dispatchDomain: 'active-descriptor' as const,
    bindings: classifyBindings,
    storageBindingCount: classifyBindings.length,
    exactRequirement: 'Run the existing CCD sweep after DCD ownership is known; never replace FAIL_OPEN with a miss and never overwrite a valid DCD decision.',
  }),
  Object.freeze({
    name: 'preseed-sphere-penalty',
    status: 'implemented-all-dcd' as const,
    dispatchDomain: 'active-descriptor' as const,
    bindings: spherePenaltyPreseedBindings,
    storageBindingCount: spherePenaltyPreseedBindings.length,
    exactRequirement: 'Before retiring old rows, capture the maximum speculative normal penalty and the mass-scaled static-target seed into the chosen fresh sphere row.',
  }),
  ...(['clear-lifecycle', 'reserve-missing-rows', 'validate-reservations', 'commit-or-rollback'] as const)
    .map((name): WideExactContactGenerationPassContract => {
      const bindings = name === 'clear-lifecycle'
        ? makeBindings([['lifecycleControl', 'read_write']] as const)
        : name === 'validate-reservations'
          ? makeBindings([
            ['activeDescriptors', 'read_write'],
            ['scratch.decisions', 'read_write'],
            ['persistent.slotRow', 'read'],
            ['lifecycleControl', 'read_write'],
          ] as const)
          : makeBindings([
            ['activeDescriptors', 'read_write'],
            ['scratch.decisions', 'read_write'],
            ['persistent.slotRow', 'read_write'],
            ['persistent.outputMask', 'read_write'],
            ['persistent.header', 'read_write'],
            ['persistent.freeRowsCombined', 'read_write'],
            ['lifecycleControl', 'read_write'],
          ] as const);
      return Object.freeze({
        name,
        status: 'implemented-foundation' as const,
        dispatchDomain: name === 'clear-lifecycle' ? 'single' as const : 'active-descriptor' as const,
        bindings,
        storageBindingCount: bindings.length,
        exactRequirement: name === 'commit-or-rollback'
          ? 'Commit every desired mask only after a separate global validation dispatch, or return every newly reserved row and preserve all old masks/rows.'
          : 'Use exact u32 descriptor/logical-slot/physical-row identities and sticky fatal reporting.',
      });
    }),
  Object.freeze({
    name: 'transition-persistent-state',
    status: 'implemented-all-dcd' as const,
    dispatchDomain: 'active-descriptor' as const,
    bindings: stateTransitionBindings,
    storageBindingCount: stateTransitionBindings.length,
    exactRequirement: 'Before geometry overwrite, rotate/carry shadow and dual exactly for matched features; reset fresh rows and preserve the static-sphere normal-penalty seed.',
  }),
  Object.freeze({
    name: 'materialize-geometry',
    status: 'implemented-all-dcd' as const,
    dispatchDomain: 'active-descriptor' as const,
    bindings: materializeBindings,
    storageBindingCount: materializeBindings.length,
    exactRequirement: 'Re-evaluate the selected DCD shape branch in identical f32 order and write meta/normal/arms/C0 without narrowing any lane.',
  }),
  Object.freeze({
    name: 'materialize-cache',
    status: 'implemented-all-dcd' as const,
    dispatchDomain: 'active-descriptor' as const,
    bindings: materializeCacheBindings,
    storageBindingCount: materializeCacheBindings.length,
    exactRequirement: 'Preserve cache.x on exact matches and bitcast the regenerated full-f32 tangent into cache.yzw before geometry overwrites the previous tangent angle.',
  }),
  ...(['stable-active-count', 'stable-active-scan', 'stable-active-scatter'] as const)
    .map((name): WideExactContactGenerationPassContract => {
      const bindings = makeBindings([
        ['activeDescriptors', 'read_write'],
        ['scratch.decisions', 'read_write'],
        ['persistent.slotRow', 'read'],
        ['scratch.stableCountsOffsets', 'read_write'],
        ['pairActivity.activeContacts', 'read_write'],
      ] as const);
      return Object.freeze({
        name,
        status: 'formula-port-required' as const,
        dispatchDomain: 'scan' as const,
        bindings,
        storageBindingCount: bindings.length,
        exactRequirement: 'Preserve descriptor-stream order and increasing logical slot; atomics may count but may not choose final active-row ordinal.',
      });
    }),
]);

export function resolveWideExactContactGenerationDispatch(
  itemCapacityValue: number,
  maxComputeWorkgroupsPerDimensionValue = WIDE_EXACT_CONTACT_GENERATION_DEFAULT_WORKGROUP_LIMIT,
): WideExactContactGenerationDispatch {
  const itemCapacity = nonNegativeSafeInteger(itemCapacityValue, 'itemCapacity');
  const limit = positiveSafeInteger(
    maxComputeWorkgroupsPerDimensionValue,
    'maxComputeWorkgroupsPerDimension',
  );
  if (limit > 65_535) throw new Error('maxComputeWorkgroupsPerDimension exceeds WebGPU limit.');
  const logicalWorkgroups = Math.ceil(itemCapacity / WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE);
  if (logicalWorkgroups === 0) {
    return Object.freeze({
      itemCapacity,
      logicalWorkgroups,
      workgroups: Object.freeze([0, 1, 1] as const),
      invocationCapacity: 0,
    });
  }
  const x = Math.min(logicalWorkgroups, limit);
  const y = Math.ceil(logicalWorkgroups / x);
  if (y > limit) throw new Error(`Dispatch exceeds ${limit} x ${limit} workgroups.`);
  return Object.freeze({
    itemCapacity,
    logicalWorkgroups,
    workgroups: Object.freeze([x, y, 1] as const),
    invocationCapacity: checkedProduct(
      'wide contact generation invocation capacity',
      x,
      y,
      WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE,
    ),
  });
}

function bindingFits(bytes: number, limits?: WideExactContactGenerationDeviceLimits): boolean {
  return limits === undefined || (
    bytes <= positiveSafeInteger(limits.maxStorageBufferBindingSize, 'maxStorageBufferBindingSize')
    && bytes <= positiveSafeInteger(limits.maxBufferSize, 'maxBufferSize')
  );
}

/** Allocation-free capacity, dispatch and binding plan for the inert wide path. */
export function planWideExactContactGeneration(
  requested: WideExactContactGenerationPlanInput,
): WideExactContactGenerationPlan {
  const maxBodies = positiveSafeInteger(requested.maxBodies, 'maxBodies');
  const maxPersistentManifolds = positiveSafeInteger(
    requested.maxPersistentManifolds,
    'maxPersistentManifolds',
  );
  const pairManifoldSlots = positiveSafeInteger(
    requested.pairManifoldSlots,
    'pairManifoldSlots',
  );
  if (pairManifoldSlots > 8) throw new Error('pairManifoldSlots must be <= 8.');
  const contactRowCapacity = positiveSafeInteger(
    requested.contactRowCapacity,
    'contactRowCapacity',
  );
  const logicalSlotCapacity = checkedProduct(
    'wide contact logical slot capacity',
    maxPersistentManifolds,
    pairManifoldSlots,
  );
  const decisionWords = checkedProduct(
    'wide contact decision words',
    logicalSlotCapacity,
    WIDE_EXACT_CONTACT_DECISION.wordsPerLogicalSlot,
  );
  if (decisionWords > U32_MAX) throw new Error('Decision scratch exceeds the u32 WGSL ABI.');
  const activeDescriptorStreamWords = checkedSum(
    'wide active descriptor stream words',
    WIDE_EXACT_ACTIVE_DESCRIPTOR_STREAM.headerWords,
    maxPersistentManifolds,
  );
  const activeDescriptorStreamBytes = checkedProduct(
    'wide active descriptor stream bytes',
    activeDescriptorStreamWords,
    U32_BYTES,
  );
  const decisionBytes = checkedProduct('wide contact decision bytes', decisionWords, U32_BYTES);
  const collisionBodyStateBytes = checkedProduct(
    'wide collision body-state bytes',
    maxBodies,
    WIDE_EXACT_COLLISION_BODY_STATE.bytesPerBody,
  );
  const lifecycleControlBytes = checkedProduct(
    'lifecycle control bytes',
    WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.words,
    U32_BYTES,
  );
  const stableDescriptorCountWords = maxPersistentManifolds;
  const stableDescriptorOffsetWords = checkedSum(
    'stable descriptor offsets',
    maxPersistentManifolds,
    1,
  );
  const stableCompactionBytes = checkedProduct(
    'stable active compaction bytes',
    checkedSum(
      'stable active compaction words',
      stableDescriptorCountWords,
      stableDescriptorOffsetWords,
    ),
    U32_BYTES,
  );
  const pool = planWideCompactContactPoolLayout({
    persistentManifoldHashCapacity: requested.persistentHashCapacity,
    maxPersistentManifolds,
    pairManifoldSlots,
    compactContactPoolCapacity: contactRowCapacity,
    bindingOffsetAlignmentBytes: requested.bindingOffsetAlignmentBytes,
  });
  const decisionFits = bindingFits(decisionBytes, requested.limits);
  const activeDescriptorStreamFits = bindingFits(activeDescriptorStreamBytes, requested.limits);
  const bodyStateFits = bindingFits(collisionBodyStateBytes, requested.limits);
  const lifecycleFits = bindingFits(lifecycleControlBytes, requested.limits);
  const stableCompactionFits = bindingFits(stableCompactionBytes, requested.limits);
  const descriptorCombinedBytes = pool.descriptorBodyB.end * U32_BYTES
    - pool.descriptorBodyA.byteOffset;
  const freeRowsCombinedBytes = pool.freeRows1.end * U32_BYTES
    - pool.freeRows0.byteOffset;
  const persistentSlicesFit = [
    pool.header.byteLength,
    descriptorCombinedBytes,
    pool.slotRow.byteLength,
    pool.outputMask.byteLength,
    freeRowsCombinedBytes,
  ].every((bytes) => bindingFits(bytes, requested.limits));
  const dimensionLimit = requested.limits?.maxComputeWorkgroupsPerDimension
    ?? WIDE_EXACT_CONTACT_GENERATION_DEFAULT_WORKGROUP_LIMIT;
  const descriptorDispatch = resolveWideExactContactGenerationDispatch(
    maxPersistentManifolds,
    dimensionLimit,
  );
  const logicalSlotDispatch = resolveWideExactContactGenerationDispatch(
    logicalSlotCapacity,
    dimensionLimit,
  );
  const maxStorageBindingsInAnyPass = Math.max(
    ...WIDE_EXACT_CONTACT_GENERATION_PASS_CONTRACTS.map(
      (contract) => contract.storageBindingCount,
    ),
  );
  const withinStorageBindingBudget = maxStorageBindingsInAnyPass
    <= REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE;
  const allBindingsWithinLimits = decisionFits
    && activeDescriptorStreamFits
    && bodyStateFits
    && lifecycleFits
    && stableCompactionFits
    && persistentSlicesFit;
  return Object.freeze({
    maxBodies,
    maxPersistentManifolds,
    pairManifoldSlots,
    contactRowCapacity,
    pool,
    scratch: Object.freeze({
      logicalSlotCapacity,
      activeDescriptorStreamWords,
      activeDescriptorStreamBytes,
      decisionWords,
      decisionBytes,
      lifecycleControlWords: WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.words,
      lifecycleControlBytes,
      stableDescriptorCountWords,
      stableDescriptorOffsetWords,
      stableCompactionBytes,
      collisionBodyStateBytes,
      allBindingsWithinLimits,
    }),
    descriptorDispatch,
    logicalSlotDispatch,
    passContracts: WIDE_EXACT_CONTACT_GENERATION_PASS_CONTRACTS,
    maxStorageBindingsInAnyPass,
    withinStorageBindingBudget,
    allBindingsWithinLimits,
    productionReady: false as const,
  });
}

export interface WideExactContactLifecycleWgslBundle {
  clear: string;
  reserve: string;
  validate: string;
  commitOrRollback: string;
  dispatchSequence: readonly [
    'clear',
    'dispatch-boundary',
    'reserve',
    'dispatch-boundary',
    'validate',
    'dispatch-boundary',
    'commit-or-rollback',
    'dispatch-boundary',
  ];
}

function lifecycleWgslConstants(plan: WideExactContactGenerationPlan): string {
  const freeRows1RelativeOffset = plan.pool.freeRows1.offset - plan.pool.freeRows0.offset;
  return /* wgsl */`
const DESCRIPTOR_CAPACITY: u32 = ${plan.maxPersistentManifolds}u;
const PAIR_MANIFOLD_SLOTS: u32 = ${plan.pairManifoldSlots}u;
const CONTACT_ROW_CAPACITY: u32 = ${plan.contactRowCapacity}u;
const ACTIVE_DESCRIPTOR_START: u32 = ${WIDE_EXACT_ACTIVE_DESCRIPTOR_STREAM.descriptorStart}u;
const DISPATCH_WORKGROUPS_X: u32 = ${plan.descriptorDispatch.workgroups[0]}u;
const DECISION_ACTIVE: u32 = ${WIDE_EXACT_CONTACT_DECISION.activeBit}u;
const DECISION_NEWLY_RESERVED: u32 = ${WIDE_EXACT_CONTACT_DECISION.newlyReservedBit}u;
const DECISION_RESERVATION_FAILED: u32 = ${WIDE_EXACT_CONTACT_DECISION.reservationFailedBit}u;
const FAILURE_ROW_POOL_OVERFLOW: u32 = ${WideExactContactLifecycleFailureBit.rowPoolOverflow}u;
const FAILURE_DUPLICATE_LOGICAL_OWNER: u32 = ${WideExactContactLifecycleFailureBit.duplicateLogicalOwner}u;
const FAILURE_FREE_LIST_OVERFLOW: u32 = ${WideExactContactLifecycleFailureBit.freeListOverflow}u;
const FAILURE_MISSING_RESERVATION: u32 = ${WideExactContactLifecycleFailureBit.missingReservation}u;
const FAILURE_INVALID_DESCRIPTOR: u32 = ${WideExactContactLifecycleFailureBit.invalidDescriptor}u;
const HEADER_ROW_HIGH_WATER: u32 = ${CompactContactPoolHeaderOffset.rowHighWater}u;
const HEADER_EPOCH_PARITY: u32 = ${CompactContactPoolHeaderOffset.epochParity}u;
const HEADER_FREE_COUNT_0: u32 = ${CompactContactPoolHeaderOffset.freeCount0}u;
const HEADER_FREE_COUNT_1: u32 = ${CompactContactPoolHeaderOffset.freeCount1}u;
const HEADER_FREE_CURSOR_0: u32 = ${CompactContactPoolHeaderOffset.freeCursor0}u;
const HEADER_FREE_CURSOR_1: u32 = ${CompactContactPoolHeaderOffset.freeCursor1}u;
const HEADER_FATAL_BITS: u32 = ${CompactContactPoolHeaderOffset.fatalBits}u;
const FREE_ROWS_1_RELATIVE_OFFSET: u32 = ${freeRows1RelativeOffset}u;
const CONTROL_FAILURE_BITS: u32 = ${WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.failureBits}u;
const CONTROL_RESERVATIONS: u32 = ${WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.reservations}u;
const CONTROL_ROLLBACKS: u32 = ${WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.rollbacks}u;
const CONTROL_RELEASES: u32 = ${WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.releases}u;
const CONTROL_COMMITTED_DESCRIPTORS: u32 = ${WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.committedDescriptors}u;
const CONTACT_INFO_LOGICAL_SLOT_SHIFT: u32 = ${CONTACT_INFO_LOGICAL_SLOT_SHIFT}u;
const CONTACT_INFO_LOGICAL_SLOT_MASK: u32 = ${CONTACT_INFO_LOGICAL_SLOT_MASK}u;
`;
}

function wideExactBoxBoxStateAndCacheWgsl(common: string): Readonly<{
  transitionState: string;
  materializeCache: string;
}> {
  const transitionState = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(5) var<storage, read> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(6) var<storage, read> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read_write> contactShadowPlane: Vec4fBuffer;
@group(0) @binding(8) var<storage, read_write> contactDualPlane: Vec4fBuffer;
@group(0) @binding(9) var<storage, read_write> contactPenaltyPlane: Vec4fBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  var forcedFeature: i32 = -1;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & (DECISION_ACTIVE | DECISION_BOX_BOX)) == (DECISION_ACTIVE | DECISION_BOX_BOX)) {
      forcedFeature = i32(decodeContactBaseFeatureExact(decision.x));
      break;
    }
  }
  if (forcedFeature < 0) { return; }
  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let manifold = buildWideBoxBoxManifold(
    positionMassA.xyz, quaternionA, shapeA.yzw,
    positionMassB.xyz, quaternionB, shapeB.yzw,
    false, -1, vec3f(0.0), forcedFeature,
  );
  if (manifold.isActive == 0u) { return; }

  var preferredTangent = vec3f(0.0);
  var preferredCount = 0u;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & DECISION_ACTIVE) == 0u || ((decision.x >> 16u) & 1u) == 0u) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let oldNormal = contactNormalPlane.values[row].xyz;
    let oldTangentAngle = contactArmAPlane.values[row].w;
    preferredTangent += tangentBasisFromAngleExact(oldNormal, oldTangentAngle).t1;
    preferredCount += 1u;
  }
  var writeTangent = tangentBasisFromPreferredT1Exact(manifold.normal, manifold.tangentT1);
  if (preferredCount > 0u && dot(preferredTangent, writeTangent.t1) < 0.0) {
    writeTangent = TangentBasis(-writeTangent.t1, -writeTangent.t2);
  }
  let frictionScale = sqrt(
    max(shapeFrictionExact(shapeA.x), 0.0)
      * max(shapeFrictionExact(shapeB.x), 0.0)
  );

  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & DECISION_ACTIVE) == 0u) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let preserve = ((decision.x >> 16u) & 1u) != 0u;
    if (!preserve) {
      contactShadowPlane.values[row] = vec4f(0.0, 0.0, 0.0, frictionScale);
      contactDualPlane.values[row] = vec4f(0.0);
      contactPenaltyPlane.values[row] = vec4f(1.0, 1.0, 1.0, frictionScale);
      continue;
    }
    let oldNormal = contactNormalPlane.values[row].xyz;
    let oldTangentAngle = contactArmAPlane.values[row].w;
    let oldBasis = tangentBasisFromAngleExact(oldNormal, oldTangentAngle);
    let oldShadow = contactShadowPlane.values[row];
    let oldDual = contactDualPlane.values[row];
    var carriedShadow = oldShadow;
    var carriedDual = oldDual;
    let lambdaN = max(oldShadow.x, 0.0);
    if (dot(oldNormal, oldNormal) > 1e-10) {
      let tangentImpulseWorld = oldBasis.t1 * oldShadow.y + oldBasis.t2 * oldShadow.z;
      var lambdaT = vec2f(
        dot(tangentImpulseWorld, writeTangent.t1),
        dot(tangentImpulseWorld, writeTangent.t2),
      );
      let maxT = FRICTION_STATIC * frictionScale * lambdaN;
      let maxT2 = maxT * maxT;
      let lambdaT2 = dot(lambdaT, lambdaT);
      if (lambdaT2 > maxT2 && lambdaT2 > 1e-12) {
        lambdaT *= maxT * inverseSqrt(lambdaT2);
      }
      carriedShadow = vec4f(lambdaN, lambdaT.x, lambdaT.y, oldShadow.w);
      let oldDualTangentWorld = oldBasis.t1 * oldDual.y + oldBasis.t2 * oldDual.z;
      carriedDual = vec4f(
        oldDual.x,
        dot(oldDualTangentWorld, writeTangent.t1),
        dot(oldDualTangentWorld, writeTangent.t2),
        0.0,
      );
    }
    contactShadowPlane.values[row] = carriedShadow;
    contactDualPlane.values[row] = carriedDual;
    // Exact matches retain the complete adaptive penalty vector unchanged.
    contactPenaltyPlane.values[row] = contactPenaltyPlane.values[row];
  }
}
`;

  const materializeCache = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(5) var<storage, read> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(6) var<storage, read> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read_write> contactCachePlane: Vec4uBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  var forcedFeature: i32 = -1;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & (DECISION_ACTIVE | DECISION_BOX_BOX)) == (DECISION_ACTIVE | DECISION_BOX_BOX)) {
      forcedFeature = i32(decodeContactBaseFeatureExact(decision.x));
      break;
    }
  }
  if (forcedFeature < 0) { return; }
  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let manifold = buildWideBoxBoxManifold(
    positionMassA.xyz, quaternionA, shapeA.yzw,
    positionMassB.xyz, quaternionB, shapeB.yzw,
    false, -1, vec3f(0.0), forcedFeature,
  );
  if (manifold.isActive == 0u) { return; }
  var preferredTangent = vec3f(0.0);
  var preferredCount = 0u;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & DECISION_ACTIVE) == 0u || ((decision.x >> 16u) & 1u) == 0u) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let oldNormal = contactNormalPlane.values[row].xyz;
    let oldAngle = contactArmAPlane.values[row].w;
    preferredTangent += tangentBasisFromAngleExact(oldNormal, oldAngle).t1;
    preferredCount += 1u;
  }
  var writeTangent = tangentBasisFromPreferredT1Exact(manifold.normal, manifold.tangentT1);
  if (preferredCount > 0u && dot(preferredTangent, writeTangent.t1) < 0.0) {
    writeTangent = TangentBasis(-writeTangent.t1, -writeTangent.t2);
  }
  let tangentBits = bitcast<vec3u>(writeTangent.t1);
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & DECISION_ACTIVE) == 0u) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let oldCacheWord = contactCachePlane.values[row].x;
    let preserve = ((decision.x >> 16u) & 1u) != 0u;
    contactCachePlane.values[row] = vec4u(select(0u, oldCacheWord, preserve), tangentBits);
  }
}
`;

  return Object.freeze({ transitionState, materializeCache });
}

function lifecycleAppendFreeRowWgsl(): string {
  return /* wgsl */`
fn recordFailure(
  poolHeader: ptr<storage, AtomicU32Buffer, read_write>,
  lifecycleControl: ptr<storage, AtomicU32Buffer, read_write>,
  transactionBit: u32,
  stickyBit: u32,
) {
  atomicOr(&(*lifecycleControl).values[CONTROL_FAILURE_BITS], transactionBit);
  atomicOr(&(*poolHeader).values[HEADER_FATAL_BITS], stickyBit);
}

fn appendNextFreeRow(
  row: u32,
  poolHeader: ptr<storage, AtomicU32Buffer, read_write>,
  freeRows: ptr<storage, AtomicU32Buffer, read_write>,
  lifecycleControl: ptr<storage, AtomicU32Buffer, read_write>,
) {
  if (row >= CONTACT_ROW_CAPACITY) {
    recordFailure(
      poolHeader,
      lifecycleControl,
      FAILURE_DUPLICATE_LOGICAL_OWNER,
      ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
    );
    return;
  }
  let parity = atomicLoad(&(*poolHeader).values[HEADER_EPOCH_PARITY]);
  let nextCountAddress = select(HEADER_FREE_COUNT_1, HEADER_FREE_COUNT_0, parity != 0u);
  let nextBase = select(FREE_ROWS_1_RELATIVE_OFFSET, 0u, parity != 0u);
  let writeIndex = atomicAdd(&(*poolHeader).values[nextCountAddress], 1u);
  if (writeIndex < CONTACT_ROW_CAPACITY) {
    atomicStore(&(*freeRows).values[nextBase + writeIndex], row);
  } else {
    recordFailure(
      poolHeader,
      lifecycleControl,
      FAILURE_FREE_LIST_OVERFLOW,
      ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
    );
  }
}
`;
}

/**
 * Executable raw WGSL for the exact all-or-rollback physical-row transaction.
 * It intentionally performs no shape math and is not selected by production.
 */
export function generateWideExactContactLifecycleWgsl(
  plan: WideExactContactGenerationPlan,
): WideExactContactLifecycleWgslBundle {
  const constants = lifecycleWgslConstants(plan);
  const appendFreeRow = lifecycleAppendFreeRowWgsl();
  const clear = /* wgsl */`
struct AtomicU32Buffer { values: array<atomic<u32>>, }
@group(0) @binding(0) var<storage, read_write> lifecycleControl: AtomicU32Buffer;

@compute @workgroup_size(1, 1, 1)
fn main() {
  for (var word = 0u; word < ${WIDE_EXACT_CONTACT_LIFECYCLE_CONTROL.words}u; word += 1u) {
    atomicStore(&lifecycleControl.values[word], 0u);
  }
}
`;
  const reserve = /* wgsl */`
struct U32Buffer { values: array<u32>, }
struct Vec2uBuffer { values: array<vec2u>, }
struct AtomicU32Buffer { values: array<atomic<u32>>, }
${constants}
@group(0) @binding(0) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(1) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(2) var<storage, read_write> slotRows: AtomicU32Buffer;
// This is logically read-only in this pass. It is declared read_write because
// slotRows/header/freeRows are non-overlapping views of the same monolithic
// persistent GPUBuffer, and WebGPU usage-scope validation is buffer-wide.
@group(0) @binding(3) var<storage, read_write> outputMasks: U32Buffer;
@group(0) @binding(4) var<storage, read_write> poolHeader: AtomicU32Buffer;
@group(0) @binding(5) var<storage, read_write> freeRows: AtomicU32Buffer;
@group(0) @binding(6) var<storage, read_write> lifecycleControl: AtomicU32Buffer;
${appendFreeRow}

fn failDecision(logicalSlot: u32, bit: u32, stickyBit: u32) {
  let decision = decisions.values[logicalSlot];
  decisions.values[logicalSlot] = vec2u(
    decision.x,
    decision.y | DECISION_RESERVATION_FAILED,
  );
  recordFailure(&poolHeader, &lifecycleControl, bit, stickyBit);
}

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = (
    workgroupId.y * DISPATCH_WORKGROUPS_X + workgroupId.x
  ) * ${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}u + localId.x;
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) {
    recordFailure(
      &poolHeader,
      &lifecycleControl,
      FAILURE_INVALID_DESCRIPTOR,
      ${CompactContactPoolFatalBit.persistentHashProtocolFailure}u,
    );
    return;
  }
  // Read the prior mask to keep the binding contract explicit. The reserve
  // pass never mutates it; only commit-or-rollback may publish a new mask.
  let priorMask = outputMasks.values[descriptor];
  _ = priorMask;
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let logicalSlot = logicalBase + slot;
    let decision = decisions.values[logicalSlot];
    if ((decision.y & DECISION_ACTIVE) == 0u) { continue; }
    if (atomicLoad(&slotRows.values[logicalSlot]) < CONTACT_ROW_CAPACITY) { continue; }

    let parity = atomicLoad(&poolHeader.values[HEADER_EPOCH_PARITY]);
    let currentCountAddress = select(HEADER_FREE_COUNT_0, HEADER_FREE_COUNT_1, parity != 0u);
    let currentCursorAddress = select(HEADER_FREE_CURSOR_0, HEADER_FREE_CURSOR_1, parity != 0u);
    let currentBase = select(0u, FREE_ROWS_1_RELATIVE_OFFSET, parity != 0u);
    let freeIndex = atomicAdd(&poolHeader.values[currentCursorAddress], 1u);
    let freeCount = min(
      atomicLoad(&poolHeader.values[currentCountAddress]),
      CONTACT_ROW_CAPACITY,
    );
    var row = CONTACT_ROW_CAPACITY;
    if (freeIndex < freeCount) {
      row = atomicLoad(&freeRows.values[currentBase + freeIndex]);
      if (row >= CONTACT_ROW_CAPACITY) {
        failDecision(
          logicalSlot,
          FAILURE_DUPLICATE_LOGICAL_OWNER,
          ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
        );
        continue;
      }
    } else {
      row = atomicAdd(&poolHeader.values[HEADER_ROW_HIGH_WATER], 1u);
      if (row >= CONTACT_ROW_CAPACITY) {
        atomicMin(&poolHeader.values[HEADER_ROW_HIGH_WATER], CONTACT_ROW_CAPACITY);
        failDecision(
          logicalSlot,
          FAILURE_ROW_POOL_OVERFLOW,
          ${CompactContactPoolFatalBit.rowPoolOverflow}u,
        );
        continue;
      }
    }

    var installed = false;
    var observed = CONTACT_ROW_CAPACITY;
    for (var attempt = 0u; attempt < 32u; attempt += 1u) {
      let result = atomicCompareExchangeWeak(
        &slotRows.values[logicalSlot],
        CONTACT_ROW_CAPACITY,
        row,
      );
      observed = result.old_value;
      if (result.exchanged) {
        installed = true;
        break;
      }
      if (observed != CONTACT_ROW_CAPACITY) { break; }
    }
    if (!installed) {
      appendNextFreeRow(row, &poolHeader, &freeRows, &lifecycleControl);
      failDecision(
        logicalSlot,
        FAILURE_DUPLICATE_LOGICAL_OWNER,
        ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
      );
      continue;
    }
    decisions.values[logicalSlot] = vec2u(
      decision.x,
      decision.y | DECISION_NEWLY_RESERVED,
    );
    atomicAdd(&lifecycleControl.values[CONTROL_RESERVATIONS], 1u);
  }
}
`;
  const validate = /* wgsl */`
struct U32Buffer { values: array<u32>, }
struct Vec2uBuffer { values: array<vec2u>, }
struct AtomicU32Buffer { values: array<atomic<u32>>, }
${constants}
@group(0) @binding(0) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(1) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(2) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(3) var<storage, read_write> lifecycleControl: AtomicU32Buffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = (
    workgroupId.y * DISPATCH_WORKGROUPS_X + workgroupId.x
  ) * ${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}u + localId.x;
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) {
    atomicOr(&lifecycleControl.values[CONTROL_FAILURE_BITS], FAILURE_INVALID_DESCRIPTOR);
    return;
  }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let logicalSlot = logicalBase + slot;
    let decision = decisions.values[logicalSlot];
    if ((decision.y & DECISION_ACTIVE) == 0u) { continue; }
    let encodedLogicalSlot = (
      decision.x & CONTACT_INFO_LOGICAL_SLOT_MASK
    ) >> CONTACT_INFO_LOGICAL_SLOT_SHIFT;
    if (
      encodedLogicalSlot != slot
      || (decision.y & DECISION_RESERVATION_FAILED) != 0u
      || slotRows.values[logicalSlot] >= CONTACT_ROW_CAPACITY
    ) {
      atomicOr(&lifecycleControl.values[CONTROL_FAILURE_BITS], FAILURE_MISSING_RESERVATION);
    }
  }
}
`;
  const commitOrRollback = /* wgsl */`
struct U32Buffer { values: array<u32>, }
struct Vec2uBuffer { values: array<vec2u>, }
struct AtomicU32Buffer { values: array<atomic<u32>>, }
${constants}
@group(0) @binding(0) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(1) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(2) var<storage, read_write> slotRows: AtomicU32Buffer;
@group(0) @binding(3) var<storage, read_write> outputMasks: AtomicU32Buffer;
@group(0) @binding(4) var<storage, read_write> poolHeader: AtomicU32Buffer;
@group(0) @binding(5) var<storage, read_write> freeRows: AtomicU32Buffer;
@group(0) @binding(6) var<storage, read_write> lifecycleControl: AtomicU32Buffer;
${appendFreeRow}

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = (
    workgroupId.y * DISPATCH_WORKGROUPS_X + workgroupId.x
  ) * ${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}u + localId.x;
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let transactionFailed = atomicLoad(
    &lifecycleControl.values[CONTROL_FAILURE_BITS],
  ) != 0u;
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;

  if (transactionFailed) {
    for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
      let logicalSlot = logicalBase + slot;
      let decision = decisions.values[logicalSlot];
      if ((decision.y & DECISION_NEWLY_RESERVED) != 0u) {
        let row = atomicExchange(&slotRows.values[logicalSlot], CONTACT_ROW_CAPACITY);
        appendNextFreeRow(row, &poolHeader, &freeRows, &lifecycleControl);
        atomicAdd(&lifecycleControl.values[CONTROL_ROLLBACKS], 1u);
      }
      decisions.values[logicalSlot] = vec2u(
        decision.x,
        decision.y & ~(DECISION_NEWLY_RESERVED | DECISION_RESERVATION_FAILED),
      );
    }
    return;
  }

  let oldMask = atomicLoad(&outputMasks.values[descriptor]);
  var desiredMask = 0u;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let logicalSlot = logicalBase + slot;
    let decision = decisions.values[logicalSlot];
    let desired = (decision.y & DECISION_ACTIVE) != 0u;
    if (desired) { desiredMask |= 1u << slot; }
    if (!desired && (oldMask & (1u << slot)) != 0u) {
      let row = atomicExchange(&slotRows.values[logicalSlot], CONTACT_ROW_CAPACITY);
      appendNextFreeRow(row, &poolHeader, &freeRows, &lifecycleControl);
      atomicAdd(&lifecycleControl.values[CONTROL_RELEASES], 1u);
    }
    decisions.values[logicalSlot] = vec2u(
      decision.x,
      decision.y & ~(DECISION_NEWLY_RESERVED | DECISION_RESERVATION_FAILED),
    );
  }
  atomicStore(&outputMasks.values[descriptor], desiredMask);
  atomicAdd(&lifecycleControl.values[CONTROL_COMMITTED_DESCRIPTORS], 1u);
}
`;
  return Object.freeze({
    clear,
    reserve,
    validate,
    commitOrRollback,
    dispatchSequence: Object.freeze([
      'clear',
      'dispatch-boundary',
      'reserve',
      'dispatch-boundary',
      'validate',
      'dispatch-boundary',
      'commit-or-rollback',
      'dispatch-boundary',
    ] as const),
  });
}

export interface WideExactBoxBoxDcdWgslOptions {
  contactSlop?: number;
  collisionMargin?: number;
  frictionStatic?: number;
}

export interface WideExactBoxBoxDcdWgslBundle {
  classify: string;
  transitionState: string;
  materializeCache: string;
  materializeGeometry: string;
  dispatchSequence: readonly [
    'classify-box-box-dcd',
    'dispatch-boundary',
    'row-lifecycle',
    'dispatch-boundary',
    'transition-box-box-state',
    'dispatch-boundary',
    'materialize-box-box-cache',
    'dispatch-boundary',
    'materialize-box-box-geometry',
    'dispatch-boundary',
  ];
  coverage: Readonly<{
    boxBoxDcd: true;
    sphereSphereDcd: false;
    sphereBoxDcd: false;
    ccd: false;
  }>;
}

function wgslFiniteF32(value: number, label: string): string {
  if (!Number.isFinite(value)) throw new Error(`${label} must be finite.`);
  const rounded = Math.fround(value);
  if (rounded < 0) throw new Error(`${label} must be non-negative.`);
  const literal = rounded.toPrecision(9);
  return literal.includes('.') || /e/i.test(literal) ? literal : `${literal}.0`;
}

// WGSL 16.2 reserved identifiers. Keep this scanner next to the raw-source
// generator because Chromium rejects these before pipeline-layout inference.
export const WGSL_16_2_RESERVED_IDENTIFIERS: readonly string[] = Object.freeze([
  'active', 'alignas', 'alignof', 'as', 'asm', 'asm_fragment', 'async',
  'attribute', 'auto', 'await', 'become', 'binding_array', 'cast', 'catch',
  'class', 'co_await', 'co_return', 'co_yield', 'coherent', 'column_major',
  'common', 'compile', 'compile_fragment', 'concept', 'const_cast', 'consteval',
  'constexpr', 'constinit', 'crate', 'debug', 'decltype', 'delete', 'demote',
  'demote_to_helper', 'do', 'dynamic_cast', 'enum', 'explicit', 'export',
  'extends', 'extern', 'external', 'fallthrough', 'filter', 'final', 'finally',
  'friend', 'from', 'fxgroup', 'get', 'goto', 'groupshared', 'highp', 'impl',
  'implements', 'import', 'inline', 'instanceof', 'interface', 'layout', 'lowp',
  'macro', 'macro_rules', 'match', 'mediump', 'meta', 'mod', 'module', 'move',
  'mut', 'mutable', 'namespace', 'new', 'nil', 'noinline', 'nointerpolation',
  'noperspective', 'null', 'nullptr', 'of', 'operator', 'package', 'packoffset',
  'partition', 'pass', 'patch', 'pixelfragment', 'precise', 'precision',
  'premerge', 'priv', 'protected', 'pub', 'public', 'readonly', 'ref',
  'regardless', 'register', 'reinterpret_cast', 'require', 'resource',
  'restrict', 'self', 'set', 'shared', 'sizeof', 'smooth', 'snorm', 'static',
  'static_assert', 'static_cast', 'std', 'subroutine', 'super', 'target',
  'template', 'this', 'thread_local', 'throw', 'trait', 'try', 'type',
  'typedef', 'typeid', 'typename', 'typeof', 'union', 'unless', 'unorm',
  'unsafe', 'unsized', 'use', 'using', 'varying', 'virtual', 'volatile', 'wgsl',
  'where', 'with', 'writeonly', 'yield',
]);

export function findWgsl16_2ReservedIdentifiers(source: string): readonly string[] {
  const codeOnly = source
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/\/\/[^\r\n]*/g, ' ');
  const identifiers = new Set(codeOnly.match(/[A-Za-z_][A-Za-z0-9_]*/g) ?? []);
  return Object.freeze(WGSL_16_2_RESERVED_IDENTIFIERS.filter(
    (identifier) => identifiers.has(identifier),
  ));
}

/**
 * Common full-f32 box/box DCD implementation shared by the four split passes.
 * The split is solely a WebGPU binding-budget concern: every pass replays the
 * same arithmetic, and no geometric value is quantized or stored in scratch.
 */
function wideExactBoxBoxCommonWgsl(
  plan: WideExactContactGenerationPlan,
  options: Required<WideExactBoxBoxDcdWgslOptions>,
): string {
  if (plan.pairManifoldSlots !== WIDE_EXACT_CONTACT_GENERATION_PAIR_MANIFOLD_SLOTS) {
    throw new Error('Exact box/box DCD currently requires the canonical eight logical slots.');
  }
  const descriptorBodyBRelativeWords = (
    plan.pool.descriptorBodyB.byteOffset - plan.pool.descriptorBodyA.byteOffset
  ) / U32_BYTES;
  if (!Number.isInteger(descriptorBodyBRelativeWords)) {
    throw new Error('Descriptor endpoint planes are not word aligned.');
  }
  return /* wgsl */`
const BODY_CAPACITY: u32 = ${plan.maxBodies}u;
const DESCRIPTOR_CAPACITY: u32 = ${plan.maxPersistentManifolds}u;
const CONTACT_ROW_CAPACITY: u32 = ${plan.contactRowCapacity}u;
const PAIR_MANIFOLD_SLOTS: u32 = ${plan.pairManifoldSlots}u;
const ACTIVE_DESCRIPTOR_START: u32 = ${WIDE_EXACT_ACTIVE_DESCRIPTOR_STREAM.descriptorStart}u;
const DESCRIPTOR_BODY_B_RELATIVE_WORDS: u32 = ${descriptorBodyBRelativeWords}u;
const DISPATCH_WORKGROUPS_X: u32 = ${plan.descriptorDispatch.workgroups[0]}u;
const CONTACT_SLOP: f32 = ${wgslFiniteF32(options.contactSlop, 'contactSlop')};
const COLLISION_MARGIN: f32 = ${wgslFiniteF32(options.collisionMargin, 'collisionMargin')};
const FRICTION_STATIC: f32 = ${wgslFiniteF32(options.frictionStatic, 'frictionStatic')};
const DECISION_ACTIVE: u32 = ${WIDE_EXACT_CONTACT_DECISION.activeBit}u;
const DECISION_DCD: u32 = ${WIDE_EXACT_CONTACT_DECISION.dcdBit}u;
const DECISION_BOX_BOX: u32 = ${WIDE_EXACT_CONTACT_DECISION.boxBoxBit}u;
const CONTACT_INFO_SPECULATIVE_BIT: u32 = ${CONTACT_INFO_SPECULATIVE_BIT}u;
const CONTACT_INFO_LOGICAL_SLOT_SHIFT: u32 = ${CONTACT_INFO_LOGICAL_SLOT_SHIFT}u;
const CONTACT_INFO_LOGICAL_SLOT_MASK: u32 = ${CONTACT_INFO_LOGICAL_SLOT_MASK}u;
const SHAPE_FRICTION_WORD_MASK: u32 = 0x3fffu;
const SHAPE_FRICTION_WORD_SCALE: f32 = 2.0 / 16383.0;
const SHAPE_TYPE_SHIFT: u32 = 14u;
const SHAPE_TYPE_MASK: u32 = 3u;
const SHAPE_TYPE_BOX: u32 = 0u;

struct U32Buffer { values: array<u32>, }
struct Vec2uBuffer { values: array<vec2u>, }
struct Vec4uBuffer { values: array<vec4u>, }
struct Vec4fBuffer { values: array<vec4f>, }

struct TangentBasis {
  t1: vec3f,
  t2: vec3f,
}

struct SegmentResult {
  p: vec3f,
  q: vec3f,
}

struct WideBoxBoxManifold {
  isActive: u32,
  bestFeature: i32,
  normal: vec3f,
  candidateCount: u32,
  pointWorld: array<vec3f, 8>,
  armAWorld: array<vec3f, 8>,
  armBWorld: array<vec3f, 8>,
  penetration: array<f32, 8>,
  featureKey: array<u32, 8>,
  tangentT1: vec3f,
  tangentAngle: f32,
}

fn descriptorOrdinal(workgroupId: vec3u, localId: vec3u) -> u32 {
  return (
    workgroupId.y * DISPATCH_WORKGROUPS_X + workgroupId.x
  ) * ${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}u + localId.x;
}

fn qrotExact(q: vec4f, v: vec3f) -> vec3f {
  let t = 2.0 * cross(q.xyz, v);
  return v + q.w * t + cross(q.xyz, t);
}

fn qconjExact(q: vec4f) -> vec4f {
  return vec4f(-q.xyz, q.w);
}

fn obbSupportExact(pos: vec3f, q: vec4f, half: vec3f, dir: vec3f) -> vec3f {
  let localDir = qrotExact(qconjExact(q), dir);
  let s = vec3f(
    select(-1.0, 1.0, localDir.x >= 0.0),
    select(-1.0, 1.0, localDir.y >= 0.0),
    select(-1.0, 1.0, localDir.z >= 0.0)
  );
  return pos + qrotExact(q, s * half);
}

fn canonicalTangentBasisExact(n: vec3f) -> TangentBasis {
  let refAxis = select(
    vec3f(0.0, 1.0, 0.0),
    vec3f(1.0, 0.0, 0.0),
    abs(n.y) > 0.999,
  );
  let t1Raw = cross(refAxis, n);
  let t1Len2 = dot(t1Raw, t1Raw);
  var t1 = vec3f(0.0, 0.0, 1.0);
  if (t1Len2 > 1e-12) { t1 = t1Raw * inverseSqrt(t1Len2); }
  let t2Raw = cross(n, t1);
  let t2Len2 = dot(t2Raw, t2Raw);
  var t2 = vec3f(1.0, 0.0, 0.0);
  if (t2Len2 > 1e-12) { t2 = t2Raw * inverseSqrt(t2Len2); }
  return TangentBasis(t1, t2);
}

fn tangentBasisFromPreferredT1Exact(n: vec3f, preferredT1: vec3f) -> TangentBasis {
  let projected = preferredT1 - n * dot(preferredT1, n);
  let projectedLen2 = dot(projected, projected);
  if (projectedLen2 <= 1e-12) { return canonicalTangentBasisExact(n); }
  let t1 = projected * inverseSqrt(projectedLen2);
  let t2Raw = cross(n, t1);
  let t2Len2 = dot(t2Raw, t2Raw);
  if (t2Len2 <= 1e-12) { return canonicalTangentBasisExact(n); }
  return TangentBasis(t1, t2Raw * inverseSqrt(t2Len2));
}

fn tangentBasisFromAngleExact(n: vec3f, theta: f32) -> TangentBasis {
  let canonical = canonicalTangentBasisExact(n);
  let c = cos(theta);
  let s = sin(theta);
  return tangentBasisFromPreferredT1Exact(n, canonical.t1 * c + canonical.t2 * s);
}

fn tangentBasisAngleFromT1Exact(n: vec3f, t1: vec3f) -> f32 {
  let canonical = canonicalTangentBasisExact(n);
  var theta = atan2(dot(t1, canonical.t2), dot(t1, canonical.t1));
  if (theta < 0.0) { theta += 6.283185307179586; }
  return theta;
}

fn shapeMetaWord(shapeMeta: f32) -> u32 { return bitcast<u32>(shapeMeta); }
fn shapeTypeExact(shapeMeta: f32) -> u32 {
  return (shapeMetaWord(shapeMeta) >> SHAPE_TYPE_SHIFT) & SHAPE_TYPE_MASK;
}
fn shapeFrictionExact(shapeMeta: f32) -> f32 {
  return f32(shapeMetaWord(shapeMeta) & SHAPE_FRICTION_WORD_MASK)
    * SHAPE_FRICTION_WORD_SCALE;
}
fn shapesCanCollideExact(a: vec4f, b: vec4f) -> bool {
  let aw = shapeMetaWord(a.x);
  let bw = shapeMetaWord(b.x);
  let groupA = (aw >> 16u) & 0xffu;
  let groupB = (bw >> 16u) & 0xffu;
  let maskA = (aw >> 24u) & 0xffu;
  let maskB = (bw >> 24u) & 0xffu;
  return (maskA & groupB) != 0u && (maskB & groupA) != 0u;
}

fn packFaceContactFeatureExact(
  referenceIsA: bool,
  referenceAxis: u32,
  incidentAxis: u32,
  ordinal: u32,
) -> u32 {
  let featureType = select(1u, 0u, referenceIsA);
  return (ordinal & 0x7u)
    | ((incidentAxis & 0x3u) << 3u)
    | ((referenceAxis & 0x3u) << 5u)
    | ((featureType & 0x3u) << 7u);
}

fn packEdgeContactFeatureExact(axisA: u32, axisB: u32) -> u32 {
  return ((axisB & 0x3u) << 3u) | ((axisA & 0x3u) << 5u) | (2u << 7u);
}

fn decodeContactBaseFeatureExact(encodedInfo: u32) -> u32 {
  let featureKey = encodedInfo & 0x1ffu;
  let featureType = (featureKey >> 7u) & 0x3u;
  let referenceAxis = (featureKey >> 5u) & 0x3u;
  let incidentAxis = (featureKey >> 3u) & 0x3u;
  if (featureType == 0u) { return referenceAxis; }
  if (featureType == 1u) { return 3u + referenceAxis; }
  return 6u + referenceAxis * 3u + incidentAxis;
}

fn contactInfoWithSlotExact(encodedInfo: u32, logicalSlot: u32) -> u32 {
  return (encodedInfo & ~CONTACT_INFO_LOGICAL_SLOT_MASK)
    | ((logicalSlot & 0x7u) << CONTACT_INFO_LOGICAL_SLOT_SHIFT);
}

fn packContactInfoExact(
  featureKey: u32,
  preserveWarmstart: u32,
  stick: u32,
  stickAnchorReuse: u32,
  warmstartReason: u32,
  logicalSlot: u32,
) -> u32 {
  return contactInfoWithSlotExact(
    (featureKey & 0x1ffu)
      | ((preserveWarmstart & 1u) << 16u)
      | ((stick & 1u) << 17u)
      | ((stickAnchorReuse & 1u) << 18u)
      | ((warmstartReason & 0x7u) << 21u),
    logicalSlot,
  );
}

fn signNonzeroExact(x: f32) -> f32 { return select(1.0, -1.0, x < 0.0); }

fn closestPointsSegmentsExact(
  p0: vec3f,
  p1: vec3f,
  q0: vec3f,
  q1: vec3f,
) -> SegmentResult {
  let u = p1 - p0;
  let v = q1 - q0;
  let w0 = p0 - q0;
  let a = dot(u, u);
  let b = dot(u, v);
  let c = dot(v, v);
  let d = dot(u, w0);
  let e = dot(v, w0);
  let D = a * c - b * b;
  let EPS = 1e-6;
  var sN: f32;
  var sD = D;
  var tN: f32;
  var tD = D;
  if (D < EPS) {
    sN = 0.0; sD = 1.0; tN = e; tD = c;
  } else {
    sN = b * e - c * d;
    tN = a * e - b * d;
    if (sN < 0.0) {
      sN = 0.0; tN = e; tD = c;
    } else if (sN > sD) {
      sN = sD; tN = e + b; tD = c;
    }
  }
  if (tN < 0.0) {
    tN = 0.0;
    if (-d < 0.0) { sN = 0.0; }
    else if (-d > a) { sN = sD; }
    else { sN = -d; sD = a; }
  } else if (tN > tD) {
    tN = tD;
    if (-d + b < 0.0) { sN = 0.0; }
    else if (-d + b > a) { sN = sD; }
    else { sN = -d + b; sD = a; }
  }
  let sc = select(0.0, sN / sD, abs(sN) > EPS);
  let tc = select(0.0, tN / tD, abs(tN) > EPS);
  return SegmentResult(p0 + sc * u, q0 + tc * v);
}

fn evaluatePreviousFeatureExact(
  feature: i32,
  d: vec3f,
  halfA: vec3f,
  halfB: vec3f,
  axA0: vec3f,
  axA1: vec3f,
  axA2: vec3f,
  axB0: vec3f,
  axB1: vec3f,
  axB2: vec3f,
  tA: vec3f,
  tB: vec3f,
  aR0: vec3f,
  aR1: vec3f,
  aR2: vec3f,
) -> vec4f {
  var pen = 1e30;
  var normal = vec3f(0.0);
  var valid = 0.0;
  if (feature == 0) {
    pen = halfA.x + (aR0.x * halfB.x + aR0.y * halfB.y + aR0.z * halfB.z) - abs(tA.x);
    normal = select(axA0, -axA0, tA.x < 0.0); valid = 1.0;
  } else if (feature == 1) {
    pen = halfA.y + (aR1.x * halfB.x + aR1.y * halfB.y + aR1.z * halfB.z) - abs(tA.y);
    normal = select(axA1, -axA1, tA.y < 0.0); valid = 1.0;
  } else if (feature == 2) {
    pen = halfA.z + (aR2.x * halfB.x + aR2.y * halfB.y + aR2.z * halfB.z) - abs(tA.z);
    normal = select(axA2, -axA2, tA.z < 0.0); valid = 1.0;
  } else if (feature == 3) {
    pen = (aR0.x * halfA.x + aR1.x * halfA.y + aR2.x * halfA.z) + halfB.x - abs(tB.x);
    normal = select(axB0, -axB0, tB.x < 0.0); valid = 1.0;
  } else if (feature == 4) {
    pen = (aR0.y * halfA.x + aR1.y * halfA.y + aR2.y * halfA.z) + halfB.y - abs(tB.y);
    normal = select(axB1, -axB1, tB.y < 0.0); valid = 1.0;
  } else if (feature == 5) {
    pen = (aR0.z * halfA.x + aR1.z * halfA.y + aR2.z * halfA.z) + halfB.z - abs(tB.z);
    normal = select(axB2, -axB2, tB.z < 0.0); valid = 1.0;
  } else if (feature >= 6 && feature < 15) {
    let code = u32(feature - 6);
    let k = code / 3u;
    let l = code - k * 3u;
    let aAxis = select(select(axA2, axA1, k == 1u), axA0, k == 0u);
    let bAxis = select(select(axB2, axB1, l == 1u), axB0, l == 0u);
    let axis = cross(aAxis, bAxis);
    let len2 = dot(axis, axis);
    if (len2 > 1e-6) {
      let invLen = inverseSqrt(len2);
      let n = axis * invLen;
      var ra = 0.0;
      var rb = 0.0;
      if (k == 0u && l == 0u) { ra = halfA.y * aR2.x + halfA.z * aR1.x; rb = halfB.y * aR0.z + halfB.z * aR0.y; }
      else if (k == 0u && l == 1u) { ra = halfA.y * aR2.y + halfA.z * aR1.y; rb = halfB.x * aR0.z + halfB.z * aR0.x; }
      else if (k == 0u && l == 2u) { ra = halfA.y * aR2.z + halfA.z * aR1.z; rb = halfB.x * aR0.y + halfB.y * aR0.x; }
      else if (k == 1u && l == 0u) { ra = halfA.x * aR2.x + halfA.z * aR0.x; rb = halfB.y * aR1.z + halfB.z * aR1.y; }
      else if (k == 1u && l == 1u) { ra = halfA.x * aR2.y + halfA.z * aR0.y; rb = halfB.x * aR1.z + halfB.z * aR1.x; }
      else if (k == 1u && l == 2u) { ra = halfA.x * aR2.z + halfA.z * aR0.z; rb = halfB.x * aR1.y + halfB.y * aR1.x; }
      else if (k == 2u && l == 0u) { ra = halfA.x * aR1.x + halfA.y * aR0.x; rb = halfB.y * aR2.z + halfB.z * aR2.y; }
      else if (k == 2u && l == 1u) { ra = halfA.x * aR1.y + halfA.y * aR0.y; rb = halfB.x * aR2.z + halfB.z * aR2.x; }
      else { ra = halfA.x * aR1.z + halfA.y * aR0.z; rb = halfB.x * aR2.y + halfB.y * aR2.x; }
      pen = (ra + rb) * invLen - abs(dot(d, n));
      normal = select(n, -n, dot(d, n) < 0.0);
      valid = 1.0;
    }
  }
  return vec4f(normal, pen);
}

fn buildWideBoxBoxManifold(
  posA: vec3f,
  qA: vec4f,
  halfA: vec3f,
  posB: vec3f,
  qB: vec4f,
  halfB: vec3f,
  wasActive: bool,
  previousFeature: i32,
  previousNormal: vec3f,
  forcedFeature: i32,
) -> WideBoxBoxManifold {
  var result: WideBoxBoxManifold;
  result.isActive = 0u;
  result.bestFeature = -1;
  result.normal = vec3f(0.0);
  result.candidateCount = 0u;
  result.tangentT1 = vec3f(0.0);
  result.tangentAngle = 0.0;

  let e0 = vec3f(1.0, 0.0, 0.0);
  let e1 = vec3f(0.0, 1.0, 0.0);
  let e2 = vec3f(0.0, 0.0, 1.0);
  let axA0 = qrotExact(qA, e0);
  let axA1 = qrotExact(qA, e1);
  let axA2 = qrotExact(qA, e2);
  let axB0 = qrotExact(qB, e0);
  let axB1 = qrotExact(qB, e1);
  let axB2 = qrotExact(qB, e2);
  let d = posB - posA;
  let eps = 1e-6;
  let R00 = dot(axA0, axB0); let R01 = dot(axA0, axB1); let R02 = dot(axA0, axB2);
  let R10 = dot(axA1, axB0); let R11 = dot(axA1, axB1); let R12 = dot(axA1, axB2);
  let R20 = dot(axA2, axB0); let R21 = dot(axA2, axB1); let R22 = dot(axA2, axB2);
  let aR0 = vec3f(abs(R00) + eps, abs(R01) + eps, abs(R02) + eps);
  let aR1 = vec3f(abs(R10) + eps, abs(R11) + eps, abs(R12) + eps);
  let aR2 = vec3f(abs(R20) + eps, abs(R21) + eps, abs(R22) + eps);
  let tA = vec3f(dot(d, axA0), dot(d, axA1), dot(d, axA2));
  let tB = vec3f(dot(d, axB0), dot(d, axB1), dot(d, axB2));
  let hysteresis = 0.35 * CONTACT_SLOP;
  let sepSlop = CONTACT_SLOP + select(0.0, hysteresis, wasActive);
  var minPen = 1e30;
  var faceMinPen = 1e30;
  var faceBestNormal = vec3f(0.0);
  var faceBestFeature: i32 = -1;
  var bestNormal = vec3f(0.0);
  var bestFeature: i32 = -1;
  var separated = false;

  {
    let ra = halfA.x;
    let rb = aR0.x * halfB.x + aR0.y * halfB.y + aR0.z * halfB.z;
    let pen = ra + rb - abs(tA.x);
    if (pen < -sepSlop) { separated = true; }
    if (!separated && pen < faceMinPen) { faceMinPen = pen; faceBestNormal = select(axA0, -axA0, tA.x < 0.0); faceBestFeature = 0; }
    if (!separated && pen < minPen) { minPen = pen; bestNormal = select(axA0, -axA0, tA.x < 0.0); bestFeature = 0; }
  }
  if (!separated) {
    let ra = halfA.y;
    let rb = aR1.x * halfB.x + aR1.y * halfB.y + aR1.z * halfB.z;
    let pen = ra + rb - abs(tA.y);
    if (pen < -sepSlop) { separated = true; }
    if (!separated && pen < faceMinPen) { faceMinPen = pen; faceBestNormal = select(axA1, -axA1, tA.y < 0.0); faceBestFeature = 1; }
    if (!separated && pen < minPen) { minPen = pen; bestNormal = select(axA1, -axA1, tA.y < 0.0); bestFeature = 1; }
  }
  if (!separated) {
    let ra = halfA.z;
    let rb = aR2.x * halfB.x + aR2.y * halfB.y + aR2.z * halfB.z;
    let pen = ra + rb - abs(tA.z);
    if (pen < -sepSlop) { separated = true; }
    if (!separated && pen < faceMinPen) { faceMinPen = pen; faceBestNormal = select(axA2, -axA2, tA.z < 0.0); faceBestFeature = 2; }
    if (!separated && pen < minPen) { minPen = pen; bestNormal = select(axA2, -axA2, tA.z < 0.0); bestFeature = 2; }
  }
  if (!separated) {
    let ra = aR0.x * halfA.x + aR1.x * halfA.y + aR2.x * halfA.z;
    let pen = ra + halfB.x - abs(tB.x);
    if (pen < -sepSlop) { separated = true; }
    if (!separated && pen < faceMinPen) { faceMinPen = pen; faceBestNormal = select(axB0, -axB0, tB.x < 0.0); faceBestFeature = 3; }
    if (!separated && pen < minPen) { minPen = pen; bestNormal = select(axB0, -axB0, tB.x < 0.0); bestFeature = 3; }
  }
  if (!separated) {
    let ra = aR0.y * halfA.x + aR1.y * halfA.y + aR2.y * halfA.z;
    let pen = ra + halfB.y - abs(tB.y);
    if (pen < -sepSlop) { separated = true; }
    if (!separated && pen < faceMinPen) { faceMinPen = pen; faceBestNormal = select(axB1, -axB1, tB.y < 0.0); faceBestFeature = 4; }
    if (!separated && pen < minPen) { minPen = pen; bestNormal = select(axB1, -axB1, tB.y < 0.0); bestFeature = 4; }
  }
  if (!separated) {
    let ra = aR0.z * halfA.x + aR1.z * halfA.y + aR2.z * halfA.z;
    let pen = ra + halfB.z - abs(tB.z);
    if (pen < -sepSlop) { separated = true; }
    if (!separated && pen < faceMinPen) { faceMinPen = pen; faceBestNormal = select(axB2, -axB2, tB.z < 0.0); faceBestFeature = 5; }
    if (!separated && pen < minPen) { minPen = pen; bestNormal = select(axB2, -axB2, tB.z < 0.0); bestFeature = 5; }
  }

  if (!separated) {
    let axis = cross(axA0, axB0); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.y * aR2.x + halfA.z * aR1.x; let rb = halfB.y * aR0.z + halfB.z * aR0.y; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 6; } }
  }
  if (!separated) {
    let axis = cross(axA0, axB1); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.y * aR2.y + halfA.z * aR1.y; let rb = halfB.x * aR0.z + halfB.z * aR0.x; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 7; } }
  }
  if (!separated) {
    let axis = cross(axA0, axB2); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.y * aR2.z + halfA.z * aR1.z; let rb = halfB.x * aR0.y + halfB.y * aR0.x; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 8; } }
  }
  if (!separated) {
    let axis = cross(axA1, axB0); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.x * aR2.x + halfA.z * aR0.x; let rb = halfB.y * aR1.z + halfB.z * aR1.y; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 9; } }
  }
  if (!separated) {
    let axis = cross(axA1, axB1); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.x * aR2.y + halfA.z * aR0.y; let rb = halfB.x * aR1.z + halfB.z * aR1.x; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 10; } }
  }
  if (!separated) {
    let axis = cross(axA1, axB2); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.x * aR2.z + halfA.z * aR0.z; let rb = halfB.x * aR1.y + halfB.y * aR1.x; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 11; } }
  }
  if (!separated) {
    let axis = cross(axA2, axB0); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.x * aR1.x + halfA.y * aR0.x; let rb = halfB.y * aR2.z + halfB.z * aR2.y; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 12; } }
  }
  if (!separated) {
    let axis = cross(axA2, axB1); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.x * aR1.y + halfA.y * aR0.y; let rb = halfB.x * aR2.z + halfB.z * aR2.x; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 13; } }
  }
  if (!separated) {
    let axis = cross(axA2, axB2); let len2 = dot(axis, axis);
    if (len2 > 1e-6) { let invLen = inverseSqrt(len2); let n = axis * invLen; let ra = halfA.x * aR1.z + halfA.y * aR0.z; let rb = halfB.x * aR2.y + halfB.y * aR2.x; let pen = (ra + rb) * invLen - abs(dot(d, n)); if (pen < -sepSlop) { separated = true; } if (!separated && pen < minPen) { minPen = pen; bestNormal = select(n, -n, dot(d, n) < 0.0); bestFeature = 14; } }
  }

  if (!separated && wasActive && previousFeature >= 0) {
    let previous = evaluatePreviousFeatureExact(
      previousFeature, d, halfA, halfB,
      axA0, axA1, axA2, axB0, axB1, axB2,
      tA, tB, aR0, aR1, aR2,
    );
    let previousValid = dot(previous.xyz, previous.xyz) > 0.5;
    let previousPen = previous.w;
    let previousNormalEval = previous.xyz;
    if (
      previousValid
      && previousPen > -sepSlop
      && previousPen <= minPen + 0.5 * CONTACT_SLOP
      && dot(previousNormal, previousNormalEval) > 0.85
    ) {
      minPen = previousPen;
      bestNormal = previousNormalEval;
      bestFeature = previousFeature;
    }
  }

  // Later split passes carry the already-selected base feature in each exact
  // decision. Replaying that feature avoids depending on rows retired by the
  // intervening all-or-rollback lifecycle dispatch; classification is the
  // only pass that applies the historical-normal gate.
  if (!separated && forcedFeature >= 0 && forcedFeature != bestFeature) {
    let forced = evaluatePreviousFeatureExact(
      forcedFeature, d, halfA, halfB,
      axA0, axA1, axA2, axB0, axB1, axB2,
      tA, tB, aR0, aR1, aR2,
    );
    if (dot(forced.xyz, forced.xyz) > 0.5 && forced.w > -sepSlop) {
      minPen = forced.w;
      bestNormal = forced.xyz;
      bestFeature = forcedFeature;
    }
  }

  if (!separated && bestFeature >= 6 && faceBestFeature >= 0) {
    let edgeClearlyBetter = minPen < faceMinPen * 0.95 - 0.01;
    if (!edgeClearlyBetter) {
      minPen = faceMinPen;
      bestNormal = faceBestNormal;
      bestFeature = faceBestFeature;
    }
  }
  if (separated || !(minPen > -sepSlop)) { return result; }

  let storedPenetration = max(minPen, 0.0);
  var candidatePointWorld: array<vec3f, 8>;
  var candidateRAWorld: array<vec3f, 8>;
  var candidateRBWorld: array<vec3f, 8>;
  var candidatePenetration: array<f32, 8>;
  var candidateContactKey: array<u32, 8>;
  var candidateCount = 0u;
  var manifoldPreferredT1 = vec3f(0.0);
  var refIsA = false;
  var refFeature = 0u;
  var incFeature = 0u;

  if (bestFeature >= 6) {
    let code = u32(bestFeature - 6);
    let k = code / 3u;
    let l = code - k * 3u;
    var uA = vec3f(0.0); var sA1 = vec3f(0.0); var sA2 = vec3f(0.0);
    var huA = 0.0; var hA1 = 0.0; var hA2 = 0.0;
    if (k == 0u) { uA = axA0; sA1 = axA1; sA2 = axA2; huA = halfA.x; hA1 = halfA.y; hA2 = halfA.z; }
    else if (k == 1u) { uA = axA1; sA1 = axA0; sA2 = axA2; huA = halfA.y; hA1 = halfA.x; hA2 = halfA.z; }
    else { uA = axA2; sA1 = axA0; sA2 = axA1; huA = halfA.z; hA1 = halfA.x; hA2 = halfA.y; }
    let cA = posA
      + sA1 * (signNonzeroExact(dot(sA1, bestNormal)) * hA1)
      + sA2 * (signNonzeroExact(dot(sA2, bestNormal)) * hA2);
    let pA0 = cA - uA * huA; let pA1 = cA + uA * huA;
    var uB = vec3f(0.0); var sB1 = vec3f(0.0); var sB2 = vec3f(0.0);
    var huB = 0.0; var hB1 = 0.0; var hB2 = 0.0;
    if (l == 0u) { uB = axB0; sB1 = axB1; sB2 = axB2; huB = halfB.x; hB1 = halfB.y; hB2 = halfB.z; }
    else if (l == 1u) { uB = axB1; sB1 = axB0; sB2 = axB2; huB = halfB.y; hB1 = halfB.x; hB2 = halfB.z; }
    else { uB = axB2; sB1 = axB0; sB2 = axB1; huB = halfB.z; hB1 = halfB.x; hB2 = halfB.y; }
    let cB = posB
      + sB1 * (signNonzeroExact(-dot(sB1, bestNormal)) * hB1)
      + sB2 * (signNonzeroExact(-dot(sB2, bestNormal)) * hB2);
    let segment = closestPointsSegmentsExact(cA - uA * huA, cA + uA * huA, cB - uB * huB, cB + uB * huB);
    candidatePointWorld[0] = 0.5 * (segment.p + segment.q);
    candidateRAWorld[0] = segment.p - posA;
    candidateRBWorld[0] = segment.q - posB;
    candidatePenetration[0] = storedPenetration;
    candidateContactKey[0] = packEdgeContactFeatureExact(k, l);
    candidateCount = 1u;
    manifoldPreferredT1 = uA;
  } else {
    var refPos = posA; var refHalf = halfA; var refX = axA0; var refY = axA1; var refZ = axA2;
    var incPos = posB; var incHalf = halfB; var incX = axB0; var incY = axB1; var incZ = axB2;
    if (bestFeature < 3) { refFeature = u32(bestFeature); }
    else {
      refPos = posB; refHalf = halfB; refX = axB0; refY = axB1; refZ = axB2;
      incPos = posA; incHalf = halfA; incX = axA0; incY = axA1; incZ = axA2;
      refFeature = u32(bestFeature - 3);
    }
    var refAxis = refX; var refU = refY; var refV = refZ;
    var refH = refHalf.x; var refHU = refHalf.y; var refHV = refHalf.z;
    if (refFeature == 1u) { refAxis = refY; refU = refX; refV = refZ; refH = refHalf.y; refHU = refHalf.x; refHV = refHalf.z; }
    else if (refFeature == 2u) { refAxis = refZ; refU = refX; refV = refY; refH = refHalf.z; refHU = refHalf.x; refHV = refHalf.y; }
    refIsA = bestFeature < 3;
    let nRef = select(-bestNormal, bestNormal, refIsA);
    let refCenter = refPos + refAxis * (signNonzeroExact(dot(refAxis, nRef)) * refH);
    manifoldPreferredT1 = refU;
    let ad0 = abs(dot(incX, nRef)); let ad1 = abs(dot(incY, nRef)); let ad2 = abs(dot(incZ, nRef));
    if (ad1 > ad0 && ad1 >= ad2) { incFeature = 1u; }
    else if (ad2 > ad0 && ad2 > ad1) { incFeature = 2u; }
    var incAxis = incX; var incU = incY; var incV = incZ;
    var incH = incHalf.x; var incHU = incHalf.y; var incHV = incHalf.z;
    if (incFeature == 1u) { incAxis = incY; incU = incX; incV = incZ; incH = incHalf.y; incHU = incHalf.x; incHV = incHalf.z; }
    else if (incFeature == 2u) { incAxis = incZ; incU = incX; incV = incY; incH = incHalf.z; incHU = incHalf.x; incHV = incHalf.y; }
    let incSign = select(1.0, -1.0, dot(incAxis, nRef) > 0.0);
    let incCenter = incPos + incAxis * (incSign * incH);
    var polyIn: array<vec3f, 8>; var polyOut: array<vec3f, 8>;
    polyIn[0] = incCenter + incU * incHU + incV * incHV;
    polyIn[1] = incCenter - incU * incHU + incV * incHV;
    polyIn[2] = incCenter - incU * incHU - incV * incHV;
    polyIn[3] = incCenter + incU * incHU - incV * incHV;
    var polyCount = 4u;
    for (var planeIndex = 0u; planeIndex < 4u; planeIndex += 1u) {
      if (polyCount == 0u) { break; }
      var planeN = refU; var planeD = dot(refU, refCenter) + refHU;
      if (planeIndex == 1u) { planeN = -refU; planeD = dot(-refU, refCenter) + refHU; }
      else if (planeIndex == 2u) { planeN = refV; planeD = dot(refV, refCenter) + refHV; }
      else if (planeIndex == 3u) { planeN = -refV; planeD = dot(-refV, refCenter) + refHV; }
      var outCount = 0u;
      for (var vertex = 0u; vertex < polyCount; vertex += 1u) {
        let a = polyIn[vertex]; let b = polyIn[(vertex + 1u) % polyCount];
        let da = dot(planeN, a) - planeD; let db = dot(planeN, b) - planeD;
        let inA = da <= 1e-5; let inB = db <= 1e-5;
        if (inA && inB) { if (outCount < 8u) { polyOut[outCount] = b; outCount += 1u; } }
        else if (inA && !inB) {
          let denom = da - db;
          if (abs(denom) > 1e-6 && outCount < 8u) { polyOut[outCount] = a + (b - a) * clamp(da / denom, 0.0, 1.0); outCount += 1u; }
        } else if (!inA && inB) {
          let denom = da - db;
          if (abs(denom) > 1e-6 && outCount < 8u) { polyOut[outCount] = a + (b - a) * clamp(da / denom, 0.0, 1.0); outCount += 1u; }
          if (outCount < 8u) { polyOut[outCount] = b; outCount += 1u; }
        }
      }
      polyCount = outCount;
      for (var vertex = 0u; vertex < polyCount; vertex += 1u) { polyIn[vertex] = polyOut[vertex]; }
    }
    for (var vertex = 0u; vertex < polyCount; vertex += 1u) {
      let p = polyIn[vertex]; let dist = dot(nRef, p - refCenter);
      if (dist > CONTACT_SLOP) { continue; }
      let pRef = p - nRef * dist;
      let xA = select(p, pRef, refIsA); let xB = select(pRef, p, refIsA);
      let cp = 0.5 * (xA + xB);
      var duplicate = false;
      for (var candidate = 0u; candidate < candidateCount; candidate += 1u) {
        let delta = cp - candidatePointWorld[candidate];
        if (dot(delta, delta) < 1e-6) { duplicate = true; }
      }
      if (duplicate) { continue; }
      if (candidateCount < 8u) {
        candidatePointWorld[candidateCount] = cp;
        candidateRAWorld[candidateCount] = xA - posA;
        candidateRBWorld[candidateCount] = xB - posB;
        candidatePenetration[candidateCount] = max(-dist, 0.0);
        candidateContactKey[candidateCount] = packFaceContactFeatureExact(refIsA, refFeature, incFeature, min(vertex, 7u));
        candidateCount += 1u;
      }
    }
  }

  if (candidateCount == 0u) {
    let pA = obbSupportExact(posA, qA, halfA, bestNormal);
    let pB = obbSupportExact(posB, qB, halfB, -bestNormal);
    candidatePointWorld[0] = 0.5 * (pA + pB);
    candidateRAWorld[0] = pA - posA;
    candidateRBWorld[0] = pB - posB;
    candidatePenetration[0] = storedPenetration;
    candidateContactKey[0] = packFaceContactFeatureExact(refIsA, refFeature, incFeature, 0u);
    candidateCount = 1u;
  }

  if (candidateCount > 1u) {
    var compactPoint: array<vec3f, 8>; var compactRA: array<vec3f, 8>; var compactRB: array<vec3f, 8>;
    var compactPen: array<f32, 8>; var compactKey: array<u32, 8>; var compactCount = 0u;
    for (var candidate = 0u; candidate < candidateCount; candidate += 1u) {
      var duplicateIndex = 0xffffffffu;
      for (var kept = 0u; kept < compactCount; kept += 1u) {
        let delta = candidatePointWorld[candidate] - compactPoint[kept];
        if (dot(delta, delta) < 1e-6) { duplicateIndex = kept; break; }
      }
      if (duplicateIndex != 0xffffffffu) { continue; }
      compactPoint[compactCount] = candidatePointWorld[candidate];
      compactRA[compactCount] = candidateRAWorld[candidate]; compactRB[compactCount] = candidateRBWorld[candidate];
      compactPen[compactCount] = candidatePenetration[candidate]; compactKey[compactCount] = candidateContactKey[candidate];
      compactCount += 1u;
    }
    for (var candidate = 0u; candidate < compactCount; candidate += 1u) {
      candidatePointWorld[candidate] = compactPoint[candidate]; candidateRAWorld[candidate] = compactRA[candidate]; candidateRBWorld[candidate] = compactRB[candidate];
      candidatePenetration[candidate] = compactPen[candidate]; candidateContactKey[candidate] = compactKey[candidate];
    }
    candidateCount = compactCount;
  }

  if (candidateCount > 1u) {
    for (var passIndex = 0u; passIndex < 8u; passIndex += 1u) {
      if (passIndex + 1u >= candidateCount) { break; }
      let last = candidateCount - 1u - passIndex;
      for (var index = 0u; index < last; index += 1u) {
        let next = index + 1u;
        let leftKey = candidateContactKey[index]; let rightKey = candidateContactKey[next];
        var swap = leftKey > rightKey;
        if (!swap && leftKey == rightKey) { swap = candidatePenetration[index] < candidatePenetration[next]; }
        if (!swap) { continue; }
        let point = candidatePointWorld[index]; candidatePointWorld[index] = candidatePointWorld[next]; candidatePointWorld[next] = point;
        let armA = candidateRAWorld[index]; candidateRAWorld[index] = candidateRAWorld[next]; candidateRAWorld[next] = armA;
        let armB = candidateRBWorld[index]; candidateRBWorld[index] = candidateRBWorld[next]; candidateRBWorld[next] = armB;
        let penetration = candidatePenetration[index]; candidatePenetration[index] = candidatePenetration[next]; candidatePenetration[next] = penetration;
        let key = candidateContactKey[index]; candidateContactKey[index] = candidateContactKey[next]; candidateContactKey[next] = key;
      }
    }
  }

  let tangent = tangentBasisFromPreferredT1Exact(bestNormal, manifoldPreferredT1);
  result.isActive = 1u;
  result.bestFeature = bestFeature;
  result.normal = bestNormal;
  result.candidateCount = candidateCount;
  result.tangentT1 = tangent.t1;
  result.tangentAngle = tangentBasisAngleFromT1Exact(bestNormal, tangent.t1);
  for (var candidate = 0u; candidate < candidateCount; candidate += 1u) {
    result.pointWorld[candidate] = candidatePointWorld[candidate];
    result.armAWorld[candidate] = candidateRAWorld[candidate];
    result.armBWorld[candidate] = candidateRBWorld[candidate];
    result.penetration[candidate] = candidatePenetration[candidate];
    result.featureKey[candidate] = candidateContactKey[candidate];
  }
  return result;
}
`;
}

/**
 * Raw WGSL box/box DCD path for the exact-wide storage ABI.  It is deliberately
 * independent of the production TSL graph until >65K end-to-end stepping is
 * enabled.  The dispatch order is part of the contract: state and cache read
 * the previous planes before the geometry pass overwrites them.
 */
export function generateWideExactBoxBoxDcdWgsl(
  plan: WideExactContactGenerationPlan,
  requested: WideExactBoxBoxDcdWgslOptions = {},
): WideExactBoxBoxDcdWgslBundle {
  const options: Required<WideExactBoxBoxDcdWgslOptions> = {
    contactSlop: requested.contactSlop ?? 0.005,
    collisionMargin: requested.collisionMargin ?? AVBD_COLLISION_MARGIN,
    frictionStatic: requested.frictionStatic ?? AVBD_FRICTION_STATIC,
  };
  const common = wideExactBoxBoxCommonWgsl(plan, options);
  const {
    transitionState,
    materializeCache,
  } = wideExactBoxBoxStateAndCacheWgsl(common);

  const classify = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read> outputMasks: U32Buffer;
@group(0) @binding(5) var<storage, read> contactMetaPlane: Vec4uBuffer;
@group(0) @binding(6) var<storage, read> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(8) var<storage, read> contactArmBPlane: Vec4fBuffer;
@group(0) @binding(9) var<storage, read_write> decisions: Vec2uBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    decisions.values[logicalBase + slot] = vec2u(0u);
  }

  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  if (positionMassA.w + positionMassB.w == 0.0) { return; }
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  if (
    shapeTypeExact(shapeA.x) != SHAPE_TYPE_BOX
    || shapeTypeExact(shapeB.x) != SHAPE_TYPE_BOX
    || !shapesCanCollideExact(shapeA, shapeB)
  ) { return; }

  let previousMask = outputMasks.values[descriptor] & 0xffu;
  var oldActive: array<u32, 8>;
  var oldMetadata: array<vec4u, 8>;
  var oldArmA: array<vec4f, 8>;
  var oldArmB: array<vec4f, 8>;
  var previousNormal = vec3f(0.0);
  var previousFeature: i32 = -1;
  var wasActive = false;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    oldActive[slot] = 0u;
    let logicalSlot = logicalBase + slot;
    let row = slotRows.values[logicalSlot];
    if ((previousMask & (1u << slot)) == 0u || row >= CONTACT_ROW_CAPACITY) { continue; }
    let contactWords = contactMetaPlane.values[row];
    if (
      contactWords.x != bodyA
      || contactWords.y != bodyB
      || contactWords.z != descriptor + 1u
      || (contactWords.w & CONTACT_INFO_SPECULATIVE_BIT) != 0u
    ) { continue; }
    oldActive[slot] = 1u;
    oldMetadata[slot] = contactWords;
    oldArmA[slot] = contactArmAPlane.values[row];
    oldArmB[slot] = contactArmBPlane.values[row];
    if (!wasActive) {
      wasActive = true;
      previousFeature = i32(decodeContactBaseFeatureExact(contactWords.w));
      previousNormal = contactNormalPlane.values[row].xyz;
    }
  }

  let manifold = buildWideBoxBoxManifold(
    positionMassA.xyz,
    quaternionA,
    shapeA.yzw,
    positionMassB.xyz,
    quaternionB,
    shapeB.yzw,
    wasActive,
    previousFeature,
    previousNormal,
    -1,
  );
  if (manifold.isActive == 0u) { return; }

  let maxDrift = max(3.5 * CONTACT_SLOP, 0.016);
  let maxDrift2 = maxDrift * maxDrift;
  var oldMatched: array<u32, 8>;
  var candidateOldSlot: array<u32, 8>;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) { oldMatched[slot] = 0u; }
  for (var candidate = 0u; candidate < manifold.candidateCount; candidate += 1u) {
    var bestSlot = 0xffffffffu;
    var bestDistance2 = 1e30;
    for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
      if (oldActive[slot] == 0u || oldMatched[slot] != 0u) { continue; }
      if ((oldMetadata[slot].w & 0x1ffu) != manifold.featureKey[candidate]) { continue; }
      let oldWorldArmA = qrotExact(quaternionA, oldArmA[slot].xyz);
      let oldWorldArmB = qrotExact(quaternionB, oldArmB[slot].xyz);
      let oldPoint = 0.5 * (
        (positionMassA.xyz + oldWorldArmA)
          + (positionMassB.xyz + oldWorldArmB)
      );
      let delta = manifold.pointWorld[candidate] - oldPoint;
      let distance2 = dot(delta, delta);
      if (distance2 <= maxDrift2 && distance2 < bestDistance2) {
        bestDistance2 = distance2;
        bestSlot = slot;
      }
    }
    candidateOldSlot[candidate] = bestSlot;
    if (bestSlot != 0xffffffffu) { oldMatched[bestSlot] = 1u; }
  }

  var chosenMask = 0u;
  for (var candidate = 0u; candidate < manifold.candidateCount; candidate += 1u) {
    let freshPointA = positionMassA.xyz + manifold.armAWorld[candidate];
    let freshPointB = positionMassB.xyz + manifold.armBWorld[candidate];
    if (-dot(freshPointB - freshPointA, manifold.normal) < 0.0) { continue; }
    let oldSlot = candidateOldSlot[candidate];
    var chosenSlot = oldSlot;
    if (chosenSlot == 0xffffffffu) {
      for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
        let slotBit = 1u << slot;
        if (oldMatched[slot] == 0u && (chosenMask & slotBit) == 0u) {
          chosenSlot = slot;
          break;
        }
      }
    }
    if (chosenSlot == 0xffffffffu) { continue; }
    chosenMask |= 1u << chosenSlot;
    var preserve = 0u;
    var carryStick = 0u;
    var reuseAnchor = 0u;
    var warmstartReason = 0u;
    if (oldSlot != 0xffffffffu) {
      preserve = 1u;
      carryStick = (oldMetadata[oldSlot].w >> 17u) & 1u;
      reuseAnchor = carryStick;
      warmstartReason = 1u;
    }
    let encodedInfo = packContactInfoExact(
      manifold.featureKey[candidate],
      preserve,
      carryStick,
      reuseAnchor,
      warmstartReason,
      chosenSlot,
    );
    decisions.values[logicalBase + chosenSlot] = vec2u(
      encodedInfo,
      DECISION_ACTIVE | DECISION_DCD | DECISION_BOX_BOX,
    );
  }
}
`;

  const materializeGeometry = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(5) var<storage, read_write> contactMetaPlane: Vec4uBuffer;
@group(0) @binding(6) var<storage, read_write> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read_write> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(8) var<storage, read_write> contactArmBPlane: Vec4fBuffer;
@group(0) @binding(9) var<storage, read_write> contactConstraintC0Plane: Vec4fBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  var forcedFeature: i32 = -1;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & (DECISION_ACTIVE | DECISION_BOX_BOX)) == (DECISION_ACTIVE | DECISION_BOX_BOX)) {
      forcedFeature = i32(decodeContactBaseFeatureExact(decision.x));
      break;
    }
  }
  if (forcedFeature < 0) { return; }
  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let manifold = buildWideBoxBoxManifold(
    positionMassA.xyz, quaternionA, shapeA.yzw,
    positionMassB.xyz, quaternionB, shapeB.yzw,
    false, -1, vec3f(0.0), forcedFeature,
  );
  if (manifold.isActive == 0u) { return; }

  var preferredTangent = vec3f(0.0);
  var preferredCount = 0u;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & DECISION_ACTIVE) == 0u || ((decision.x >> 16u) & 1u) == 0u) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let oldNormal = contactNormalPlane.values[row].xyz;
    let oldAngle = contactArmAPlane.values[row].w;
    preferredTangent += tangentBasisFromAngleExact(oldNormal, oldAngle).t1;
    preferredCount += 1u;
  }
  var writeTangent = tangentBasisFromPreferredT1Exact(manifold.normal, manifold.tangentT1);
  if (preferredCount > 0u && dot(preferredTangent, writeTangent.t1) < 0.0) {
    writeTangent = TangentBasis(-writeTangent.t1, -writeTangent.t2);
  }
  let tangentAngle = tangentBasisAngleFromT1Exact(manifold.normal, writeTangent.t1);

  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if ((decision.y & DECISION_ACTIVE) == 0u) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let featureKey = decision.x & 0x1ffu;
    var candidateIndex = 0xffffffffu;
    for (var candidate = 0u; candidate < manifold.candidateCount; candidate += 1u) {
      if (manifold.featureKey[candidate] == featureKey) {
        candidateIndex = candidate;
        break;
      }
    }
    if (candidateIndex == 0xffffffffu) { continue; }
    var worldArmA = manifold.armAWorld[candidateIndex];
    var worldArmB = manifold.armBWorld[candidateIndex];
    let reuseAnchor = ((decision.x >> 18u) & 1u) != 0u;
    if (reuseAnchor) {
      worldArmA = qrotExact(quaternionA, contactArmAPlane.values[row].xyz);
      worldArmB = qrotExact(quaternionB, contactArmBPlane.values[row].xyz);
    }
    let finalPointA = positionMassA.xyz + worldArmA;
    let finalPointB = positionMassB.xyz + worldArmB;
    let finalRawPenetration = -dot(finalPointB - finalPointA, manifold.normal);
    if (!reuseAnchor && finalRawPenetration < 0.0) { continue; }
    let cachedVector = finalPointA - finalPointB;
    let storedArmA = qrotExact(qconjExact(quaternionA), worldArmA);
    let storedArmB = qrotExact(qconjExact(quaternionB), worldArmB);
    contactMetaPlane.values[row] = vec4u(bodyA, bodyB, descriptor + 1u, decision.x);
    contactNormalPlane.values[row] = vec4f(manifold.normal, max(finalRawPenetration, 0.0));
    contactArmAPlane.values[row] = vec4f(storedArmA, tangentAngle);
    contactArmBPlane.values[row] = vec4f(storedArmB, 0.0);
    contactConstraintC0Plane.values[row] = vec4f(
      -dot(cachedVector, manifold.normal) + COLLISION_MARGIN,
      -dot(cachedVector, writeTangent.t1),
      -dot(cachedVector, writeTangent.t2),
      0.0,
    );
  }
}
`;

  return Object.freeze({
    classify,
    transitionState,
    materializeCache,
    materializeGeometry,
    dispatchSequence: Object.freeze([
      'classify-box-box-dcd',
      'dispatch-boundary',
      'row-lifecycle',
      'dispatch-boundary',
      'transition-box-box-state',
      'dispatch-boundary',
      'materialize-box-box-cache',
      'dispatch-boundary',
      'materialize-box-box-geometry',
      'dispatch-boundary',
    ] as const),
    coverage: Object.freeze({
      boxBoxDcd: true as const,
      sphereSphereDcd: false as const,
      sphereBoxDcd: false as const,
      ccd: false as const,
    }),
  });
}

export interface WideExactSphereDcdWgslBundle {
  classify: string;
  preseedPenalty: string;
  transitionState: string;
  materializeCache: string;
  materializeGeometry: string;
  dispatchSequence: readonly [
    'classify-sphere-dcd',
    'dispatch-boundary',
    'preseed-sphere-penalty',
    'dispatch-boundary',
    'shared-row-lifecycle-after-all-dcd-classifiers',
    'dispatch-boundary',
    'transition-sphere-state',
    'dispatch-boundary',
    'materialize-sphere-cache',
    'dispatch-boundary',
    'materialize-sphere-geometry',
    'dispatch-boundary',
  ];
  coverage: Readonly<{
    boxBoxDcd: false;
    sphereSphereDcd: true;
    sphereBoxDcd: true;
    ccd: false;
  }>;
}

/**
 * Sphere-specialized extension to the exact full-f32 DCD helpers.  Keeping the
 * shape branch in a separate entry point lets the current box-only pipeline
 * remain byte-for-byte stable while the integration composes both classifiers
 * before the single transactional row-lifecycle sequence.
 */
function wideExactSphereDcdCommonWgsl(
  plan: WideExactContactGenerationPlan,
  options: Required<WideExactBoxBoxDcdWgslOptions>,
): string {
  const boxCommon = wideExactBoxBoxCommonWgsl(plan, options);
  return /* wgsl */`
${boxCommon}
const SHAPE_TYPE_SPHERE: u32 = 1u;
const DECISION_SPHERE_SPHERE: u32 = ${WIDE_EXACT_CONTACT_DECISION.sphereSphereBit}u;
const DECISION_SPHERE_BOX: u32 = ${WIDE_EXACT_CONTACT_DECISION.sphereBoxBit}u;
const DECISION_PRIOR_SPECULATIVE: u32 = ${WIDE_EXACT_CONTACT_DECISION.priorSpeculativeBit}u;
const DECISION_PRIOR_DCD_ACTIVE: u32 = ${WIDE_EXACT_CONTACT_DECISION.priorDcdActiveBit}u;
const DECISION_PRIOR_PENALTY_SEEDED: u32 = ${WIDE_EXACT_CONTACT_DECISION.priorPenaltySeededBit}u;

struct WideSphereManifold {
  isActive: u32,
  isSphereA: u32,
  isSphereB: u32,
  bestFeature: i32,
  normal: vec3f,
  pointWorld: vec3f,
  armAWorld: vec3f,
  armBWorld: vec3f,
  penetration: f32,
  featureKey: u32,
  tangentT1: vec3f,
  tangentAngle: f32,
}

fn axisValue3SphereExact(v: vec3f, axisIndex: u32) -> f32 {
  if (axisIndex == 0u) { return v.x; }
  if (axisIndex == 1u) { return v.y; }
  return v.z;
}

fn axisVector3SphereExact(
  axisIndex: u32,
  axis0: vec3f,
  axis1: vec3f,
  axis2: vec3f,
) -> vec3f {
  if (axisIndex == 0u) { return axis0; }
  if (axisIndex == 1u) { return axis1; }
  return axis2;
}

fn dominantAbsAxis3SphereExact(v: vec3f) -> u32 {
  let absoluteValue = abs(v);
  if (absoluteValue.y > absoluteValue.x && absoluteValue.y >= absoluteValue.z) { return 1u; }
  if (absoluteValue.z > absoluteValue.x && absoluteValue.z > absoluteValue.y) { return 2u; }
  return 0u;
}

fn leastAxis3SphereExact(v: vec3f) -> u32 {
  if (v.y < v.x && v.y <= v.z) { return 1u; }
  if (v.z < v.x && v.z < v.y) { return 2u; }
  return 0u;
}

fn buildWideSphereManifold(
  posA: vec3f,
  qA: vec4f,
  halfA: vec3f,
  shapeKindA: u32,
  posB: vec3f,
  qB: vec4f,
  halfB: vec3f,
  shapeKindB: u32,
  wasActive: bool,
  previousNormal: vec3f,
) -> WideSphereManifold {
  var result: WideSphereManifold;
  result.isActive = 0u;
  result.isSphereA = select(0u, 1u, shapeKindA == SHAPE_TYPE_SPHERE);
  result.isSphereB = select(0u, 1u, shapeKindB == SHAPE_TYPE_SPHERE);
  result.bestFeature = -1;
  result.normal = vec3f(0.0);
  result.pointWorld = vec3f(0.0);
  result.armAWorld = vec3f(0.0);
  result.armBWorld = vec3f(0.0);
  result.penetration = 0.0;
  result.featureKey = 0u;
  result.tangentT1 = vec3f(0.0);
  result.tangentAngle = 0.0;
  let sphereA = shapeKindA == SHAPE_TYPE_SPHERE;
  let sphereB = shapeKindB == SHAPE_TYPE_SPHERE;
  if (!sphereA && !sphereB) { return result; }

  let hysteresis = 0.35 * CONTACT_SLOP;
  let separationSlop = CONTACT_SLOP + select(0.0, hysteresis, wasActive);
  let radiusA = max(halfA.x, 0.0);
  let radiusB = max(halfB.x, 0.0);
  var contactNormal = vec3f(0.0);
  var penetration = 0.0;
  var pointA = vec3f(0.0);
  var pointB = vec3f(0.0);
  var featureKey = 0u;
  var bestFeature = -1;
  var preferredTangent = vec3f(0.0);
  var hasPreferredTangent = false;

  if (sphereA && sphereB) {
    let centerDelta = posB - posA;
    let centerDistance2 = dot(centerDelta, centerDelta);
    let radiusSum = radiusA + radiusB;
    if (centerDistance2 > 1e-12) {
      let centerDistance = sqrt(centerDistance2);
      contactNormal = centerDelta / centerDistance;
      penetration = radiusSum - centerDistance;
    } else {
      contactNormal = select(
        previousNormal,
        vec3f(0.0, 1.0, 0.0),
        dot(previousNormal, previousNormal) < 1e-12,
      );
      penetration = radiusSum;
    }
    if (!(penetration > -separationSlop)) { return result; }
    bestFeature = 6;
    pointA = posA + contactNormal * radiusA;
    pointB = posB - contactNormal * radiusB;
    featureKey = packEdgeContactFeatureExact(0u, 0u);
  } else {
    let sphereIsA = sphereA;
    let sphereCenter = select(posB, posA, sphereIsA);
    let boxCenter = select(posA, posB, sphereIsA);
    let sphereRadius = select(radiusB, radiusA, sphereIsA);
    let halfBox = select(halfA, halfB, sphereIsA);
    let boxQuaternion = select(qA, qB, sphereIsA);
    let boxAxis0 = qrotExact(boxQuaternion, vec3f(1.0, 0.0, 0.0));
    let boxAxis1 = qrotExact(boxQuaternion, vec3f(0.0, 1.0, 0.0));
    let boxAxis2 = qrotExact(boxQuaternion, vec3f(0.0, 0.0, 1.0));
    let relativeCenter = sphereCenter - boxCenter;
    let sphereLocal = vec3f(
      dot(relativeCenter, boxAxis0),
      dot(relativeCenter, boxAxis1),
      dot(relativeCenter, boxAxis2),
    );
    let clampedLocal = clamp(sphereLocal, -halfBox, halfBox);
    let deltaLocal = sphereLocal - clampedLocal;
    var boxPointLocal = clampedLocal;
    var faceAxis = 0u;
    var faceSign = 1.0;
    var boxOutwardNormal = boxAxis0;
    let outsideDistance2 = dot(deltaLocal, deltaLocal);
    if (outsideDistance2 > 1e-12) {
      faceAxis = dominantAbsAxis3SphereExact(deltaLocal);
      faceSign = signNonzeroExact(axisValue3SphereExact(deltaLocal, faceAxis));
      boxOutwardNormal = axisVector3SphereExact(
        faceAxis,
        boxAxis0,
        boxAxis1,
        boxAxis2,
      ) * faceSign;
      penetration = sphereRadius - sqrt(outsideDistance2);
    } else {
      let faceGap = halfBox - abs(sphereLocal);
      faceAxis = leastAxis3SphereExact(faceGap);
      faceSign = signNonzeroExact(axisValue3SphereExact(sphereLocal, faceAxis));
      if (faceAxis == 0u) {
        boxPointLocal = vec3f(faceSign * halfBox.x, sphereLocal.y, sphereLocal.z);
      } else if (faceAxis == 1u) {
        boxPointLocal = vec3f(sphereLocal.x, faceSign * halfBox.y, sphereLocal.z);
      } else {
        boxPointLocal = vec3f(sphereLocal.x, sphereLocal.y, faceSign * halfBox.z);
      }
      boxOutwardNormal = axisVector3SphereExact(
        faceAxis,
        boxAxis0,
        boxAxis1,
        boxAxis2,
      ) * faceSign;
      penetration = sphereRadius + axisValue3SphereExact(faceGap, faceAxis);
    }
    if (!(penetration > -separationSlop)) { return result; }
    let boxPointWorld = boxCenter
      + boxAxis0 * boxPointLocal.x
      + boxAxis1 * boxPointLocal.y
      + boxAxis2 * boxPointLocal.z;
    let spherePointWorld = sphereCenter - boxOutwardNormal * sphereRadius;
    contactNormal = select(boxOutwardNormal, -boxOutwardNormal, sphereIsA);
    bestFeature = i32(faceAxis) + select(0, 3, sphereIsA);
    featureKey = packFaceContactFeatureExact(!sphereIsA, faceAxis, 0u, 0u);
    pointA = select(boxPointWorld, spherePointWorld, sphereIsA);
    pointB = select(spherePointWorld, boxPointWorld, sphereIsA);
    hasPreferredTangent = true;
    preferredTangent = select(boxAxis0, boxAxis1, faceAxis == 0u);
    if (faceAxis == 1u) {
      preferredTangent = boxAxis2;
    } else if (faceAxis == 2u) {
      preferredTangent = boxAxis0;
    }
  }

  var tangent = canonicalTangentBasisExact(contactNormal);
  if (hasPreferredTangent) {
    tangent = tangentBasisFromPreferredT1Exact(contactNormal, preferredTangent);
  }
  result.isActive = 1u;
  result.bestFeature = bestFeature;
  result.normal = contactNormal;
  result.pointWorld = 0.5 * (pointA + pointB);
  result.armAWorld = pointA - posA;
  result.armBWorld = pointB - posB;
  result.penetration = max(penetration, 0.0);
  result.featureKey = featureKey;
  result.tangentT1 = tangent.t1;
  result.tangentAngle = tangentBasisAngleFromT1Exact(contactNormal, tangent.t1);
  return result;
}

fn sphereDecisionEnabled(decisionFlags: u32) -> bool {
  return (decisionFlags & DECISION_ACTIVE) != 0u
    && (decisionFlags & (DECISION_SPHERE_SPHERE | DECISION_SPHERE_BOX)) != 0u;
}
`;
}

/**
 * Exact sphere/sphere and sphere/box DCD for the wide SoA ABI.  This does not
 * contain swept/TOI logic and therefore deliberately reports CCD=false.
 */
export function generateWideExactSphereDcdWgsl(
  plan: WideExactContactGenerationPlan,
  requested: WideExactBoxBoxDcdWgslOptions = {},
): WideExactSphereDcdWgslBundle {
  const options: Required<WideExactBoxBoxDcdWgslOptions> = {
    contactSlop: requested.contactSlop ?? 0.005,
    collisionMargin: requested.collisionMargin ?? AVBD_COLLISION_MARGIN,
    frictionStatic: requested.frictionStatic ?? AVBD_FRICTION_STATIC,
  };
  const common = wideExactSphereDcdCommonWgsl(plan, options);

  const classify = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read> outputMasks: U32Buffer;
@group(0) @binding(5) var<storage, read> contactMetaPlane: Vec4uBuffer;
@group(0) @binding(6) var<storage, read> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(8) var<storage, read> contactArmBPlane: Vec4fBuffer;
@group(0) @binding(9) var<storage, read_write> decisions: Vec2uBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeKindA = shapeTypeExact(shapeA.x);
  let shapeKindB = shapeTypeExact(shapeB.x);
  let sphereA = shapeKindA == SHAPE_TYPE_SPHERE;
  let sphereB = shapeKindB == SHAPE_TYPE_SPHERE;
  if (!sphereA && !sphereB) { return; }

  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    decisions.values[logicalBase + slot] = vec2u(0u);
  }
  if (
    positionMassA.w + positionMassB.w == 0.0
    || !shapesCanCollideExact(shapeA, shapeB)
  ) { return; }

  let previousMask = outputMasks.values[descriptor] & 0xffu;
  var oldEnabled: array<u32, 8>;
  var oldMetadata: array<vec4u, 8>;
  var oldArmA: array<vec4f, 8>;
  var oldArmB: array<vec4f, 8>;
  var previousNormal = vec3f(0.0);
  var wasActive = false;
  var firstPriorDcdSlot = 0xffffffffu;
  var firstPriorSpeculativeSlot = 0xffffffffu;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    oldEnabled[slot] = 0u;
    let logicalSlot = logicalBase + slot;
    let row = slotRows.values[logicalSlot];
    if ((previousMask & (1u << slot)) == 0u || row >= CONTACT_ROW_CAPACITY) { continue; }
    let contactWords = contactMetaPlane.values[row];
    if (
      contactWords.x != bodyA
      || contactWords.y != bodyB
      || contactWords.z != descriptor + 1u
    ) { continue; }
    if ((contactWords.w & CONTACT_INFO_SPECULATIVE_BIT) != 0u) {
      decisions.values[logicalSlot] = vec2u(0u, DECISION_PRIOR_SPECULATIVE);
      if (firstPriorSpeculativeSlot == 0xffffffffu) {
        firstPriorSpeculativeSlot = slot;
      }
      continue;
    }
    oldEnabled[slot] = 1u;
    decisions.values[logicalSlot] = vec2u(0u, DECISION_PRIOR_DCD_ACTIVE);
    if (firstPriorDcdSlot == 0xffffffffu) { firstPriorDcdSlot = slot; }
    oldMetadata[slot] = contactWords;
    oldArmA[slot] = contactArmAPlane.values[row];
    oldArmB[slot] = contactArmBPlane.values[row];
    if (!wasActive) {
      wasActive = true;
      previousNormal = contactNormalPlane.values[row].xyz;
    }
  }

  let manifold = buildWideSphereManifold(
    positionMassA.xyz, quaternionA, shapeA.yzw, shapeKindA,
    positionMassB.xyz, quaternionB, shapeB.yzw, shapeKindB,
    wasActive, previousNormal,
  );
  if (manifold.isActive == 0u) { return; }
  let pointA = positionMassA.xyz + manifold.armAWorld;
  let pointB = positionMassB.xyz + manifold.armBWorld;
  if (-dot(pointB - pointA, manifold.normal) < 0.0) { return; }

  let maxDrift = max(3.5 * CONTACT_SLOP, 0.016);
  let maxDrift2 = maxDrift * maxDrift;
  var oldSlot = 0xffffffffu;
  var bestDistance2 = 1e30;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    if (oldEnabled[slot] == 0u) { continue; }
    if ((oldMetadata[slot].w & 0x1ffu) != manifold.featureKey) { continue; }
    let oldWorldArmA = select(
      qrotExact(quaternionA, oldArmA[slot].xyz),
      oldArmA[slot].xyz,
      sphereA,
    );
    let oldWorldArmB = select(
      qrotExact(quaternionB, oldArmB[slot].xyz),
      oldArmB[slot].xyz,
      sphereB,
    );
    let oldPoint = 0.5 * (
      (positionMassA.xyz + oldWorldArmA)
        + (positionMassB.xyz + oldWorldArmB)
    );
    let pointDelta = manifold.pointWorld - oldPoint;
    let distance2 = dot(pointDelta, pointDelta);
    if (distance2 <= maxDrift2 && distance2 < bestDistance2) {
      bestDistance2 = distance2;
      oldSlot = slot;
    }
  }

  var chosenSlot = oldSlot;
  if (chosenSlot == 0xffffffffu) {
    // One sphere DCD candidate owns at most one row.  Any old row not selected
    // as its exact match is available, including an unmatched prior DCD row.
    chosenSlot = select(0u, firstPriorSpeculativeSlot, firstPriorSpeculativeSlot != 0xffffffffu);
    chosenSlot = select(chosenSlot, firstPriorDcdSlot, firstPriorDcdSlot != 0xffffffffu);
  }
  if (chosenSlot == 0xffffffffu) { return; }
  var preserve = 0u;
  var warmstartReason = 4u;
  if (oldSlot != 0xffffffffu) {
    let oldRow = slotRows.values[logicalBase + oldSlot];
    let normalAgreement = dot(contactNormalPlane.values[oldRow].xyz, manifold.normal);
    if (normalAgreement > 0.95) {
      preserve = 1u;
      warmstartReason = 1u;
    } else {
      warmstartReason = 3u;
    }
  }
  let encodedInfo = packContactInfoExact(
    manifold.featureKey,
    preserve,
    0u,
    0u,
    warmstartReason,
    chosenSlot,
  );
  let shapeFlag = select(DECISION_SPHERE_BOX, DECISION_SPHERE_SPHERE, sphereA && sphereB);
  let priorFlags = decisions.values[logicalBase + chosenSlot].y
    & (DECISION_PRIOR_SPECULATIVE | DECISION_PRIOR_DCD_ACTIVE);
  decisions.values[logicalBase + chosenSlot] = vec2u(
    encodedInfo,
    priorFlags | DECISION_ACTIVE | DECISION_DCD | shapeFlag,
  );
}
`;

  const preseedPenalty = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(5) var<storage, read_write> contactPenaltyPlane: Vec4fBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  var chosenSlot = 0xffffffffu;
  var priorSpeculativePenaltyN = 1.0;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    let row = slotRows.values[logicalBase + slot];
    if (sphereDecisionEnabled(decision.y)) {
      if (((decision.x >> 16u) & 1u) != 0u) { return; }
      chosenSlot = slot;
    }
    if (
      (decision.y & DECISION_PRIOR_SPECULATIVE) != 0u
      && row < CONTACT_ROW_CAPACITY
    ) {
      priorSpeculativePenaltyN = max(
        priorSpeculativePenaltyN,
        contactPenaltyPlane.values[row].x,
      );
    }
  }
  if (chosenSlot == 0xffffffffu) { return; }
  let chosenRow = slotRows.values[logicalBase + chosenSlot];
  // A row that does not exist yet cannot have prior speculative state.  The
  // post-lifecycle transition will apply the mass-scaled static seed instead.
  if (chosenRow >= CONTACT_ROW_CAPACITY) { return; }
  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let frictionScale = sqrt(
    max(shapeFrictionExact(shapeA.x), 0.0)
      * max(shapeFrictionExact(shapeB.x), 0.0)
  );
  let immovableSpherePair = positionMassA.w <= 0.0 || positionMassB.w <= 0.0;
  let staticSpherePenaltyN = select(
    1.0,
    min(
      100000000.0,
      416666.67 / max(positionMassA.w + positionMassB.w, 1.0e-6),
    ),
    immovableSpherePair,
  );
  contactPenaltyPlane.values[chosenRow] = vec4f(
    max(priorSpeculativePenaltyN, staticSpherePenaltyN),
    1.0,
    1.0,
    frictionScale,
  );
  let chosenDecision = decisions.values[logicalBase + chosenSlot];
  decisions.values[logicalBase + chosenSlot] = vec2u(
    chosenDecision.x,
    chosenDecision.y | DECISION_PRIOR_PENALTY_SEEDED,
  );
}
`;

  const transitionState = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(5) var<storage, read> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(6) var<storage, read> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read_write> contactShadowPlane: Vec4fBuffer;
@group(0) @binding(8) var<storage, read_write> contactDualPlane: Vec4fBuffer;
@group(0) @binding(9) var<storage, read_write> contactPenaltyPlane: Vec4fBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  var hasSphereDecision = false;
  var previousNormal = vec3f(0.0);
  var wasActive = false;
  var preferredTangent = vec3f(0.0);
  var preferredCount = 0u;
  var priorSpeculativePenaltyN = 1.0;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    if ((decision.y & DECISION_PRIOR_DCD_ACTIVE) != 0u && !wasActive) {
      previousNormal = contactNormalPlane.values[row].xyz;
      wasActive = true;
    }
    if ((decision.y & DECISION_PRIOR_SPECULATIVE) != 0u) {
      priorSpeculativePenaltyN = max(
        priorSpeculativePenaltyN,
        contactPenaltyPlane.values[row].x,
      );
    }
    if (!sphereDecisionEnabled(decision.y)) { continue; }
    hasSphereDecision = true;
    let reason = (decision.x >> 21u) & 0x7u;
    if (((decision.x >> 16u) & 1u) != 0u || reason == 3u) {
      let oldNormal = contactNormalPlane.values[row].xyz;
      let oldAngle = contactArmAPlane.values[row].w;
      preferredTangent += tangentBasisFromAngleExact(oldNormal, oldAngle).t1;
      preferredCount += 1u;
    }
  }
  if (!hasSphereDecision) { return; }

  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeKindA = shapeTypeExact(shapeA.x);
  let shapeKindB = shapeTypeExact(shapeB.x);
  let manifold = buildWideSphereManifold(
    positionMassA.xyz, quaternionA, shapeA.yzw, shapeKindA,
    positionMassB.xyz, quaternionB, shapeB.yzw, shapeKindB,
    wasActive, previousNormal,
  );
  if (manifold.isActive == 0u) { return; }
  var writeTangent = tangentBasisFromPreferredT1Exact(manifold.normal, manifold.tangentT1);
  if (preferredCount > 0u && dot(preferredTangent, writeTangent.t1) < 0.0) {
    writeTangent = TangentBasis(-writeTangent.t1, -writeTangent.t2);
  }
  let frictionScale = sqrt(
    max(shapeFrictionExact(shapeA.x), 0.0)
      * max(shapeFrictionExact(shapeB.x), 0.0)
  );
  let spherePair = shapeKindA == SHAPE_TYPE_SPHERE || shapeKindB == SHAPE_TYPE_SPHERE;
  let immovableSpherePair = spherePair && (positionMassA.w <= 0.0 || positionMassB.w <= 0.0);
  let staticSpherePenaltyN = select(
    1.0,
    min(
      100000000.0,
      416666.67 / max(positionMassA.w + positionMassB.w, 1.0e-6),
    ),
    immovableSpherePair,
  );

  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if (!sphereDecisionEnabled(decision.y)) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let preserve = ((decision.x >> 16u) & 1u) != 0u;
    if (!preserve) {
      let preseededPenaltyN = select(
        1.0,
        contactPenaltyPlane.values[row].x,
        (decision.y & DECISION_PRIOR_PENALTY_SEEDED) != 0u,
      );
      contactShadowPlane.values[row] = vec4f(0.0, 0.0, 0.0, frictionScale);
      contactDualPlane.values[row] = vec4f(0.0);
      contactPenaltyPlane.values[row] = vec4f(
        max(max(preseededPenaltyN, priorSpeculativePenaltyN), staticSpherePenaltyN),
        1.0,
        1.0,
        frictionScale,
      );
      continue;
    }
    let oldNormal = contactNormalPlane.values[row].xyz;
    let oldAngle = contactArmAPlane.values[row].w;
    let oldBasis = tangentBasisFromAngleExact(oldNormal, oldAngle);
    let oldShadow = contactShadowPlane.values[row];
    let oldDual = contactDualPlane.values[row];
    let lambdaN = max(oldShadow.x, 0.0);
    var carriedShadow = oldShadow;
    var carriedDual = oldDual;
    if (dot(oldNormal, oldNormal) > 1e-10) {
      let tangentImpulseWorld = oldBasis.t1 * oldShadow.y + oldBasis.t2 * oldShadow.z;
      var lambdaT = vec2f(
        dot(tangentImpulseWorld, writeTangent.t1),
        dot(tangentImpulseWorld, writeTangent.t2),
      );
      let maximumTangent = FRICTION_STATIC * frictionScale * lambdaN;
      let maximumTangent2 = maximumTangent * maximumTangent;
      let lambdaT2 = dot(lambdaT, lambdaT);
      if (lambdaT2 > maximumTangent2 && lambdaT2 > 1e-12) {
        lambdaT *= maximumTangent * inverseSqrt(lambdaT2);
      }
      carriedShadow = vec4f(lambdaN, lambdaT.x, lambdaT.y, oldShadow.w);
      let dualTangentWorld = oldBasis.t1 * oldDual.y + oldBasis.t2 * oldDual.z;
      carriedDual = vec4f(
        oldDual.x,
        dot(dualTangentWorld, writeTangent.t1),
        dot(dualTangentWorld, writeTangent.t2),
        0.0,
      );
    }
    contactShadowPlane.values[row] = carriedShadow;
    contactDualPlane.values[row] = carriedDual;
  }
}
`;

  const materializeCache = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(5) var<storage, read> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(6) var<storage, read> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read_write> contactCachePlane: Vec4uBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  var hasSphereDecision = false;
  var previousNormal = vec3f(0.0);
  var wasActive = false;
  var preferredTangent = vec3f(0.0);
  var preferredCount = 0u;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    if ((decision.y & DECISION_PRIOR_DCD_ACTIVE) != 0u && !wasActive) {
      previousNormal = contactNormalPlane.values[row].xyz;
      wasActive = true;
    }
    if (!sphereDecisionEnabled(decision.y)) { continue; }
    hasSphereDecision = true;
    let reason = (decision.x >> 21u) & 0x7u;
    if (((decision.x >> 16u) & 1u) != 0u || reason == 3u) {
      let oldNormal = contactNormalPlane.values[row].xyz;
      let oldAngle = contactArmAPlane.values[row].w;
      preferredTangent += tangentBasisFromAngleExact(oldNormal, oldAngle).t1;
      preferredCount += 1u;
    }
  }
  if (!hasSphereDecision) { return; }
  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let manifold = buildWideSphereManifold(
    positionMassA.xyz, quaternionA, shapeA.yzw, shapeTypeExact(shapeA.x),
    positionMassB.xyz, quaternionB, shapeB.yzw, shapeTypeExact(shapeB.x),
    wasActive, previousNormal,
  );
  if (manifold.isActive == 0u) { return; }
  var writeTangent = tangentBasisFromPreferredT1Exact(manifold.normal, manifold.tangentT1);
  if (preferredCount > 0u && dot(preferredTangent, writeTangent.t1) < 0.0) {
    writeTangent = TangentBasis(-writeTangent.t1, -writeTangent.t2);
  }
  let tangentBits = bitcast<vec3u>(writeTangent.t1);
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if (!sphereDecisionEnabled(decision.y)) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let oldCacheWord = contactCachePlane.values[row].x;
    let preserve = ((decision.x >> 16u) & 1u) != 0u;
    contactCachePlane.values[row] = vec4u(select(0u, oldCacheWord, preserve), tangentBits);
  }
}
`;

  const materializeGeometry = /* wgsl */`
${common}
@group(0) @binding(0) var<storage, read_write> collisionBodyState: Vec4fBuffer;
@group(0) @binding(1) var<storage, read_write> activeDescriptors: U32Buffer;
@group(0) @binding(2) var<storage, read> descriptorEndpoints: U32Buffer;
@group(0) @binding(3) var<storage, read> slotRows: U32Buffer;
@group(0) @binding(4) var<storage, read_write> decisions: Vec2uBuffer;
@group(0) @binding(5) var<storage, read_write> contactMetaPlane: Vec4uBuffer;
@group(0) @binding(6) var<storage, read_write> contactNormalPlane: Vec4fBuffer;
@group(0) @binding(7) var<storage, read_write> contactArmAPlane: Vec4fBuffer;
@group(0) @binding(8) var<storage, read_write> contactArmBPlane: Vec4fBuffer;
@group(0) @binding(9) var<storage, read_write> contactConstraintC0Plane: Vec4fBuffer;

@compute @workgroup_size(${WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE}, 1, 1)
fn main(
  @builtin(workgroup_id) workgroupId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let ordinal = descriptorOrdinal(workgroupId, localId);
  let activeCount = min(activeDescriptors.values[0], DESCRIPTOR_CAPACITY);
  if (ordinal >= activeCount) { return; }
  let descriptor = activeDescriptors.values[ACTIVE_DESCRIPTOR_START + ordinal];
  if (descriptor >= DESCRIPTOR_CAPACITY) { return; }
  let logicalBase = descriptor * PAIR_MANIFOLD_SLOTS;
  var hasSphereDecision = false;
  var previousNormal = vec3f(0.0);
  var wasActive = false;
  var preferredTangent = vec3f(0.0);
  var preferredCount = 0u;
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    if ((decision.y & DECISION_PRIOR_DCD_ACTIVE) != 0u && !wasActive) {
      previousNormal = contactNormalPlane.values[row].xyz;
      wasActive = true;
    }
    if (!sphereDecisionEnabled(decision.y)) { continue; }
    hasSphereDecision = true;
    let reason = (decision.x >> 21u) & 0x7u;
    if (((decision.x >> 16u) & 1u) != 0u || reason == 3u) {
      let oldNormal = contactNormalPlane.values[row].xyz;
      let oldAngle = contactArmAPlane.values[row].w;
      preferredTangent += tangentBasisFromAngleExact(oldNormal, oldAngle).t1;
      preferredCount += 1u;
    }
  }
  if (!hasSphereDecision) { return; }
  let bodyA = descriptorEndpoints.values[descriptor];
  let bodyB = descriptorEndpoints.values[DESCRIPTOR_BODY_B_RELATIVE_WORDS + descriptor];
  if (bodyA >= bodyB || bodyB >= BODY_CAPACITY) { return; }
  let bodyBaseA = bodyA * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let bodyBaseB = bodyB * ${WIDE_EXACT_COLLISION_BODY_STATE.vec4sPerBody}u;
  let positionMassA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let positionMassB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentPositionAndInverseMass}u];
  let quaternionA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let quaternionB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.currentQuaternion}u];
  let shapeA = collisionBodyState.values[bodyBaseA + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeB = collisionBodyState.values[bodyBaseB + ${WIDE_EXACT_COLLISION_BODY_STATE.shape}u];
  let shapeKindA = shapeTypeExact(shapeA.x);
  let shapeKindB = shapeTypeExact(shapeB.x);
  let sphereA = shapeKindA == SHAPE_TYPE_SPHERE;
  let sphereB = shapeKindB == SHAPE_TYPE_SPHERE;
  let manifold = buildWideSphereManifold(
    positionMassA.xyz, quaternionA, shapeA.yzw, shapeKindA,
    positionMassB.xyz, quaternionB, shapeB.yzw, shapeKindB,
    wasActive, previousNormal,
  );
  if (manifold.isActive == 0u) { return; }
  var writeTangent = tangentBasisFromPreferredT1Exact(manifold.normal, manifold.tangentT1);
  if (preferredCount > 0u && dot(preferredTangent, writeTangent.t1) < 0.0) {
    writeTangent = TangentBasis(-writeTangent.t1, -writeTangent.t2);
  }
  let tangentAngle = tangentBasisAngleFromT1Exact(manifold.normal, writeTangent.t1);
  for (var slot = 0u; slot < PAIR_MANIFOLD_SLOTS; slot += 1u) {
    let decision = decisions.values[logicalBase + slot];
    if (!sphereDecisionEnabled(decision.y)) { continue; }
    if ((decision.x & 0x1ffu) != manifold.featureKey) { continue; }
    let row = slotRows.values[logicalBase + slot];
    if (row >= CONTACT_ROW_CAPACITY) { continue; }
    let worldArmA = manifold.armAWorld;
    let worldArmB = manifold.armBWorld;
    let pointA = positionMassA.xyz + worldArmA;
    let pointB = positionMassB.xyz + worldArmB;
    let rawPenetration = -dot(pointB - pointA, manifold.normal);
    if (rawPenetration < 0.0) { continue; }
    let cachedVector = pointA - pointB;
    let storedArmA = select(qrotExact(qconjExact(quaternionA), worldArmA), worldArmA, sphereA);
    let storedArmB = select(qrotExact(qconjExact(quaternionB), worldArmB), worldArmB, sphereB);
    let sphereArmFlags = select(0u, 1u, sphereA) | select(0u, 2u, sphereB);
    contactMetaPlane.values[row] = vec4u(bodyA, bodyB, descriptor + 1u, decision.x);
    contactNormalPlane.values[row] = vec4f(manifold.normal, max(rawPenetration, 0.0));
    contactArmAPlane.values[row] = vec4f(storedArmA, tangentAngle);
    contactArmBPlane.values[row] = vec4f(storedArmB, f32(sphereArmFlags));
    contactConstraintC0Plane.values[row] = vec4f(
      -dot(cachedVector, manifold.normal) + COLLISION_MARGIN,
      -dot(cachedVector, writeTangent.t1),
      -dot(cachedVector, writeTangent.t2),
      0.0,
    );
  }
}
`;

  return Object.freeze({
    classify,
    preseedPenalty,
    transitionState,
    materializeCache,
    materializeGeometry,
    dispatchSequence: Object.freeze([
      'classify-sphere-dcd',
      'dispatch-boundary',
      'preseed-sphere-penalty',
      'dispatch-boundary',
      'shared-row-lifecycle-after-all-dcd-classifiers',
      'dispatch-boundary',
      'transition-sphere-state',
      'dispatch-boundary',
      'materialize-sphere-cache',
      'dispatch-boundary',
      'materialize-sphere-geometry',
      'dispatch-boundary',
    ] as const),
    coverage: Object.freeze({
      boxBoxDcd: false as const,
      sphereSphereDcd: true as const,
      sphereBoxDcd: true as const,
      ccd: false as const,
    }),
  });
}

function exactU32(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0 || value > U32_MAX) {
    throw new Error(`${label} must be an exact u32 (received ${String(value)}).`);
  }
  return value;
}

function exactMask(value: number, slots: number, label: string): number {
  const mask = exactU32(value, label);
  const validMask = slots === 32 ? U32_MAX : (2 ** slots) - 1;
  if ((mask & ~validMask) !== 0) throw new Error(`${label} exceeds ${slots} logical slots.`);
  return mask;
}

/**
 * GPU-free canonical-order model of the same all-or-rollback transaction.
 * Atomics choose physical rows on GPU, but they never choose logical contact
 * identity or stable active ordinal. This reference fixes reservation order to
 * active-descriptor order then increasing logical slot for small-scene tests.
 */
export function runWideExactContactLifecycleReference(
  input: WideExactContactLifecycleReferenceInput,
): WideExactContactLifecycleReferenceResult {
  const descriptorCapacity = positiveSafeInteger(
    input.descriptorCapacity,
    'descriptorCapacity',
  );
  const pairManifoldSlots = positiveSafeInteger(
    input.pairManifoldSlots,
    'pairManifoldSlots',
  );
  if (pairManifoldSlots > 8) throw new Error('pairManifoldSlots must be <= 8.');
  const contactRowCapacity = positiveSafeInteger(
    input.contactRowCapacity,
    'contactRowCapacity',
  );
  const logicalSlots = checkedProduct(
    'reference logical slots',
    descriptorCapacity,
    pairManifoldSlots,
  );
  if (input.desiredMasks.length !== descriptorCapacity) {
    throw new Error('desiredMasks length must equal descriptorCapacity.');
  }
  if (input.outputMasks.length !== descriptorCapacity) {
    throw new Error('outputMasks length must equal descriptorCapacity.');
  }
  if (input.slotRows.length !== logicalSlots) {
    throw new Error('slotRows length must equal descriptorCapacity * pairManifoldSlots.');
  }
  const rowHighWaterStart = nonNegativeSafeInteger(input.rowHighWater, 'rowHighWater');
  if (rowHighWaterStart > contactRowCapacity) {
    throw new Error('rowHighWater exceeds contactRowCapacity.');
  }
  const activeDescriptors = [...input.activeDescriptors];
  const seenDescriptors = new Set<number>();
  for (const descriptorValue of activeDescriptors) {
    const descriptor = nonNegativeSafeInteger(descriptorValue, 'active descriptor');
    if (descriptor >= descriptorCapacity) throw new Error('active descriptor exceeds capacity.');
    if (seenDescriptors.has(descriptor)) {
      throw new Error('active descriptor stream must contain unique descriptor indices.');
    }
    seenDescriptors.add(descriptor);
  }
  const desiredMasks = input.desiredMasks.slice();
  const outputMasks = input.outputMasks.slice();
  const slotRows = input.slotRows.slice();
  const ownedRows = new Set<number>();
  for (let descriptor = 0; descriptor < descriptorCapacity; descriptor++) {
    const desired = exactMask(
      desiredMasks[descriptor]!,
      pairManifoldSlots,
      `desiredMasks[${descriptor}]`,
    );
    desiredMasks[descriptor] = desired;
    const output = exactMask(
      outputMasks[descriptor]!,
      pairManifoldSlots,
      `outputMasks[${descriptor}]`,
    );
    outputMasks[descriptor] = output;
    for (let slot = 0; slot < pairManifoldSlots; slot++) {
      const logicalSlot = descriptor * pairManifoldSlots + slot;
      const row = exactU32(slotRows[logicalSlot]!, `slotRows[${logicalSlot}]`);
      if (row > contactRowCapacity) throw new Error('slotRows contains an invalid row sentinel.');
      const active = (output & (1 << slot)) !== 0;
      if (active !== (row < contactRowCapacity)) {
        throw new Error('outputMasks and slotRows violate the exact ownership invariant.');
      }
      if (active) {
        if (ownedRows.has(row)) throw new Error('Two logical slots own the same physical row.');
        ownedRows.add(row);
      }
    }
  }
  const currentFreeRows = [...(input.currentFreeRows ?? [])];
  for (const [index, rowValue] of currentFreeRows.entries()) {
    const row = exactU32(rowValue, `currentFreeRows[${index}]`);
    if (row >= contactRowCapacity) throw new Error('currentFreeRows contains an invalid row.');
    if (row >= rowHighWaterStart) {
      throw new Error('currentFreeRows cannot contain a row beyond rowHighWater.');
    }
    if (ownedRows.has(row)) throw new Error('A current free row is still owned.');
    ownedRows.add(row);
  }

  let failureBits = 0;
  let freeCursor = 0;
  let rowHighWater = rowHighWaterStart;
  const reservations: Array<readonly [logicalSlot: number, row: number]> = [];
  const nextFreeRows: number[] = [];
  for (const descriptor of activeDescriptors) {
    const desired = desiredMasks[descriptor]!;
    for (let slot = 0; slot < pairManifoldSlots; slot++) {
      if ((desired & (1 << slot)) === 0) continue;
      const logicalSlot = descriptor * pairManifoldSlots + slot;
      if (slotRows[logicalSlot]! < contactRowCapacity) continue;
      let row: number;
      if (freeCursor < currentFreeRows.length) {
        row = currentFreeRows[freeCursor++]!;
      } else {
        row = rowHighWater++;
      }
      if (row >= contactRowCapacity) {
        rowHighWater = Math.min(rowHighWater, contactRowCapacity);
        failureBits |= WideExactContactLifecycleFailureBit.rowPoolOverflow;
        continue;
      }
      slotRows[logicalSlot] = row;
      reservations.push(Object.freeze([logicalSlot, row] as const));
    }
  }
  for (const descriptor of activeDescriptors) {
    const desired = desiredMasks[descriptor]!;
    for (let slot = 0; slot < pairManifoldSlots; slot++) {
      if ((desired & (1 << slot)) === 0) continue;
      const logicalSlot = descriptor * pairManifoldSlots + slot;
      if (slotRows[logicalSlot]! >= contactRowCapacity) {
        failureBits |= WideExactContactLifecycleFailureBit.missingReservation;
      }
    }
  }

  if (failureBits !== 0) {
    for (const [logicalSlot, row] of reservations) {
      slotRows[logicalSlot] = contactRowCapacity;
      nextFreeRows.push(row);
    }
    return Object.freeze({
      committed: false,
      failureBits,
      outputMasks,
      slotRows,
      rowHighWater,
      consumedFreeRows: freeCursor,
      nextFreeRows: Object.freeze(nextFreeRows),
      reservations: reservations.length,
      rollbacks: reservations.length,
      releases: 0,
    });
  }

  let releases = 0;
  for (const descriptor of activeDescriptors) {
    const oldMask = outputMasks[descriptor]!;
    const desiredMask = desiredMasks[descriptor]!;
    for (let slot = 0; slot < pairManifoldSlots; slot++) {
      if ((oldMask & (1 << slot)) === 0 || (desiredMask & (1 << slot)) !== 0) continue;
      const logicalSlot = descriptor * pairManifoldSlots + slot;
      const row = slotRows[logicalSlot]!;
      if (row >= contactRowCapacity) {
        throw new Error('An active old logical slot lost its physical row before commit.');
      }
      slotRows[logicalSlot] = contactRowCapacity;
      nextFreeRows.push(row);
      releases++;
    }
    outputMasks[descriptor] = desiredMask;
  }
  return Object.freeze({
    committed: true,
    failureBits: 0,
    outputMasks,
    slotRows,
    rowHighWater,
    consumedFreeRows: freeCursor,
    nextFreeRows: Object.freeze(nextFreeRows),
    reservations: reservations.length,
    rollbacks: 0,
    releases,
  });
}

type ReferenceVec3 = readonly [number, number, number];
type ReferenceQuat = readonly [number, number, number, number];

export interface WideExactBoxBoxReferenceInput {
  bodyA: number;
  bodyB: number;
  descriptorIndex: number;
  positionA: ReferenceVec3;
  quaternionA: ReferenceQuat;
  halfA: ReferenceVec3;
  positionB: ReferenceVec3;
  quaternionB: ReferenceQuat;
  halfB: ReferenceVec3;
  contactSlop?: number;
  collisionMargin?: number;
}

export interface WideExactBoxBoxReferenceResult {
  separated: boolean;
  bestFeature: number;
  rows: readonly WideExactReferenceContactRow[];
  legacyAosWords: readonly Uint32Array[];
  candidateFeatureKeys: readonly number[];
}

export type WideExactSphereReferenceShape = 'box' | 'sphere';

export interface WideExactSphereDcdReferenceInput {
  bodyA: number;
  bodyB: number;
  descriptorIndex: number;
  positionA: ReferenceVec3;
  quaternionA: ReferenceQuat;
  halfA: ReferenceVec3;
  shapeA: WideExactSphereReferenceShape;
  inverseMassA?: number;
  positionB: ReferenceVec3;
  quaternionB: ReferenceQuat;
  halfB: ReferenceVec3;
  shapeB: WideExactSphereReferenceShape;
  inverseMassB?: number;
  previousNormal?: ReferenceVec3;
  contactSlop?: number;
  collisionMargin?: number;
  frictionScale?: number;
  priorSpeculativePenaltyN?: number;
}

export interface WideExactSphereDcdReferenceResult {
  separated: boolean;
  branch: 'sphere-sphere' | 'sphere-box-outside' | 'sphere-box-inside';
  bestFeature: number;
  faceAxis: number;
  row: WideExactReferenceContactRow | null;
  legacyAosWords: Uint32Array | null;
}

type ReferenceBoxCandidate = {
  point: ReferenceVec3;
  armA: ReferenceVec3;
  armB: ReferenceVec3;
  penetration: number;
  featureKey: number;
};

const rf = Math.fround;
const rv = (x: number, y: number, z: number): ReferenceVec3 => [rf(x), rf(y), rf(z)];
const radd = (a: ReferenceVec3, b: ReferenceVec3): ReferenceVec3 =>
  rv(rf(a[0] + b[0]), rf(a[1] + b[1]), rf(a[2] + b[2]));
const rsub = (a: ReferenceVec3, b: ReferenceVec3): ReferenceVec3 =>
  rv(rf(a[0] - b[0]), rf(a[1] - b[1]), rf(a[2] - b[2]));
const rscale = (a: ReferenceVec3, s: number): ReferenceVec3 =>
  rv(rf(a[0] * s), rf(a[1] * s), rf(a[2] * s));
const rneg = (a: ReferenceVec3): ReferenceVec3 => rv(-a[0], -a[1], -a[2]);
const rdot = (a: ReferenceVec3, b: ReferenceVec3): number =>
  rf(rf(rf(a[0] * b[0]) + rf(a[1] * b[1])) + rf(a[2] * b[2]));
const rcross = (a: ReferenceVec3, b: ReferenceVec3): ReferenceVec3 => rv(
  rf(rf(a[1] * b[2]) - rf(a[2] * b[1])),
  rf(rf(a[2] * b[0]) - rf(a[0] * b[2])),
  rf(rf(a[0] * b[1]) - rf(a[1] * b[0])),
);
const rlength2 = (a: ReferenceVec3): number => rdot(a, a);
const rnormalize = (a: ReferenceVec3): ReferenceVec3 => {
  const length2 = rlength2(a);
  return length2 > 1e-12 ? rscale(a, rf(1 / Math.sqrt(length2))) : rv(0, 0, 0);
};
const raxis = (axis: number): ReferenceVec3 =>
  axis === 0 ? rv(1, 0, 0) : axis === 1 ? rv(0, 1, 0) : rv(0, 0, 1);
const rcomponent = (v: ReferenceVec3, axis: number): number => v[axis] ?? v[2];

function rquatRotate(q: ReferenceQuat, v: ReferenceVec3): ReferenceVec3 {
  const qxyz = rv(q[0], q[1], q[2]);
  const t = rscale(rcross(qxyz, v), 2);
  return radd(radd(v, rscale(t, q[3])), rcross(qxyz, t));
}

function rcanonicalTangent(normal: ReferenceVec3): readonly [ReferenceVec3, ReferenceVec3] {
  const reference = Math.abs(normal[1]) > 0.999 ? rv(1, 0, 0) : rv(0, 1, 0);
  let t1 = rnormalize(rcross(reference, normal));
  if (rlength2(t1) <= 1e-12) t1 = rv(0, 0, 1);
  let t2 = rnormalize(rcross(normal, t1));
  if (rlength2(t2) <= 1e-12) t2 = rv(1, 0, 0);
  return [t1, t2];
}

function rtangentFromPreferred(
  normal: ReferenceVec3,
  preferred: ReferenceVec3,
): readonly [ReferenceVec3, ReferenceVec3] {
  const projected = rsub(preferred, rscale(normal, rdot(preferred, normal)));
  if (rlength2(projected) <= 1e-12) return rcanonicalTangent(normal);
  const t1 = rnormalize(projected);
  const t2 = rnormalize(rcross(normal, t1));
  return rlength2(t2) <= 1e-12 ? rcanonicalTangent(normal) : [t1, t2];
}

function rpackFaceFeature(
  referenceIsA: boolean,
  referenceAxis: number,
  incidentAxis: number,
  ordinal: number,
): number {
  const featureType = referenceIsA ? 0 : 1;
  return (
    (ordinal & 7)
      | ((incidentAxis & 3) << 3)
      | ((referenceAxis & 3) << 5)
      | ((featureType & 3) << 7)
  ) >>> 0;
}

function rpackEdgeFeature(axisA: number, axisB: number): number {
  return (((axisB & 3) << 3) | ((axisA & 3) << 5) | (2 << 7)) >>> 0;
}

function rclosestSegments(
  p0: ReferenceVec3,
  p1: ReferenceVec3,
  q0: ReferenceVec3,
  q1: ReferenceVec3,
): readonly [ReferenceVec3, ReferenceVec3] {
  const u = rsub(p1, p0);
  const v = rsub(q1, q0);
  const w0 = rsub(p0, q0);
  const a = rdot(u, u);
  const b = rdot(u, v);
  const c = rdot(v, v);
  const d = rdot(u, w0);
  const e = rdot(v, w0);
  const determinant = rf(rf(a * c) - rf(b * b));
  const epsilon = rf(1e-6);
  let sN: number;
  let sD = determinant;
  let tN: number;
  let tD = determinant;
  if (determinant < epsilon) {
    sN = 0; sD = 1; tN = e; tD = c;
  } else {
    sN = rf(rf(b * e) - rf(c * d));
    tN = rf(rf(a * e) - rf(b * d));
    if (sN < 0) { sN = 0; tN = e; tD = c; }
    else if (sN > sD) { sN = sD; tN = rf(e + b); tD = c; }
  }
  if (tN < 0) {
    tN = 0;
    if (-d < 0) sN = 0;
    else if (-d > a) sN = sD;
    else { sN = -d; sD = a; }
  } else if (tN > tD) {
    tN = tD;
    if (-d + b < 0) sN = 0;
    else if (-d + b > a) sN = sD;
    else { sN = rf(-d + b); sD = a; }
  }
  const sc = Math.abs(sN) > epsilon ? rf(sN / sD) : 0;
  const tc = Math.abs(tN) > epsilon ? rf(tN / tD) : 0;
  return [radd(p0, rscale(u, sc)), radd(q0, rscale(v, tc))];
}

function referenceBoxBoxGeometry(input: WideExactBoxBoxReferenceInput): Readonly<{
  separated: boolean;
  bestFeature: number;
  normal: ReferenceVec3;
  tangent: readonly [ReferenceVec3, ReferenceVec3];
  tangentAngle: number;
  candidates: readonly ReferenceBoxCandidate[];
}> {
  const contactSlop = rf(input.contactSlop ?? 0.005);
  const positionA = rv(...input.positionA);
  const positionB = rv(...input.positionB);
  const halfA = rv(...input.halfA);
  const halfB = rv(...input.halfB);
  const axesA = [0, 1, 2].map((axis) => rquatRotate(input.quaternionA, raxis(axis)));
  const axesB = [0, 1, 2].map((axis) => rquatRotate(input.quaternionB, raxis(axis)));
  const delta = rsub(positionB, positionA);
  const rotation = axesA.map((axisA) => axesB.map((axisB) => rdot(axisA!, axisB!)));
  const absolute = rotation.map((row) => row.map((value) => rf(Math.abs(value!) + 1e-6)));
  const translatedA = axesA.map((axis) => rdot(delta, axis!));
  const translatedB = axesB.map((axis) => rdot(delta, axis!));
  let minimumPenetration = rf(1e30);
  let faceMinimumPenetration = rf(1e30);
  let bestFeature = -1;
  let faceBestFeature = -1;
  let bestNormal = rv(0, 0, 0);
  let faceBestNormal = rv(0, 0, 0);
  let separated = false;
  const consider = (feature: number, penetration: number, normal: ReferenceVec3, face: boolean): void => {
    const pen = rf(penetration);
    if (pen < -contactSlop) { separated = true; return; }
    if (face && pen < faceMinimumPenetration) {
      faceMinimumPenetration = pen;
      faceBestFeature = feature;
      faceBestNormal = normal;
    }
    if (pen < minimumPenetration) {
      minimumPenetration = pen;
      bestFeature = feature;
      bestNormal = normal;
    }
  };
  for (let axis = 0; axis < 3 && !separated; axis++) {
    const projectedB = rf(rf(rf(absolute[axis]![0]! * halfB[0])
      + rf(absolute[axis]![1]! * halfB[1])) + rf(absolute[axis]![2]! * halfB[2]));
    const sign = translatedA[axis]! < 0 ? -1 : 1;
    consider(axis, rf(rf(halfA[axis]! + projectedB) - Math.abs(translatedA[axis]!)),
      rscale(axesA[axis]!, sign), true);
  }
  for (let axis = 0; axis < 3 && !separated; axis++) {
    const projectedA = rf(rf(rf(absolute[0]![axis]! * halfA[0])
      + rf(absolute[1]![axis]! * halfA[1])) + rf(absolute[2]![axis]! * halfA[2]));
    const sign = translatedB[axis]! < 0 ? -1 : 1;
    consider(3 + axis, rf(rf(projectedA + halfB[axis]!) - Math.abs(translatedB[axis]!)),
      rscale(axesB[axis]!, sign), true);
  }
  for (let axisA = 0; axisA < 3 && !separated; axisA++) {
    for (let axisB = 0; axisB < 3 && !separated; axisB++) {
      const crossAxis = rcross(axesA[axisA]!, axesB[axisB]!);
      const length2 = rlength2(crossAxis);
      if (length2 <= 1e-6) continue;
      const inverseLength = rf(1 / Math.sqrt(length2));
      const normal = rscale(crossAxis, inverseLength);
      const otherA = [0, 1, 2].filter((axis) => axis !== axisA);
      const otherB = [0, 1, 2].filter((axis) => axis !== axisB);
      const radiusA = rf(
        rf(halfA[otherA[0]!] * absolute[otherA[1]!]![axisB]!)
          + rf(halfA[otherA[1]!] * absolute[otherA[0]!]![axisB]!),
      );
      const radiusB = rf(
        rf(halfB[otherB[0]!] * absolute[axisA]![otherB[1]!]!)
          + rf(halfB[otherB[1]!] * absolute[axisA]![otherB[0]!]!),
      );
      const penetration = rf(rf(rf(radiusA + radiusB) * inverseLength) - Math.abs(rdot(delta, normal)));
      consider(6 + axisA * 3 + axisB, penetration,
        rdot(delta, normal) < 0 ? rneg(normal) : normal, false);
    }
  }
  if (!separated && bestFeature >= 6 && faceBestFeature >= 0) {
    const edgeClearlyBetter = minimumPenetration < rf(rf(faceMinimumPenetration * 0.95) - 0.01);
    if (!edgeClearlyBetter) {
      minimumPenetration = faceMinimumPenetration;
      bestFeature = faceBestFeature;
      bestNormal = faceBestNormal;
    }
  }
  if (separated || bestFeature < 0 || !(minimumPenetration > -contactSlop)) {
    return Object.freeze({
      separated: true,
      bestFeature,
      normal: bestNormal,
      tangent: rcanonicalTangent(bestNormal),
      tangentAngle: 0,
      candidates: Object.freeze([]),
    });
  }

  const candidates: ReferenceBoxCandidate[] = [];
  let preferredTangent = rv(0, 0, 0);
  let referenceIsA = false;
  let referenceFeature = 0;
  let incidentFeature = 0;
  if (bestFeature >= 6) {
    const code = bestFeature - 6;
    const axisA = Math.floor(code / 3);
    const axisB = code % 3;
    const sideAxesA = [0, 1, 2].filter((axis) => axis !== axisA);
    const sideAxesB = [0, 1, 2].filter((axis) => axis !== axisB);
    let centerA = positionA;
    for (const side of sideAxesA) {
      centerA = radd(centerA, rscale(axesA[side]!, (rdot(axesA[side]!, bestNormal) < 0 ? -1 : 1) * halfA[side]!));
    }
    let centerB = positionB;
    for (const side of sideAxesB) {
      centerB = radd(centerB, rscale(axesB[side]!, (-rdot(axesB[side]!, bestNormal) < 0 ? -1 : 1) * halfB[side]!));
    }
    const [pointA, pointB] = rclosestSegments(
      rsub(centerA, rscale(axesA[axisA]!, halfA[axisA]!)),
      radd(centerA, rscale(axesA[axisA]!, halfA[axisA]!)),
      rsub(centerB, rscale(axesB[axisB]!, halfB[axisB]!)),
      radd(centerB, rscale(axesB[axisB]!, halfB[axisB]!)),
    );
    candidates.push({
      point: rscale(radd(pointA, pointB), 0.5),
      armA: rsub(pointA, positionA),
      armB: rsub(pointB, positionB),
      penetration: rf(Math.max(minimumPenetration, 0)),
      featureKey: rpackEdgeFeature(axisA, axisB),
    });
    preferredTangent = axesA[axisA]!;
  } else {
    referenceIsA = bestFeature < 3;
    referenceFeature = referenceIsA ? bestFeature : bestFeature - 3;
    const referencePosition = referenceIsA ? positionA : positionB;
    const referenceHalf = referenceIsA ? halfA : halfB;
    const referenceAxes = referenceIsA ? axesA : axesB;
    const incidentPosition = referenceIsA ? positionB : positionA;
    const incidentHalf = referenceIsA ? halfB : halfA;
    const incidentAxes = referenceIsA ? axesB : axesA;
    const sideAxes = [0, 1, 2].filter((axis) => axis !== referenceFeature);
    const referenceNormal = referenceIsA ? bestNormal : rneg(bestNormal);
    const referenceAxis = referenceAxes[referenceFeature]!;
    const referenceCenter = radd(
      referencePosition,
      rscale(referenceAxis, (rdot(referenceAxis, referenceNormal) < 0 ? -1 : 1) * referenceHalf[referenceFeature]!),
    );
    const referenceU = referenceAxes[sideAxes[0]!]!;
    const referenceV = referenceAxes[sideAxes[1]!]!;
    const halfU = referenceHalf[sideAxes[0]!]!;
    const halfV = referenceHalf[sideAxes[1]!]!;
    preferredTangent = referenceU;
    const incidentDots = incidentAxes.map((axis) => Math.abs(rdot(axis!, referenceNormal)));
    if (incidentDots[1]! > incidentDots[0]! && incidentDots[1]! >= incidentDots[2]!) incidentFeature = 1;
    else if (incidentDots[2]! > incidentDots[0]! && incidentDots[2]! > incidentDots[1]!) incidentFeature = 2;
    const incidentSide = [0, 1, 2].filter((axis) => axis !== incidentFeature);
    const incidentAxis = incidentAxes[incidentFeature]!;
    const incidentSign = rdot(incidentAxis, referenceNormal) > 0 ? -1 : 1;
    const incidentCenter = radd(
      incidentPosition,
      rscale(incidentAxis, incidentSign * incidentHalf[incidentFeature]!),
    );
    const incidentU = incidentAxes[incidentSide[0]!]!;
    const incidentV = incidentAxes[incidentSide[1]!]!;
    const incidentHalfU = incidentHalf[incidentSide[0]!]!;
    const incidentHalfV = incidentHalf[incidentSide[1]!]!;
    let polygon: ReferenceVec3[] = [
      radd(radd(incidentCenter, rscale(incidentU, incidentHalfU)), rscale(incidentV, incidentHalfV)),
      radd(rsub(incidentCenter, rscale(incidentU, incidentHalfU)), rscale(incidentV, incidentHalfV)),
      rsub(rsub(incidentCenter, rscale(incidentU, incidentHalfU)), rscale(incidentV, incidentHalfV)),
      rsub(radd(incidentCenter, rscale(incidentU, incidentHalfU)), rscale(incidentV, incidentHalfV)),
    ];
    const planes: readonly (readonly [ReferenceVec3, number])[] = [
      [referenceU, rf(rdot(referenceU, referenceCenter) + halfU)],
      [rneg(referenceU), rf(rdot(rneg(referenceU), referenceCenter) + halfU)],
      [referenceV, rf(rdot(referenceV, referenceCenter) + halfV)],
      [rneg(referenceV), rf(rdot(rneg(referenceV), referenceCenter) + halfV)],
    ];
    for (const [planeNormal, planeDistance] of planes) {
      const clipped: ReferenceVec3[] = [];
      for (let vertex = 0; vertex < polygon.length; vertex++) {
        const a = polygon[vertex]!;
        const b = polygon[(vertex + 1) % polygon.length]!;
        const distanceA = rf(rdot(planeNormal, a) - planeDistance);
        const distanceB = rf(rdot(planeNormal, b) - planeDistance);
        const insideA = distanceA <= 1e-5;
        const insideB = distanceB <= 1e-5;
        if (insideA && insideB) clipped.push(b);
        else if (insideA !== insideB) {
          const denominator = rf(distanceA - distanceB);
          if (Math.abs(denominator) > 1e-6) {
            const fraction = Math.max(0, Math.min(1, rf(distanceA / denominator)));
            clipped.push(radd(a, rscale(rsub(b, a), fraction)));
          }
          if (!insideA && insideB) clipped.push(b);
        }
        if (clipped.length >= 8) break;
      }
      polygon = clipped;
      if (polygon.length === 0) break;
    }
    for (let vertex = 0; vertex < polygon.length && candidates.length < 8; vertex++) {
      const point = polygon[vertex]!;
      const distance = rdot(referenceNormal, rsub(point, referenceCenter));
      if (distance > contactSlop) continue;
      const pointOnReference = rsub(point, rscale(referenceNormal, distance));
      const pointA = referenceIsA ? pointOnReference : point;
      const pointB = referenceIsA ? point : pointOnReference;
      const midpoint = rscale(radd(pointA, pointB), 0.5);
      if (candidates.some((candidate) => rlength2(rsub(midpoint, candidate.point)) < 1e-6)) continue;
      candidates.push({
        point: midpoint,
        armA: rsub(pointA, positionA),
        armB: rsub(pointB, positionB),
        penetration: rf(Math.max(-distance, 0)),
        featureKey: rpackFaceFeature(referenceIsA, referenceFeature, incidentFeature, Math.min(vertex, 7)),
      });
    }
  }
  candidates.sort((left, right) => left.featureKey - right.featureKey
    || right.penetration - left.penetration);
  const tangent = rtangentFromPreferred(bestNormal, preferredTangent);
  const canonical = rcanonicalTangent(bestNormal);
  let tangentAngle = rf(Math.atan2(rdot(tangent[0], canonical[1]), rdot(tangent[0], canonical[0])));
  if (tangentAngle < 0) tangentAngle = rf(tangentAngle + 2 * Math.PI);
  return Object.freeze({
    separated: false,
    bestFeature,
    normal: bestNormal,
    tangent,
    tangentAngle,
    candidates: Object.freeze(candidates),
  });
}

function legacyReferenceWords(row: WideExactReferenceContactRow): Uint32Array {
  const words = new Uint32Array(36);
  words[0] = f32Bits(row.meta[0]);
  words[1] = f32Bits(row.meta[1]);
  words[2] = f32Bits(row.meta[2]);
  words[3] = row.meta[3] >>> 0;
  const planes = [
    row.normalPen,
    row.armA,
    row.armB,
    row.constraintC0,
    row.shadow,
    row.dual,
    row.penalty,
  ] as const;
  let word = 4;
  for (const plane of planes) {
    for (const lane of plane) words[word++] = f32Bits(lane);
  }
  words.set(row.cache, 32);
  return words;
}

function legacyAosEqualsWideSoaExactly(
  row: WideExactReferenceContactRow,
  legacyWords: Uint32Array,
): boolean {
  if (legacyWords.length !== 36) return false;
  const identity = new Float32Array(legacyWords.buffer, legacyWords.byteOffset, 3);
  if (
    identity[0] !== row.meta[0]
    || identity[1] !== row.meta[1]
    || identity[2] !== row.meta[2]
    || legacyWords[3] !== row.meta[3]
  ) return false;
  const wideWords = [
    ...row.normalPen.map(f32Bits),
    ...row.armA.map(f32Bits),
    ...row.armB.map(f32Bits),
    ...row.constraintC0.map(f32Bits),
    ...row.shadow.map(f32Bits),
    ...row.dual.map(f32Bits),
    ...row.penalty.map(f32Bits),
    ...row.cache,
  ];
  return wideWords.every((word, index) => legacyWords[4 + index] === word);
}

/** Independent operation-rounded CPU reference for cold box/box DCD. */
export function runWideExactBoxBoxDcdReference(
  input: WideExactBoxBoxReferenceInput,
): WideExactBoxBoxReferenceResult {
  const bodyA = exactU32(input.bodyA, 'bodyA');
  const bodyB = exactU32(input.bodyB, 'bodyB');
  if (bodyA >= bodyB || bodyB >= COLISEUM_510K_BODY_CAPACITY) {
    throw new Error('box/box reference requires canonical in-capacity endpoints.');
  }
  const descriptorIndex = exactU32(input.descriptorIndex, 'descriptorIndex');
  const geometry = referenceBoxBoxGeometry(input);
  if (geometry.separated) {
    return Object.freeze({
      separated: true,
      bestFeature: geometry.bestFeature,
      rows: Object.freeze([]),
      legacyAosWords: Object.freeze([]),
      candidateFeatureKeys: Object.freeze([]),
    });
  }
  const collisionMargin = rf(input.collisionMargin ?? AVBD_COLLISION_MARGIN);
  const rows: WideExactReferenceContactRow[] = [];
  for (let logicalSlot = 0; logicalSlot < geometry.candidates.length; logicalSlot++) {
    const candidate = geometry.candidates[logicalSlot]!;
    const pointA = radd(rv(...input.positionA), candidate.armA);
    const pointB = radd(rv(...input.positionB), candidate.armB);
    const finalRawPenetration = rf(-rdot(rsub(pointB, pointA), geometry.normal));
    if (finalRawPenetration < 0) continue;
    const cachedVector = rsub(pointA, pointB);
    const conjugateA: ReferenceQuat = [
      rf(-input.quaternionA[0]),
      rf(-input.quaternionA[1]),
      rf(-input.quaternionA[2]),
      rf(input.quaternionA[3]),
    ];
    const conjugateB: ReferenceQuat = [
      rf(-input.quaternionB[0]),
      rf(-input.quaternionB[1]),
      rf(-input.quaternionB[2]),
      rf(input.quaternionB[3]),
    ];
    const localArmA = rquatRotate(conjugateA, candidate.armA);
    const localArmB = rquatRotate(conjugateB, candidate.armB);
    rows.push(makeWideExactReferenceContactRow({
      bodyA,
      bodyB,
      descriptorIndex,
      logicalSlot,
      encodedInfo: candidate.featureKey,
      normalPen: [geometry.normal[0], geometry.normal[1], geometry.normal[2], Math.max(finalRawPenetration, 0)],
      armA: [localArmA[0], localArmA[1], localArmA[2], geometry.tangentAngle],
      armB: [localArmB[0], localArmB[1], localArmB[2], 0],
      constraintC0: [
        rf(-rdot(cachedVector, geometry.normal) + collisionMargin),
        -rdot(cachedVector, geometry.tangent[0]),
        -rdot(cachedVector, geometry.tangent[1]),
        0,
      ],
      tangentT1: geometry.tangent[0],
    }));
  }
  return Object.freeze({
    separated: false,
    bestFeature: geometry.bestFeature,
    rows: Object.freeze(rows),
    legacyAosWords: Object.freeze(rows.map(legacyReferenceWords)),
    candidateFeatureKeys: Object.freeze(rows.map((row) => row.meta[3] & 0x1ff)),
  });
}

/**
 * Independent operation-rounded reference for the two production sphere DCD
 * branches.  Every arithmetic helper rounds at the corresponding WGSL f32
 * operation; the returned legacy record and wide planes therefore compare as
 * raw words instead of through an epsilon.
 */
export function runWideExactSphereDcdReference(
  input: WideExactSphereDcdReferenceInput,
): WideExactSphereDcdReferenceResult {
  const bodyA = exactU32(input.bodyA, 'bodyA');
  const bodyB = exactU32(input.bodyB, 'bodyB');
  if (bodyA >= bodyB || bodyB >= COLISEUM_510K_BODY_CAPACITY) {
    throw new Error('sphere DCD reference requires canonical in-capacity endpoints.');
  }
  const descriptorIndex = exactU32(input.descriptorIndex, 'descriptorIndex');
  if (input.shapeA !== 'sphere' && input.shapeB !== 'sphere') {
    throw new Error('sphere DCD reference requires at least one sphere.');
  }
  const sphereA = input.shapeA === 'sphere';
  const sphereB = input.shapeB === 'sphere';
  const positionA = rv(...input.positionA);
  const positionB = rv(...input.positionB);
  const halfA = rv(...input.halfA);
  const halfB = rv(...input.halfB);
  const radiusA = rf(Math.max(halfA[0], 0));
  const radiusB = rf(Math.max(halfB[0], 0));
  const contactSlop = rf(input.contactSlop ?? 0.005);
  const hysteresis = rf(rf(0.35) * contactSlop);
  const previousNormal = input.previousNormal === undefined
    ? rv(0, 0, 0)
    : rv(...input.previousNormal);
  const separationSlop = rf(contactSlop + (input.previousNormal === undefined ? 0 : hysteresis));
  let branch: WideExactSphereDcdReferenceResult['branch'] = sphereA && sphereB
    ? 'sphere-sphere'
    : 'sphere-box-outside';
  let bestFeature = -1;
  let faceAxis = 0;
  let normal = rv(0, 0, 0);
  let penetration = rf(0);
  let pointA = rv(0, 0, 0);
  let pointB = rv(0, 0, 0);
  let featureKey = 0;
  let preferredTangent: ReferenceVec3 | null = null;

  if (sphereA && sphereB) {
    const centerDelta = rsub(positionB, positionA);
    const centerDistance2 = rdot(centerDelta, centerDelta);
    const radiusSum = rf(radiusA + radiusB);
    if (centerDistance2 > rf(1e-12)) {
      const centerDistance = rf(Math.sqrt(centerDistance2));
      normal = rv(
        rf(centerDelta[0] / centerDistance),
        rf(centerDelta[1] / centerDistance),
        rf(centerDelta[2] / centerDistance),
      );
      penetration = rf(radiusSum - centerDistance);
    } else {
      normal = rlength2(previousNormal) < rf(1e-12)
        ? rv(0, 1, 0)
        : previousNormal;
      penetration = radiusSum;
    }
    bestFeature = 6;
    pointA = radd(positionA, rscale(normal, radiusA));
    pointB = rsub(positionB, rscale(normal, radiusB));
    featureKey = rpackEdgeFeature(0, 0);
  } else {
    const sphereIsA = sphereA;
    const sphereCenter = sphereIsA ? positionA : positionB;
    const boxCenter = sphereIsA ? positionB : positionA;
    const sphereRadius = sphereIsA ? radiusA : radiusB;
    const halfBox = sphereIsA ? halfB : halfA;
    const boxQuaternion = sphereIsA ? input.quaternionB : input.quaternionA;
    const boxAxes = [0, 1, 2].map((axis) => rquatRotate(boxQuaternion, raxis(axis)));
    const relativeCenter = rsub(sphereCenter, boxCenter);
    const sphereLocal = rv(
      rdot(relativeCenter, boxAxes[0]!),
      rdot(relativeCenter, boxAxes[1]!),
      rdot(relativeCenter, boxAxes[2]!),
    );
    const clampedLocal = rv(
      Math.max(-halfBox[0], Math.min(halfBox[0], sphereLocal[0])),
      Math.max(-halfBox[1], Math.min(halfBox[1], sphereLocal[1])),
      Math.max(-halfBox[2], Math.min(halfBox[2], sphereLocal[2])),
    );
    const deltaLocal = rsub(sphereLocal, clampedLocal);
    let boxPointLocal = clampedLocal;
    let faceSign = rf(1);
    let boxOutwardNormal = boxAxes[0]!;
    const outsideDistance2 = rdot(deltaLocal, deltaLocal);
    if (outsideDistance2 > rf(1e-12)) {
      const absoluteDelta = rv(
        Math.abs(deltaLocal[0]),
        Math.abs(deltaLocal[1]),
        Math.abs(deltaLocal[2]),
      );
      if (absoluteDelta[1] > absoluteDelta[0] && absoluteDelta[1] >= absoluteDelta[2]) {
        faceAxis = 1;
      } else if (absoluteDelta[2] > absoluteDelta[0] && absoluteDelta[2] > absoluteDelta[1]) {
        faceAxis = 2;
      }
      faceSign = rcomponent(deltaLocal, faceAxis) < 0 ? rf(-1) : rf(1);
      boxOutwardNormal = rscale(boxAxes[faceAxis]!, faceSign);
      penetration = rf(sphereRadius - rf(Math.sqrt(outsideDistance2)));
      branch = 'sphere-box-outside';
    } else {
      const faceGap = rv(
        rf(halfBox[0] - Math.abs(sphereLocal[0])),
        rf(halfBox[1] - Math.abs(sphereLocal[1])),
        rf(halfBox[2] - Math.abs(sphereLocal[2])),
      );
      if (faceGap[1] < faceGap[0] && faceGap[1] <= faceGap[2]) {
        faceAxis = 1;
      } else if (faceGap[2] < faceGap[0] && faceGap[2] < faceGap[1]) {
        faceAxis = 2;
      }
      faceSign = rcomponent(sphereLocal, faceAxis) < 0 ? rf(-1) : rf(1);
      if (faceAxis === 0) {
        boxPointLocal = rv(rf(faceSign * halfBox[0]), sphereLocal[1], sphereLocal[2]);
      } else if (faceAxis === 1) {
        boxPointLocal = rv(sphereLocal[0], rf(faceSign * halfBox[1]), sphereLocal[2]);
      } else {
        boxPointLocal = rv(sphereLocal[0], sphereLocal[1], rf(faceSign * halfBox[2]));
      }
      boxOutwardNormal = rscale(boxAxes[faceAxis]!, faceSign);
      penetration = rf(sphereRadius + rcomponent(faceGap, faceAxis));
      branch = 'sphere-box-inside';
    }
    const boxPointWorld = radd(
      radd(
        radd(boxCenter, rscale(boxAxes[0]!, boxPointLocal[0])),
        rscale(boxAxes[1]!, boxPointLocal[1]),
      ),
      rscale(boxAxes[2]!, boxPointLocal[2]),
    );
    const spherePointWorld = rsub(sphereCenter, rscale(boxOutwardNormal, sphereRadius));
    normal = sphereIsA ? rneg(boxOutwardNormal) : boxOutwardNormal;
    bestFeature = faceAxis + (sphereIsA ? 3 : 0);
    featureKey = rpackFaceFeature(!sphereIsA, faceAxis, 0, 0);
    pointA = sphereIsA ? spherePointWorld : boxPointWorld;
    pointB = sphereIsA ? boxPointWorld : spherePointWorld;
    preferredTangent = faceAxis === 0
      ? boxAxes[1]!
      : faceAxis === 1
        ? boxAxes[2]!
        : boxAxes[0]!;
  }

  if (!(penetration > rf(-separationSlop))) {
    return Object.freeze({
      separated: true,
      branch,
      bestFeature,
      faceAxis,
      row: null,
      legacyAosWords: null,
    });
  }
  const armAWorld = rsub(pointA, positionA);
  const armBWorld = rsub(pointB, positionB);
  const finalRawPenetration = rf(-rdot(rsub(pointB, pointA), normal));
  if (finalRawPenetration < 0) {
    return Object.freeze({
      separated: true,
      branch,
      bestFeature,
      faceAxis,
      row: null,
      legacyAosWords: null,
    });
  }
  const tangent = preferredTangent === null
    ? rcanonicalTangent(normal)
    : rtangentFromPreferred(normal, preferredTangent);
  const canonicalTangent = rcanonicalTangent(normal);
  let tangentAngle = rf(Math.atan2(
    rdot(tangent[0], canonicalTangent[1]),
    rdot(tangent[0], canonicalTangent[0]),
  ));
  if (tangentAngle < 0) tangentAngle = rf(tangentAngle + 2 * Math.PI);
  const conjugateA: ReferenceQuat = [
    rf(-input.quaternionA[0]), rf(-input.quaternionA[1]),
    rf(-input.quaternionA[2]), rf(input.quaternionA[3]),
  ];
  const conjugateB: ReferenceQuat = [
    rf(-input.quaternionB[0]), rf(-input.quaternionB[1]),
    rf(-input.quaternionB[2]), rf(input.quaternionB[3]),
  ];
  const storedArmA = sphereA ? armAWorld : rquatRotate(conjugateA, armAWorld);
  const storedArmB = sphereB ? armBWorld : rquatRotate(conjugateB, armBWorld);
  const cachedVector = rsub(pointA, pointB);
  const inverseMassA = rf(input.inverseMassA ?? 1);
  const inverseMassB = rf(input.inverseMassB ?? 1);
  const inverseMassSum = rf(inverseMassA + inverseMassB);
  const immovableSpherePair = inverseMassA <= 0 || inverseMassB <= 0;
  const staticPenaltyN = immovableSpherePair
    ? rf(Math.min(
      rf(100000000),
      rf(rf(416666.67) / Math.max(inverseMassSum, rf(1e-6))),
    ))
    : rf(1);
  const penaltyN = rf(Math.max(
    rf(input.priorSpeculativePenaltyN ?? 1),
    staticPenaltyN,
  ));
  const sphereArmFlags = (sphereA ? 1 : 0) | (sphereB ? 2 : 0);
  const collisionMargin = rf(input.collisionMargin ?? AVBD_COLLISION_MARGIN);
  const row = makeWideExactReferenceContactRow({
    bodyA,
    bodyB,
    descriptorIndex,
    logicalSlot: 0,
    encodedInfo: featureKey,
    normalPen: [normal[0], normal[1], normal[2], rf(Math.max(finalRawPenetration, 0))],
    armA: [storedArmA[0], storedArmA[1], storedArmA[2], tangentAngle],
    armB: [storedArmB[0], storedArmB[1], storedArmB[2], rf(sphereArmFlags)],
    constraintC0: [
      rf(rf(-rdot(cachedVector, normal)) + collisionMargin),
      rf(-rdot(cachedVector, tangent[0])),
      rf(-rdot(cachedVector, tangent[1])),
      0,
    ],
    frictionScale: rf(input.frictionScale ?? 1),
    penaltyN,
    tangentT1: tangent[0],
  });
  return Object.freeze({
    separated: false,
    branch,
    bestFeature,
    faceAxis,
    row,
    legacyAosWords: legacyReferenceWords(row),
  });
}

function f32Bits(value: number): number {
  const bytes = new ArrayBuffer(4);
  new Float32Array(bytes)[0] = value;
  return new Uint32Array(bytes)[0]!;
}

/**
 * Small exact-ABI row fixture used by GPU-free tests. Geometry is supplied by
 * the caller; this helper intentionally does not claim to implement DCD/CCD.
 */
export function makeWideExactReferenceContactRow(input: {
  bodyA: number;
  bodyB: number;
  descriptorIndex: number;
  logicalSlot: number;
  encodedInfo?: number;
  speculative?: boolean;
  normalPen: readonly [number, number, number, number];
  armA: readonly [number, number, number, number];
  armB: readonly [number, number, number, number];
  constraintC0: readonly [number, number, number, number];
  frictionScale?: number;
  penaltyN?: number;
  cacheWord?: number;
  tangentT1?: readonly [number, number, number];
}): WideExactReferenceContactRow {
  const bodyA = exactU32(input.bodyA, 'bodyA');
  const bodyB = exactU32(input.bodyB, 'bodyB');
  if (bodyA === bodyB || bodyA === U32_MAX || bodyB === U32_MAX) {
    throw new Error('Reference row requires two distinct non-sentinel endpoints.');
  }
  const descriptorIndex = exactU32(input.descriptorIndex, 'descriptorIndex');
  if (descriptorIndex >= 0xffff_fffe) throw new Error('descriptorIndex cannot be published.');
  const logicalSlot = nonNegativeSafeInteger(input.logicalSlot, 'logicalSlot');
  if (logicalSlot >= WIDE_EXACT_CONTACT_GENERATION_PAIR_MANIFOLD_SLOTS) {
    throw new Error('logicalSlot exceeds the canonical three-bit ABI.');
  }
  let encodedInfo = exactU32(input.encodedInfo ?? 0, 'encodedInfo');
  encodedInfo = encodeContactLogicalSlot(encodedInfo, logicalSlot);
  if (input.speculative === true) encodedInfo = (encodedInfo | CONTACT_INFO_SPECULATIVE_BIT) >>> 0;
  const frictionScale = Math.fround(input.frictionScale ?? 1);
  const penaltyN = Math.fround(input.penaltyN ?? 1);
  const cacheWord = exactU32(input.cacheWord ?? 0, 'cacheWord');
  const tangent = input.tangentT1 ?? ([1, 0, 0] as const);
  return Object.freeze({
    meta: Object.freeze([
      Math.min(bodyA, bodyB),
      Math.max(bodyA, bodyB),
      descriptorIndex + 1,
      encodedInfo,
    ] as const),
    normalPen: Object.freeze([...input.normalPen] as [number, number, number, number]),
    armA: Object.freeze([...input.armA] as [number, number, number, number]),
    armB: Object.freeze([...input.armB] as [number, number, number, number]),
    constraintC0: Object.freeze([...input.constraintC0] as [number, number, number, number]),
    shadow: Object.freeze([0, 0, 0, frictionScale] as const),
    dual: Object.freeze([0, 0, 0, 0] as const),
    penalty: Object.freeze([penaltyN, 1, 1, frictionScale] as const),
    cache: Object.freeze([
      cacheWord,
      f32Bits(tangent[0]),
      f32Bits(tangent[1]),
      f32Bits(tangent[2]),
    ] as const),
  });
}

function countDeclaredStorageBindings(source: string): number {
  return [...source.matchAll(/@group\(0\)\s*@binding\((\d+)\)\s*var<storage/g)].length;
}

/** GPU-free layout, source-contract and transactional small-scene tests. */
export function runWideExactContactGenerationSelfTests():
WideExactContactGenerationSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };
  const limits: WideExactContactGenerationDeviceLimits = {
    maxStorageBufferBindingSize: 128 * 1024 * 1024,
    maxBufferSize: 256 * 1024 * 1024,
    maxComputeWorkgroupsPerDimension: 65_535,
  };
  const target = planWideExactContactGeneration({
    maxBodies: COLISEUM_510K_BODY_CAPACITY,
    persistentHashCapacity: COLISEUM_510K_PERSISTENT_HASH_CAPACITY,
    maxPersistentManifolds: COLISEUM_510K_PERSISTENT_MANIFOLD_CAPACITY,
    pairManifoldSlots: WIDE_EXACT_CONTACT_GENERATION_PAIR_MANIFOLD_SLOTS,
    contactRowCapacity: COLISEUM_510K_CONTACT_ROW_CAPACITY,
    limits,
  });
  check('wide contact generation was accidentally marked production-ready',
    target.productionReady === false);
  check('510K logical-slot capacity changed',
    target.scratch.logicalSlotCapacity === 16_777_216);
  check('510K active-descriptor stream bytes changed',
    target.scratch.activeDescriptorStreamWords === 2_097_156
      && target.scratch.activeDescriptorStreamBytes === 8_388_624);
  check('two-word decision layout changed',
    target.scratch.decisionWords === 33_554_432);
  check('510K decision binding is not exactly 128 MiB',
    target.scratch.decisionBytes === 128 * 1024 * 1024);
  check('510K merged collision-body input bytes changed',
    target.scratch.collisionBodyStateBytes === 57_120_000);
  check('510K stable compaction scratch bytes changed',
    target.scratch.stableCompactionBytes === 16_777_220);
  check('a planned 510K binding exceeds the target device limits',
    target.allBindingsWithinLimits && target.scratch.allBindingsWithinLimits);
  check('a contact-generation pass exceeds ten storage bindings',
    target.maxStorageBindingsInAnyPass === 10
      && target.withinStorageBindingBudget
      && WIDE_EXACT_CONTACT_GENERATION_PASS_CONTRACTS.every(
        (contract) => contract.storageBindingCount <= 10,
      ));
  check('implemented lifecycle pass count changed',
    target.passContracts.filter((pass) => pass.status === 'implemented-foundation').length === 4);
  check('DCD formula ledger is not exact or CCD gap was hidden',
    WIDE_EXACT_CONTACT_IMPLEMENTED_FORMULA_PORTS.length === 3
      && WIDE_EXACT_CONTACT_IMPLEMENTED_FORMULA_PORTS.some(
        (port) => port.shapePair === 'box-box' && port.status === 'ported-box-box-dcd',
      )
      && WIDE_EXACT_CONTACT_IMPLEMENTED_FORMULA_PORTS.filter(
        (port) => port.status === 'ported-sphere-dcd',
      ).length === 2
      && WIDE_EXACT_CONTACT_REMAINING_FORMULA_PORTS.length === 1
      && WIDE_EXACT_CONTACT_REMAINING_FORMULA_PORTS[0]?.shapePair === 'ccd'
      && WIDE_EXACT_CONTACT_REMAINING_FORMULA_PORTS[0]?.status === 'not-yet-ported');
  check('510K descriptor dispatch changed',
    target.descriptorDispatch.workgroups[0] === 32_768
      && target.descriptorDispatch.workgroups[1] === 1);
  check('510K logical-slot dispatch is not tiled in canonical 2D order',
    target.logicalSlotDispatch.workgroups[0] === 65_535
      && target.logicalSlotDispatch.workgroups[1] === 5);
  const boundaryItems = WIDE_EXACT_CONTACT_GENERATION_WORKGROUP_SIZE
    * WIDE_EXACT_CONTACT_GENERATION_DEFAULT_WORKGROUP_LIMIT;
  const boundary = resolveWideExactContactGenerationDispatch(boundaryItems);
  const tiled = resolveWideExactContactGenerationDispatch(boundaryItems + 1);
  check('2D dispatch boundary changed',
    boundary.workgroups[0] === 65_535
      && boundary.workgroups[1] === 1
      && tiled.workgroups[0] === 65_535
      && tiled.workgroups[1] === 2);

  const wgsl = generateWideExactContactLifecycleWgsl(target);
  const sources = [wgsl.clear, wgsl.reserve, wgsl.validate, wgsl.commitOrRollback];
  check('lifecycle WGSL binding count changed',
    countDeclaredStorageBindings(wgsl.clear) === 1
      && countDeclaredStorageBindings(wgsl.reserve) === 7
      && countDeclaredStorageBindings(wgsl.validate) === 4
      && countDeclaredStorageBindings(wgsl.commitOrRollback) === 7);
  check('lifecycle WGSL exceeds the project storage-binding budget',
    sources.every(
      (source) => countDeclaredStorageBindings(source)
        <= REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE,
    ));
  check('lifecycle WGSL narrowed or packed endpoint identity',
    sources.every((source) => !/\bf16\b/.test(source))
      && sources.every((source) => !source.includes('& 0xffffu')));
  check('lifecycle WGSL lost canonical 2D flattening',
    wgsl.reserve.includes('workgroupId.y * DISPATCH_WORKGROUPS_X + workgroupId.x')
      && wgsl.validate.includes('workgroupId.y * DISPATCH_WORKGROUPS_X + workgroupId.x')
      && wgsl.commitOrRollback.includes('workgroupId.y * DISPATCH_WORKGROUPS_X + workgroupId.x'));
  check('lifecycle WGSL lost the mandatory transaction boundaries',
    wgsl.dispatchSequence.filter((step) => step === 'dispatch-boundary').length === 4);
  check('row reservations can be published before global validation',
    wgsl.reserve.includes('never mutates it')
      && !wgsl.reserve.includes('atomicStore(&outputMasks.values')
      && wgsl.commitOrRollback.includes('transactionFailed'));
  check('rollback does not return every newly reserved row',
    wgsl.commitOrRollback.includes('DECISION_NEWLY_RESERVED')
      && wgsl.commitOrRollback.includes('atomicExchange(&slotRows.values[logicalSlot]')
      && wgsl.commitOrRollback.includes('appendNextFreeRow'));
  check('lifecycle WGSL no longer reports sticky pool fatal bits',
    wgsl.reserve.includes(`${CompactContactPoolFatalBit.rowPoolOverflow}u`)
      && wgsl.commitOrRollback.includes(`${CompactContactPoolFatalBit.freeListCarryOverflow}u`));

  const boxBoxWgsl = generateWideExactBoxBoxDcdWgsl(target);
  const boxBoxSources = [
    boxBoxWgsl.classify,
    boxBoxWgsl.transitionState,
    boxBoxWgsl.materializeCache,
    boxBoxWgsl.materializeGeometry,
  ];
  check('box/box split pass storage-binding counts changed',
    countDeclaredStorageBindings(boxBoxWgsl.classify) === 10
      && countDeclaredStorageBindings(boxBoxWgsl.transitionState) === 10
      && countDeclaredStorageBindings(boxBoxWgsl.materializeCache) === 8
      && countDeclaredStorageBindings(boxBoxWgsl.materializeGeometry) === 10);
  check('box/box split pass exceeds ten storage bindings',
    boxBoxSources.every((source) => countDeclaredStorageBindings(source)
      <= REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE));
  check('box/box wide shader narrowed or packed an endpoint',
    boxBoxSources.every((source) => !/\bf16\b/.test(source))
      && boxBoxSources.every((source) => !source.includes('& 0xffffu'))
      && boxBoxWgsl.materializeGeometry.includes(
        'vec4u(bodyA, bodyB, descriptor + 1u, decision.x)',
      ));
  check('box/box WGSL used a WGSL 16.2 reserved identifier',
    boxBoxSources.every((source) => findWgsl16_2ReservedIdentifiers(source).length === 0));
  check('box/box SAT/clipping/edge formulas disappeared',
    boxBoxWgsl.classify.includes('let R00 = dot(axA0, axB0)')
      && boxBoxWgsl.classify.includes('for (var planeIndex = 0u; planeIndex < 4u')
      && boxBoxWgsl.classify.includes('closestPointsSegmentsExact')
      && boxBoxWgsl.classify.includes('edgeClearlyBetter')
      && boxBoxWgsl.classify.includes('candidateContactKey[candidate] < candidateContactKey[next]') === false
      && boxBoxWgsl.classify.includes('leftKey > rightKey'));
  check('box/box split ordering can overwrite old state before carry',
    boxBoxWgsl.dispatchSequence.indexOf('transition-box-box-state')
      < boxBoxWgsl.dispatchSequence.indexOf('materialize-box-box-geometry')
      && boxBoxWgsl.dispatchSequence.indexOf('materialize-box-box-cache')
        < boxBoxWgsl.dispatchSequence.indexOf('materialize-box-box-geometry'));
  check('box/box DCD bundle falsely claims sphere or CCD coverage',
    boxBoxWgsl.coverage.boxBoxDcd
      && !boxBoxWgsl.coverage.sphereBoxDcd
      && !boxBoxWgsl.coverage.sphereSphereDcd
      && !boxBoxWgsl.coverage.ccd);

  const sphereWgsl = generateWideExactSphereDcdWgsl(target);
  const sphereSources = [
    sphereWgsl.classify,
    sphereWgsl.preseedPenalty,
    sphereWgsl.transitionState,
    sphereWgsl.materializeCache,
    sphereWgsl.materializeGeometry,
  ];
  check('sphere split pass storage-binding counts changed',
    countDeclaredStorageBindings(sphereWgsl.classify) === 10
      && countDeclaredStorageBindings(sphereWgsl.preseedPenalty) === 6
      && countDeclaredStorageBindings(sphereWgsl.transitionState) === 10
      && countDeclaredStorageBindings(sphereWgsl.materializeCache) === 8
      && countDeclaredStorageBindings(sphereWgsl.materializeGeometry) === 10);
  check('sphere split pass exceeds ten storage bindings',
    sphereSources.every((source) => countDeclaredStorageBindings(source)
      <= REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE));
  const aliasedScratchReadDeclaration = /var<storage,\s*read>\s+(?:collisionBodyState|activeDescriptors|decisions)\b/;
  check('phase-aliased contact scratch has mixed Dawn access classes',
    [...sources, ...boxBoxSources, ...sphereSources].every(
      (source) => !aliasedScratchReadDeclaration.test(source),
    )
      && WIDE_EXACT_CONTACT_GENERATION_PASS_CONTRACTS.every((contract) => (
        contract.bindings
          .filter((binding) => [
            'collisionBodyState',
            'activeDescriptors',
            'scratch.decisions',
            'lifecycleControl',
            'scratch.stableCountsOffsets',
          ].includes(binding.resource))
          .every((binding) => binding.access === 'read_write')
      )));
  check('sphere wide shader narrowed or packed an endpoint',
    sphereSources.every((source) => !/\bf16\b/.test(source))
      && sphereSources.every((source) => !source.includes('& 0xffffu'))
      && sphereWgsl.materializeGeometry.includes(
        'vec4u(bodyA, bodyB, descriptor + 1u, decision.x)',
      ));
  check('sphere WGSL used a WGSL 16.2 reserved identifier',
    sphereSources.every((source) => findWgsl16_2ReservedIdentifiers(source).length === 0));
  check('sphere outside/inside/coincident DCD formulas disappeared',
    sphereWgsl.classify.includes('centerDistance2 > 1e-12')
      && sphereWgsl.classify.includes('outsideDistance2 > 1e-12')
      && sphereWgsl.classify.includes('leastAxis3SphereExact(faceGap)')
      && sphereWgsl.classify.includes('dominantAbsAxis3SphereExact(deltaLocal)')
      && sphereWgsl.classify.includes('packEdgeContactFeatureExact(0u, 0u)'));
  check('sphere local/world arm ABI or C0 cache semantics disappeared',
    sphereWgsl.classify.includes('oldArmA[slot].xyz,\n      sphereA')
      && sphereWgsl.materializeGeometry.includes('f32(sphereArmFlags)')
      && sphereWgsl.materializeGeometry.includes('-dot(cachedVector, writeTangent.t1)')
      && sphereWgsl.materializeCache.includes('vec4u(select(0u, oldCacheWord, preserve), tangentBits)'));
  check('sphere static/TOI penalty seed or warmstate rotation disappeared',
    sphereWgsl.preseedPenalty.includes('416666.67 / max(positionMassA.w + positionMassB.w, 1.0e-6)')
      && sphereWgsl.preseedPenalty.includes('DECISION_PRIOR_PENALTY_SEEDED')
      && sphereWgsl.transitionState.includes('max(max(preseededPenaltyN, priorSpeculativePenaltyN), staticSpherePenaltyN)')
      && sphereWgsl.transitionState.includes('tangentImpulseWorld')
      && sphereWgsl.transitionState.includes('dualTangentWorld'));
  check('sphere split ordering can overwrite old state before carry',
    sphereWgsl.dispatchSequence.indexOf('transition-sphere-state')
      < sphereWgsl.dispatchSequence.indexOf('materialize-sphere-geometry')
      && sphereWgsl.dispatchSequence.indexOf('materialize-sphere-cache')
        < sphereWgsl.dispatchSequence.indexOf('materialize-sphere-geometry'));
  check('sphere DCD bundle falsely claims box or CCD coverage',
    !sphereWgsl.coverage.boxBoxDcd
      && sphereWgsl.coverage.sphereBoxDcd
      && sphereWgsl.coverage.sphereSphereDcd
      && !sphereWgsl.coverage.ccd);

  const sentinel = 8;
  const successSlotRows = new Uint32Array(12).fill(sentinel);
  successSlotRows[0] = 0;
  successSlotRows[1] = 1;
  successSlotRows[4] = 2;
  const success = runWideExactContactLifecycleReference({
    descriptorCapacity: 3,
    pairManifoldSlots: 4,
    contactRowCapacity: sentinel,
    activeDescriptors: [0, 1, 2],
    desiredMasks: new Uint32Array([0b0101, 0b0001, 0b0011]),
    outputMasks: new Uint32Array([0b0011, 0b0001, 0]),
    slotRows: successSlotRows,
    currentFreeRows: [3],
    rowHighWater: 4,
  });
  check('successful transaction did not commit every descriptor',
    success.committed && success.failureBits === 0);
  check('successful transaction changed a reused physical row',
    success.slotRows[0] === 0 && success.slotRows[4] === 2);
  check('successful transaction allocation order changed',
    success.slotRows[2] === 3
      && success.slotRows[8] === 4
      && success.slotRows[9] === 5
      && success.rowHighWater === 6);
  check('successful transaction did not release only the retired old row',
    success.nextFreeRows.length === 1
      && success.nextFreeRows[0] === 1
      && success.releases === 1);
  check('successful transaction masks changed',
    success.outputMasks[0] === 0b0101
      && success.outputMasks[1] === 0b0001
      && success.outputMasks[2] === 0b0011);

  const rollbackSlotRows = new Uint32Array(4).fill(2);
  rollbackSlotRows[0] = 0;
  const rollback = runWideExactContactLifecycleReference({
    descriptorCapacity: 2,
    pairManifoldSlots: 2,
    contactRowCapacity: 2,
    activeDescriptors: [0, 1],
    desiredMasks: new Uint32Array([0b01, 0b11]),
    outputMasks: new Uint32Array([0b01, 0]),
    slotRows: rollbackSlotRows,
    rowHighWater: 1,
  });
  check('overflow transaction was partially committed',
    !rollback.committed
      && (rollback.failureBits & WideExactContactLifecycleFailureBit.rowPoolOverflow) !== 0
      && rollback.outputMasks[0] === 0b01
      && rollback.outputMasks[1] === 0);
  check('overflow transaction did not restore every newly reserved logical slot',
    rollback.slotRows[0] === 0
      && rollback.slotRows[2] === 2
      && rollback.slotRows[3] === 2
      && rollback.rollbacks === 1);
  check('overflow transaction leaked its partial high-water row',
    rollback.nextFreeRows.length === 1 && rollback.nextFreeRows[0] === 1);

  let duplicateRejected = false;
  try {
    runWideExactContactLifecycleReference({
      descriptorCapacity: 1,
      pairManifoldSlots: 1,
      contactRowCapacity: 1,
      activeDescriptors: [0, 0],
      desiredMasks: new Uint32Array([0]),
      outputMasks: new Uint32Array([0]),
      slotRows: new Uint32Array([1]),
      rowHighWater: 0,
    });
  } catch {
    duplicateRejected = true;
  }
  check('duplicate active descriptor stream was accepted', duplicateRejected);

  let brokenOwnershipRejected = false;
  try {
    runWideExactContactLifecycleReference({
      descriptorCapacity: 1,
      pairManifoldSlots: 1,
      contactRowCapacity: 1,
      activeDescriptors: [0],
      desiredMasks: new Uint32Array([0]),
      outputMasks: new Uint32Array([1]),
      slotRows: new Uint32Array([1]),
      rowHighWater: 0,
    });
  } catch {
    brokenOwnershipRejected = true;
  }
  check('broken outputMask/slotRow ownership was accepted', brokenOwnershipRejected);

  const identityQuaternion = [0, 0, 0, 1] as const;
  const sphereOutside = runWideExactSphereDcdReference({
    bodyA: 100_001,
    bodyB: 400_003,
    descriptorIndex: 70_000,
    positionA: [0, 1.25, 0],
    quaternionA: identityQuaternion,
    halfA: [0.5, 0.5, 0.5],
    shapeA: 'sphere',
    positionB: [0, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
    shapeB: 'box',
  });
  check('sphere/box outside branch lost >65K exact identity or normal orientation',
    !sphereOutside.separated
      && sphereOutside.branch === 'sphere-box-outside'
      && sphereOutside.bestFeature === 4
      && sphereOutside.faceAxis === 1
      && sphereOutside.row?.meta[0] === 100_001
      && sphereOutside.row.meta[1] === 400_003
      && sphereOutside.row.meta[2] === 70_001
      && sphereOutside.row.normalPen[0] === 0
      && sphereOutside.row.normalPen[1] === -1
      && sphereOutside.row.normalPen[2] === 0
      && sphereOutside.row.armB[3] === 1);

  const sphereInside = runWideExactSphereDcdReference({
    bodyA: 100_003,
    bodyB: 400_005,
    descriptorIndex: 70_002,
    positionA: [0, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [0.5, 0.5, 0.5],
    shapeA: 'sphere',
    positionB: [0, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
    shapeB: 'box',
  });
  check('sphere/box inside branch changed least-axis ties or penetration',
    !sphereInside.separated
      && sphereInside.branch === 'sphere-box-inside'
      && sphereInside.faceAxis === 0
      && sphereInside.bestFeature === 3
      && sphereInside.row?.normalPen[0] === -1
      && sphereInside.row.normalPen[3] === rf(1.5));

  const sphereCorner = runWideExactSphereDcdReference({
    bodyA: 100_005,
    bodyB: 400_007,
    descriptorIndex: 70_004,
    positionA: [1.25, 1.25, 1.25],
    quaternionA: identityQuaternion,
    halfA: [0.5, 0.5, 0.5],
    shapeA: 'sphere',
    positionB: [0, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
    shapeB: 'box',
  });
  check('sphere/box corner branch changed dominant-axis ties',
    !sphereCorner.separated
      && sphereCorner.branch === 'sphere-box-outside'
      && sphereCorner.faceAxis === 0
      && sphereCorner.row?.normalPen[0] === -1);

  const sphereTangent = runWideExactSphereDcdReference({
    bodyA: 100_007,
    bodyB: 400_009,
    descriptorIndex: 70_006,
    positionA: [1.5, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [0.5, 0.5, 0.5],
    shapeA: 'sphere',
    positionB: [0, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
    shapeB: 'box',
  });
  const sphereSeparated = runWideExactSphereDcdReference({
    bodyA: 100_009,
    bodyB: 400_011,
    descriptorIndex: 70_008,
    positionA: [1.51, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [0.5, 0.5, 0.5],
    shapeA: 'sphere',
    positionB: [0, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
    shapeB: 'box',
  });
  check('sphere/box tangent or separated thresholds changed',
    !sphereTangent.separated
      && sphereTangent.row?.normalPen[3] === 0
      && sphereSeparated.separated
      && sphereSeparated.row === null);

  const boxSphereOutside = runWideExactSphereDcdReference({
    bodyA: 100_011,
    bodyB: 400_013,
    descriptorIndex: 70_010,
    positionA: [0, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [1, 1, 1],
    shapeA: 'box',
    positionB: [0, 1.25, 0],
    quaternionB: identityQuaternion,
    halfB: [0.5, 0.5, 0.5],
    shapeB: 'sphere',
  });
  check('box/sphere orientation lost local/world arm flags or face identity',
    !boxSphereOutside.separated
      && boxSphereOutside.bestFeature === 1
      && boxSphereOutside.row?.normalPen[1] === 1
      && boxSphereOutside.row.armB[3] === 2
      && (boxSphereOutside.row.meta[3] & 0x1ff) === rpackFaceFeature(true, 1, 0, 0));

  const staticFloorSphere = runWideExactSphereDcdReference({
    bodyA: 100_013,
    bodyB: 400_015,
    descriptorIndex: 70_012,
    positionA: [0, 1.25, 0],
    quaternionA: identityQuaternion,
    halfA: [0.5, 0.5, 0.5],
    shapeA: 'sphere',
    inverseMassA: 1,
    positionB: [0, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
    shapeB: 'box',
    inverseMassB: 0,
    priorSpeculativePenaltyN: 500_000,
  });
  check('static sphere/floor penalty did not preserve the larger swept seed',
    staticFloorSphere.row?.penalty[0] === rf(500_000)
      && staticFloorSphere.row.penalty[1] === 1
      && staticFloorSphere.row.penalty[2] === 1);

  const sphereSphere = runWideExactSphereDcdReference({
    bodyA: 100_015,
    bodyB: 400_017,
    descriptorIndex: 70_014,
    positionA: [0, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [1, 1, 1],
    shapeA: 'sphere',
    positionB: [1.5, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
    shapeB: 'sphere',
  });
  const coincidentSpheres = runWideExactSphereDcdReference({
    bodyA: 100_017,
    bodyB: 400_019,
    descriptorIndex: 70_016,
    positionA: [0, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [1, 1, 1],
    shapeA: 'sphere',
    positionB: [0, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [0.5, 0.5, 0.5],
    shapeB: 'sphere',
  });
  check('sphere/sphere overlap or coincident-center fallback changed',
    !sphereSphere.separated
      && sphereSphere.bestFeature === 6
      && sphereSphere.row?.normalPen[0] === 1
      && sphereSphere.row.normalPen[3] === rf(0.5)
      && sphereSphere.row.armB[3] === 3
      && coincidentSpheres.row?.normalPen[0] === 0
      && coincidentSpheres.row.normalPen[1] === 1
      && coincidentSpheres.row.normalPen[2] === 0
      && coincidentSpheres.row.normalPen[3] === rf(1.5));

  const sphereReferences = [
    sphereOutside,
    sphereInside,
    sphereCorner,
    sphereTangent,
    boxSphereOutside,
    staticFloorSphere,
    sphereSphere,
    coincidentSpheres,
  ];
  check('sphere legacy AoS versus wide SoA differential is not zero-ULP',
    sphereReferences.every((reference) =>
      reference.row !== null
      && reference.legacyAosWords !== null
      && legacyAosEqualsWideSoaExactly(reference.row, reference.legacyAosWords)));

  const faceReference = runWideExactBoxBoxDcdReference({
    bodyA: 100_001,
    bodyB: 400_003,
    descriptorIndex: 70_000,
    positionA: [0, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [1, 1, 1],
    positionB: [0, 1.5, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
  });
  check('independent CPU face reference did not produce the four clipped rows',
    !faceReference.separated
      && faceReference.bestFeature === 1
      && faceReference.rows.length === 4
      && faceReference.candidateFeatureKeys.length === 4);
  check('independent CPU face reference truncated >65K endpoints or descriptor identity',
    faceReference.rows.every((row) => row.meta[0] === 100_001
      && row.meta[1] === 400_003
      && row.meta[2] === 70_001));
  check('independent CPU face reference lost stable logical slots',
    faceReference.rows.every((row, slot) =>
      ((row.meta[3] & CONTACT_INFO_LOGICAL_SLOT_MASK) >>> CONTACT_INFO_LOGICAL_SLOT_SHIFT) === slot));

  let faceGeometryDifferential = true;
  for (let rowIndex = 0; rowIndex < faceReference.rows.length; rowIndex++) {
    const row = faceReference.rows[rowIndex]!;
    const legacyWords = faceReference.legacyAosWords[rowIndex]!;
    const decodedLegacyMeta = new Float32Array(legacyWords.buffer, legacyWords.byteOffset, 3);
    if (
      decodedLegacyMeta[0] !== row.meta[0]
      || decodedLegacyMeta[1] !== row.meta[1]
      || decodedLegacyMeta[2] !== row.meta[2]
      || legacyWords[3] !== row.meta[3]
    ) {
      faceGeometryDifferential = false;
    }
    const wideGeometryWords = [
      ...row.normalPen.map(f32Bits),
      ...row.armA.map(f32Bits),
      ...row.armB.map(f32Bits),
      ...row.constraintC0.map(f32Bits),
      ...row.shadow.map(f32Bits),
      ...row.dual.map(f32Bits),
      ...row.penalty.map(f32Bits),
      ...row.cache,
    ];
    for (let word = 0; word < wideGeometryWords.length; word++) {
      if (legacyWords[4 + word] !== wideGeometryWords[word]) {
        faceGeometryDifferential = false;
        break;
      }
    }
  }
  check('legacy AoS versus wide SoA CPU differential is not zero-ULP', faceGeometryDifferential);

  const halfAngle = Math.PI / 8;
  const clippedCornerReference = runWideExactBoxBoxDcdReference({
    bodyA: 70_001,
    bodyB: 300_007,
    descriptorIndex: 90_003,
    positionA: [0, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [1, 1, 1],
    positionB: [1.25, 1.15, 0.15],
    quaternionB: [0, 0, Math.sin(halfAngle), Math.cos(halfAngle)],
    halfB: [1, 0.75, 0.8],
  });
  check('independent CPU corner/clipping reference was rejected or lost its feature keys',
    !clippedCornerReference.separated
      && clippedCornerReference.rows.length >= 1
      && new Set(clippedCornerReference.candidateFeatureKeys).size
        === clippedCornerReference.candidateFeatureKeys.length);

  const edgeReference = runWideExactBoxBoxDcdReference({
    bodyA: 90_001,
    bodyB: 450_007,
    descriptorIndex: 100_003,
    positionA: [0, 0, 0],
    quaternionA: [
      -0.0069307289526388165,
      -0.0077650059850709234,
      0.009524655481521573,
      0.9999004703550225,
    ],
    halfA: [0.38788553914055224, 1.0603194952011108, 0.9910311964340508],
    positionB: [0.9483104567974806, -1.0870544713921846, -0.09414937365800143],
    quaternionB: [
      -0.8470716014108233,
      0.3405841568244547,
      0.39289865795597484,
      0.11001263009251502,
    ],
    halfB: [0.8855259558185935, 1.1748905117623507, 0.9123727943748234],
  });
  check('independent CPU edge/edge reference lost closest-segment feature identity',
    !edgeReference.separated
      && edgeReference.bestFeature === 13
      && edgeReference.rows.length === 1
      && edgeReference.candidateFeatureKeys[0] === rpackEdgeFeature(2, 1));

  const separatedReference = runWideExactBoxBoxDcdReference({
    bodyA: 80_001,
    bodyB: 500_001,
    descriptorIndex: 120_000,
    positionA: [0, 0, 0],
    quaternionA: identityQuaternion,
    halfA: [1, 1, 1],
    positionB: [3, 0, 0],
    quaternionB: identityQuaternion,
    halfB: [1, 1, 1],
  });
  check('independent CPU separation reference emitted a contact row',
    separatedReference.separated && separatedReference.rows.length === 0);

  const wideRow = makeWideExactReferenceContactRow({
    bodyA: 400_003,
    bodyB: 100_001,
    descriptorIndex: 70_000,
    logicalSlot: 5,
    encodedInfo: 0x1e0,
    speculative: true,
    normalPen: [0, 1, 0, 0.125],
    armA: [0, 0.5, 0, 0],
    armB: [0, -1, 0, 1],
    constraintC0: [-0.123, 0, 0, 0],
    tangentT1: [1, -0, 0],
  });
  check('wide reference row truncated or reordered exact endpoints',
    wideRow.meta[0] === 100_001
      && wideRow.meta[1] === 400_003
      && wideRow.meta[2] === 70_001);
  check('wide reference row lost logical-slot or speculative identity',
    ((wideRow.meta[3] & CONTACT_INFO_LOGICAL_SLOT_MASK) >>> CONTACT_INFO_LOGICAL_SLOT_SHIFT) === 5
      && (wideRow.meta[3] & CONTACT_INFO_SPECULATIVE_BIT) !== 0);
  check('wide reference row converted cached tangent bits numerically',
    wideRow.cache[1] === f32Bits(1)
      && wideRow.cache[2] === f32Bits(-0)
      && wideRow.cache[3] === f32Bits(0));
  check('wide reference row does not expose all nine canonical planes',
    Object.keys(wideRow).length === 9);

  return Object.freeze({
    ok: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
    coliseum510k: Object.freeze({
      logicalSlots: target.scratch.logicalSlotCapacity,
      activeDescriptorStreamBytes: target.scratch.activeDescriptorStreamBytes,
      decisionBytes: target.scratch.decisionBytes,
      collisionBodyStateBytes: target.scratch.collisionBodyStateBytes,
      maxStorageBindingsInAnyPass: target.maxStorageBindingsInAnyPass,
      descriptorDispatch: target.descriptorDispatch.workgroups,
      logicalSlotDispatch: target.logicalSlotDispatch.workgroups,
    }),
  });
}
